import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import buildNonFiscalTicketHtml from '../utils/ticketTemplate';
import httpRequest from '../utils/request';
import QRCode from 'qrcode';
import BarcodeScannerHtml5Qrcode from '../components/BarcodeScannerHtml5Qrcode';
import { BrowserMultiFormatReader } from '@zxing/library';
import { getAlicuotaPercent, shouldDiscriminateIVA } from '../utils/tax';
import { getFiscalStatus } from '../features/fiscal/fiscalSlice';
import fiscalService from '../features/fiscal/fiscalService';
import { generateInvoiceFromSale, getTiposComprobante } from '../features/invoice/invoiceSlice';
import { pageBg } from '../styles/ui';
import { logout, reset } from '../features/auth/authSlice';
import { hasPerm, PERM_KEYS } from '../utils/permissions';
import CustomerFormModal from '../components/CustomerFormModal';

// CONFIGURACIÓN TEMPORAL MUNDIAL DE FÚTBOL (Cambiar a false para quitar la decoración después del mundial)
const SHOW_WORLD_CUP_DECORATION = false;

// API roots (relative) to leverage shared HTTP client's baseURL and retry/fallback
const API_URL_PRODUCTS = '/product';
const API_URL_SALES = '/sales';
const API_URL_CUSTOMERS = '/customers';

// Map human-readable tipoContribuyente strings to AFIP numeric codes used elsewhere (module scope for stable references)
const tipoContribuyenteMap = {
  'IVA Responsable Inscripto': 1,
  'IVA Responsable No Inscripto': 2,
  'Sujeto No Categorizado': 3,
  'IVA Exento': 4,
  'Consumidor Final': 5,
  'Responsable Monotributo': 6,
};

// Map numeric condicion IVA codes to human-readable descriptions (used for ticket/A4 labels)
// ComboBuilderModal removed â€” combos behavior now handled automatically by Sales.detectAndApplyCombos

const tipoDescripcionMap = {
  1: 'Responsable Inscripto',
  2: 'Responsable No Inscripto',
  3: 'Sujeto No Categorizado',
  4: 'IVA Exento',
  5: 'Consumidor Final',
  6: 'Responsable Monotributo'
};

const Sales = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { tenant } = useSelector((state) => state.tenant);
  const { fiscalStatus, fiscalConfig } = useSelector((state) => state.fiscal || {});

  // 1. STATE & REF DECLARATIONS
  const [cashCheckFinished, setCashCheckFinished] = useState(false);
  const [cashRegisterOpen, setCashRegisterOpen] = useState(null);
  const [variantSelection, setVariantSelection] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [puntoVenta, setPuntoVenta] = useState('');
  const [onlineOrdersCount, setOnlineOrdersCount] = useState(0);
  const [newOnlineOrdersCount, setNewOnlineOrdersCount] = useState(0);
  const [cart, setCart] = useState([]);
  const [total, setTotal] = useState(0);
  const [subtotalAmt, setSubtotalAmt] = useState(0);
  const [ivaAmt, setIvaAmt] = useState(0);
  const [ivaLabel, setIvaLabel] = useState('0%');
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIdx, setActiveSuggestionIdx] = useState(-1);
  const [pendingQuantity, setPendingQuantity] = useState(null);
  const [products, setProducts] = useState([]);

  // ðŸŒ Fetch Online Orders count
  const fetchOnlineOrdersCount = useCallback(async () => {
    try {
      // Pedimos solo 1 registro pero filtramos por ecommerce para obtener el totalItems real desde el servidor
      const res = await httpRequest.get('/sales/all', { params: { source: 'ecommerce', limit: 1 } });
      const total = res.data.total || 0;
      setOnlineOrdersCount(total);
      // Calcular nuevos respecto al íºltimo visto
      const seen = parseInt(localStorage.getItem('seenOnlineOrdersCount') || '0', 10);
      setNewOnlineOrdersCount(Math.max(0, total - seen));
    } catch (err) {
      console.error('Error fetching online orders count:', err);
    }
  }, []);

  useEffect(() => {
    fetchOnlineOrdersCount();
    const interval = setInterval(fetchOnlineOrdersCount, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [fetchOnlineOrdersCount]);

  // ðŸ”” Notify new orders with sound
  useEffect(() => {
    if (newOnlineOrdersCount > 0) {
      try {
        if (staticAudioRef && staticAudioRef.current) {
          staticAudioRef.current.currentTime = 0;
          staticAudioRef.current.play().catch(() => { });
        }
      } catch (e) { }
    }
  }, [newOnlineOrdersCount]);

  const [lastSale, setLastSale] = useState(null);
  const [lastSaleMode, setLastSaleMode] = useState(null);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingList, setPendingList] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [disabledComboIds, setDisabledComboIds] = useState([]);
  const [applyBundlesAuto] = useState(() => {
    try {
      const v = (typeof window !== 'undefined' && window.localStorage) ? window.localStorage.getItem('applyBundlesAuto') : null;
      return v === null ? true : v === 'true';
    } catch (e) { return true; }
  });
  const [priceLookupOpen, setPriceLookupOpen] = useState(false);
  const [priceLookupProduct, setPriceLookupProduct] = useState(null);
  const [localFiscal, setLocalFiscal] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('Efectivo');
  const [amountPaid, setAmountPaid] = useState('');
  const [splitPayments, setSplitPayments] = useState([]);
  const [newSplitPaymentAmount, setNewSplitPaymentAmount] = useState('');
  const [newSplitPaymentMethod, setNewSplitPaymentMethod] = useState('Efectivo');
  const [change, setChange] = useState(0);
  const [installments, setInstallments] = useState(1);
  const [interestRate, setInterestRate] = useState(0);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [pressedBtn, setPressedBtn] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [lastScannedCodeMobile, setLastScannedCodeMobile] = useState('');

  // MP Verification State
  // MP Verification State
  const [recentMpPayments, setRecentMpPayments] = useState([]);
  const [loadingMpPayments, setLoadingMpPayments] = useState(false);
  const [showMpPaymentsList, setShowMpPaymentsList] = useState(false);
  const [openTransferTime, setOpenTransferTime] = useState(null); // Para destacar pagos nuevos
  const [tipoComprobanteSel, setTipoComprobanteSel] = useState(6);
  const [, setPrintReadyToggle] = useState(false);

  const ticketRef = useRef();
  const invoiceRef = useRef();
  const printWindowRef = useRef(null);
  const qrCacheRef = useRef(new Map());
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioCtxRef = useRef(null);
  const staticAudioRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const scanJustHappenedRef = useRef(false);
  const inputBlockedRef = useRef(false);

  // --- TERMINAL POINT INTEGRATION STATE ---
  const [waitingForTerminal, setWaitingForTerminal] = useState(false);
  const [terminalStatus, setTerminalStatus] = useState(''); // 'scanning', 'processing', 'approved'
  const [currentPaymentIntentId, setCurrentPaymentIntentId] = useState(null);
  const [pendingSaleMode, setPendingSaleMode] = useState(null); // 'fiscal' or 'no-fiscal' to resume after payment
  const pollTimerRef = useRef(null);
  const [activeDeviceId, setActiveDeviceId] = useState(null);
  const [mpPaymentData, setMpPaymentData] = useState(null); // Store MP payment details

  // Payment method selection modal (Automatic vs Manual)
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [pendingFinalizeMode, setPendingFinalizeMode] = useState(null);

  // --- QR INTEGRATION STATE ---
  // --- QR INTEGRATION STATE ---
  const [waitingForQR, setWaitingForQR] = useState(false);
  const [_qrExternalRef, setQrExternalRef] = useState(null);
  const [qrData, setQrData] = useState(null);
  const qrPollTimerRef = useRef(null);

  // Customer Modal State
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  // Sale-level discount in pesos
  const [saleDiscount, setSaleDiscount] = useState(0);

  // Nota opcional para el ticket no fiscal
  const [saleNote, setSaleNote] = useState('');

  // Image lightbox preview
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  // States para Presupuesto
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [quotationFormat, setQuotationFormat] = useState('ticket');
  const [showLoadQuotationModal, setShowLoadQuotationModal] = useState(false);
  const [pendingQuotations, setPendingQuotations] = useState([]);
  const [quotationSearch, setQuotationSearch] = useState('');
  const [loadingQuotations, setLoadingQuotations] = useState(false);

  // Debounced search for better performance
  // const [debouncedSearch, setDebouncedSearch] = useState(''); // REEMOVED due to unused


  // Debounce search input to avoid filtering on every keystroke
  /* REMOVED UNUSED
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(timer);
  }, [search]);
  */

  // 2. DERIVED CONSTANTS & SYNC HELPERS
  const canSellWithoutCash = hasPerm(user, PERM_KEYS.SALES_WITHOUT_CASH);
  const isBlockedByCash = cashCheckFinished && !cashRegisterOpen && !canSellWithoutCash;
  const EMBED_BEEP_DATAURI = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAIlYAAESsAAACABAAZGF0YQAAAAA=';
  const envAllow = String(process.env.REACT_APP_ALLOW_NEGATIVE_STOCK || '').toLowerCase() === 'true';
  const storageAllow = (typeof window !== 'undefined' && String(window.localStorage.getItem('allowNegStock') || '').toLowerCase() === 'true');
  const allowNegStock = envAllow || storageAllow;
  const invoiceMode = 'no-fiscal';

  const fiscal = localFiscal || fiscalConfig || fiscalStatus;
  const afipAmbiente = (
    (fiscalStatus && (fiscalStatus.ambiente || (fiscalStatus.afip && fiscalStatus.afip.ambiente))) ||
    (fiscalConfig && (fiscalConfig.afip && fiscalConfig.afip.ambiente)) ||
    'testing'
  );

  const emitterIsMonotributo = (() => {
    try {
      if (typeof fiscal?.discriminarIVA !== 'undefined') {
        if (fiscal.discriminarIVA === false) return true;
      }
      if (typeof fiscal?.regimenFiscal === 'string' && /mono/i.test(fiscal.regimenFiscal)) return true;
      const cond = fiscal?.condicionIVA ?? fiscal?.condicion ?? fiscal?.tipoResponsable;
      if (cond != null && Number(cond) === 6) return true;
      if (typeof cond === 'string' && /monotr/i.test(cond)) return true;
      return false;
    } catch (e) { return false; }
  })();

  // Show final price with IVA only for RI tenants
  // Forzamos a true para que se vean precios finales en el carrito, 
  // o puedes dejarlo como isResponsableInscripto || emitterIsMonotributo si prefieres.
  const showFinalPriceWithIVA = true;

  const isEcommerceLite = user?.tenantId?.serviceType === 'ecommerce_lite';

  const settingsLogoData = typeof window !== 'undefined' ? localStorage.getItem('ticketLogoData') : null;
  const settingsLeftName = typeof window !== 'undefined' ? localStorage.getItem('ticketLeftName') : null;

  // 3. HELPERS & CALLBACKS (Ordered by dependency)
  const debugLog = useCallback((...args) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('debugSales') === '1') {
        console.log('[Sales:debug]', ...args);
      }
    } catch (e) { }
  }, []);

  const getLocalNoControl = (id) => {
    try {
      return localStorage.getItem(`noControl_${id}`) === 'true';
    } catch (e) { return false; }
  };

  const playSuccessBeep = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
        const ctx = audioCtxRef.current;
        if (ctx.state === 'suspended' && typeof ctx.resume === 'function') ctx.resume().catch(() => { });
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine'; o.frequency.value = 1200; g.gain.value = 0.12;
        o.connect(g); g.connect(ctx.destination);
        o.start();
        setTimeout(() => { try { o.stop(); } catch (e) { } }, 120);
        return;
      }
    } catch (e) { }
    try { if (staticAudioRef && staticAudioRef.current) { staticAudioRef.current.currentTime = 0; staticAudioRef.current.play().catch(() => { }); } } catch (e) { }
  };

  const playErrorBeep = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
        const ctx = audioCtxRef.current;
        if (ctx.state === 'suspended' && typeof ctx.resume === 'function') ctx.resume().catch(() => { });
        const o1 = ctx.createOscillator();
        const g1 = ctx.createGain();
        o1.type = 'square'; o1.frequency.value = 700; g1.gain.value = 0.15;
        o1.connect(g1); g1.connect(ctx.destination);
        o1.start();
        setTimeout(() => { try { o1.stop(); } catch (e) { } }, 120);
        setTimeout(() => {
          try {
            const o2 = ctx.createOscillator();
            const g2 = ctx.createGain();
            o2.type = 'sawtooth'; o2.frequency.value = 600; g2.gain.value = 0.12;
            o2.connect(g2); g2.connect(ctx.destination);
            o2.start();
            o2.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.25);
            setTimeout(() => { try { o2.stop(); } catch (e) { } }, 250);
          } catch (e) { }
        }, 140);
        return;
      }
    } catch (e) { }
    try { if (staticAudioRef && staticAudioRef.current) { staticAudioRef.current.currentTime = 0; staticAudioRef.current.play().catch(() => { }); } } catch (e) { }
  };

  const hideMobileKeyboard = () => {
    try {
      const inp = searchInputRef && searchInputRef.current;
      if (inp) {
        try { inp.readOnly = true; } catch (e) { }
        try { inp.blur(); } catch (e) { }
        setTimeout(() => { try { inp.blur(); } catch (e) { } }, 80);
        setTimeout(() => { try { inp.readOnly = false; } catch (e) { } }, 700);
      }
      setTimeout(() => {
        try { if (document.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur(); } catch (e) { }
        try {
          const tmp = document.createElement('button');
          tmp.style.position = 'absolute';
          tmp.style.width = '1px';
          tmp.style.height = '1px';
          tmp.style.left = '-10000px';
          tmp.setAttribute('aria-hidden', 'true');
          tmp.tabIndex = -1;
          document.body.appendChild(tmp);
          try { tmp.focus(); } catch (e) { }
          try { tmp.blur(); } catch (e) { }
          document.body.removeChild(tmp);
        } catch (e) { }
      }, 120);
    } catch (e) { }
  };

  const toTipoCode = useCallback((val) => {
    if (val == null) return null;
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      if (/^\d+$/.test(val)) return parseInt(val, 10);
      return tipoContribuyenteMap[val] || null;
    }
    return null;
  }, []);

  const buildQrPayload = useCallback((invoice, emitterCuit) => {
    const emitter = String(emitterCuit || '').replace(/[^0-9]/g, '');
    let tipoDocRec = 96;
    let nroDocRec = '';
    try {
      nroDocRec = invoice?.cliente?.numero ? String(invoice.cliente.numero).replace(/[^0-9]/g, '') : (invoice?.cliente?.numeroDocumento ? String(invoice.cliente.numeroDocumento).replace(/[^0-9]/g, '') : '');
    } catch (e) { nroDocRec = ''; }
    try {
      const condIVA = invoice.cliente && (invoice.cliente.condicionIVA || invoice.cliente.condicion || null);
      const cliTipo = invoice.cliente && invoice.cliente.tipo ? String(invoice.cliente.tipo).toUpperCase() : '';
      const razon = invoice.cliente && invoice.cliente.razonSocial ? String(invoice.cliente.razonSocial).toLowerCase() : '';
      const isConsumidorFinal = (Number(condIVA) === 5) || (cliTipo === 'CF') || razon.includes('consumidor final') || (!nroDocRec && Number(condIVA) === 5);
      if (isConsumidorFinal) {
        tipoDocRec = 99;
        nroDocRec = '0';
      } else if (cliTipo === 'CUIT' || (nroDocRec && nroDocRec.length === 11)) {
        tipoDocRec = 80;
        nroDocRec = nroDocRec.padStart(11, '0');
      } else {
        tipoDocRec = 96;
        nroDocRec = nroDocRec || '0';
      }
    } catch (e) {
      tipoDocRec = 96;
      nroDocRec = invoice?.cliente?.numero ? String(invoice.cliente.numero) : '0';
    }
    const caeVal = invoice?.cae || (invoice?.afip && invoice.afip.cae) || '';
    return {
      ver: 1,
      fecha: new Date(invoice.fechaComprobante || invoice.fecha).toISOString().split('T')[0],
      cuit: emitter,
      ptoVta: invoice.puntoVenta,
      tipoCmp: invoice.tipoComprobante,
      nroCmp: invoice.numero,
      importe: invoice.total,
      moneda: 'PES',
      ctz: 1,
      tipoDocRec,
      nroDocRec,
      tipoCodAut: 'E',
      codAut: caeVal || ''
    };
  }, []);

  const generateQrIfNeeded = useCallback(async (invoice) => {
    try {
      if (!invoice) return null;
      const key = invoice._id || `${invoice.tipoComprobante || ''}-${invoice.numero || ''}-${invoice.puntoVenta || ''}`;
      if (invoice.qrData) {
        qrCacheRef.current.set(key, invoice.qrData);
        return invoice.qrData;
      }
      if (qrCacheRef.current.has(key)) return qrCacheRef.current.get(key);
      const emitterCuit = (fiscalStatus?.cuit || (fiscalConfig && fiscalConfig.cuit)) || null;
      if (!invoice.cae || !emitterCuit) return null;
      try {
        debugLog('generateQrIfNeeded: generating for', key);
        const qrPayload = buildQrPayload(invoice, emitterCuit);
        const json = JSON.stringify(qrPayload);
        const base64 = window.btoa(unescape(encodeURIComponent(json)));
        const qrUrl = `https://www.afip.gob.ar/fe/qr/?p=${base64}`;
        const qrDataUrl = await QRCode.toDataURL(qrUrl, { errorCorrectionLevel: 'M', margin: 1, width: 200 });
        qrCacheRef.current.set(key, qrDataUrl);
        return qrDataUrl;
      } catch (qrErr) {
        console.error('Error generando QR en helper:', qrErr);
        return null;
      }
    } catch (e) { return null; }
  }, [fiscalStatus, fiscalConfig, buildQrPayload, debugLog]);

  const processOfflineQueue = useCallback(async () => {
    try {
      const queueStr = localStorage.getItem('offlineSales');
      if (!queueStr) return;
      const queue = JSON.parse(queueStr);
      if (!Array.isArray(queue) || queue.length === 0) return;
      toast.info(`ðŸ”„ Sincronizando ${queue.length} ventas offline...`);
      const remaining = [];
      let successCount = 0;
      for (const sale of queue) {
        try {
          const { _tempId, ticketHtml: _ticketHtml, puntoVenta: storedPV, ...payload } = sale;
          const res = await httpRequest.post(`${API_URL_SALES}/new`, payload);
          successCount++;
          if (res.data?.sale?._id) {
            try {
              const pv = storedPV || puntoVenta || 1;
              await dispatch(generateInvoiceFromSale({
                saleId: res.data.sale._id,
                puntoVenta: pv,
              })).unwrap();
              toast.success(`✅ actura generada para venta recuperada.`);
            } catch (invErr) {
              console.warn('Fallo facturación automática tras sync offline:', invErr);
            }
          }
        } catch (err) {
          console.error('Error syncing sale:', err);
          remaining.push(sale);
        }
      }
      if (successCount > 0) {
        localStorage.setItem('offlineSales', JSON.stringify(remaining));
        toast.success(`✅ {successCount} ventas sincronizadas.`);
      }
      if (remaining.length === 0) {
        localStorage.removeItem('offlineSales');
      }
    } catch (e) { console.error('Error processing offline queue:', e); }
  }, [dispatch, puntoVenta]);

  const saveOfflineSale = (saleData) => {
    try {
      const queueStr = localStorage.getItem('offlineSales') || '[]';
      const queue = JSON.parse(queueStr);
      queue.push({ ...saleData, _tempId: Date.now() });
      localStorage.setItem('offlineSales', JSON.stringify(queue));
    } catch (e) {
      console.error('Error saving offline sale:', e);
      toast.error('Error guardando venta offline (LocalStorage lleno?)');
    }
  };

  const computeTipoComprobante = useCallback((emisorCondIVA, clienteCondIVA, hasCuit) => {
    const emisor = toTipoCode(emisorCondIVA) || toTipoCode(fiscalStatus?.condicionIVA) || 6;
    const cliente = toTipoCode(clienteCondIVA) || toTipoCode(selectedCustomer?.tipoContribuyente) || 5;
    if (emisor === 6 || emisor === 4) return 11;
    if (emisor === 1) {
      if (cliente === 1 && hasCuit) return 1;
      return 6;
    }
    return 6;
  }, [selectedCustomer, fiscalStatus, toTipoCode]);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await httpRequest.get(`${API_URL_PRODUCTS}/all-products`);
      setProducts(Array.isArray(res.data) ? res.data : []);
      console.log(` Productos cargados: ${Array.isArray(res.data) ? res.data.length : 0}`);
    } catch (err) {
      console.error('Error al traer productos:', err.response?.data || err.message);
      toast.error('Error cargando productos. Verifica la conexión.');
    }
  }, []);

  const fetchPending = useCallback(async () => {
    try {
      const res = await httpRequest.get(`/invoices/pending`, { params: { limit: 10, status: 'pending' } });
      setPendingCount(res.data.total || 0);
      setPendingList(res.data.items || []);
    } catch (err) {
      console.error('Error fetching pending invoices:', err.response?.data || err.message);
    }
  }, []);

  const handleRetry = useCallback(async (pendingId) => {
    try {
      const res = await httpRequest.post(`/invoices/pending/${pendingId}/retry`);
      toast.info(res.data?.message || 'Reintento solicitado');
      if (res.status === 200) {
        setPendingList((prev) => prev.filter((p) => p._id !== pendingId));
        setPendingCount((c) => Math.max(0, c - 1));
        return;
      }
      fetchPending();
    } catch (e) {
      console.error('Error solicitando reintento:', e.response?.data || e.message);
      const msg = e.response?.data?.message || e.response?.data || e.message || 'Error solicitando reintento';
      toast.error(String(msg).slice(0, 200));
      fetchPending();
    }
  }, [fetchPending]);

  const handleDismiss = useCallback(async (pendingId) => {
    if (!window.confirm('¿Descartar esta factura pendiente? Se perderá el registro.')) return;
    try {
      await httpRequest.post(`/invoices/pending/${pendingId}/dismiss`);
      toast.info('Factura pendiente descartada');
      setPendingList((prev) => prev.filter((p) => p._id !== pendingId));
      setPendingCount((c) => Math.max(0, c - 1));
    } catch (e) {
      console.error('Error descartando pendiente:', e);
      toast.error('Error al descartar');
    }
  }, []);

  const computeDiscountedPrice = useCallback((p, qty = 1) => {
    const base = Number(p.salePrice ?? p.price ?? p.unitPrice ?? 0) || 0;
    const type = p.discountType || 'none';
    const value = Number(p.discountValue || 0) || 0;
    const tiers = Array.isArray(p.discountTiers) ? p.discountTiers : [];
    if (!type || type === 'none') return Math.round(base * 100) / 100;
    if (type === 'percent') return Math.round((base * (1 - value / 100)) * 100) / 100;
    if (type === 'fixed') return Math.round(Math.max(0, base - value) * 100) / 100;
    if (type === 'tiered') {
      let match = null;
      for (const t of tiers) {
        const min = Number(t.minQty) || 0;
        const max = (t.maxQty === '' || t.maxQty === undefined) ? undefined : Number(t.maxQty);
        if (qty >= min && (max === undefined || qty <= max)) {
          if (!match || min > (Number(match.minQty) || 0)) match = t;
        }
      }
      if (!match) return Math.round(base * 100) / 100;
      if (match.type === 'percent') return Math.round((base * (1 - Number(match.value) / 100)) * 100) / 100;
      if (match.type === 'fixed') return Math.round(Math.max(0, base - Number(match.value)) * 100) / 100;
    }
    return Math.round(base * 100) / 100;
  }, []);

  const addToCart = (product, qty = null, overrideUnitPrice = null) => {
    if (!product || !(product._id || product.id) || !product.name) {
      try { playErrorBeep(); } catch (e) { }
      toast.error('âŒ Producto inválid✅ No se agregó al carrito');
      return false;
    }
    if (product.variants && product.variants.length > 0 && !product._selectedVariant) {
      setVariantSelection({ product, qty, overrideUnitPrice });
      return false;
    }
    const rawPriceFromProduct = product.salePrice ?? product.price ?? product.unitPrice ?? product.sale_price ?? '';
    const parsedRawPrice = parseFloat(String(rawPriceFromProduct || '').replace(/[^0-9.-]/g, ''));
    const priceVal = (overrideUnitPrice != null && isFinite(Number(overrideUnitPrice))) ? Number(overrideUnitPrice) : (isFinite(parsedRawPrice) ? parsedRawPrice : NaN);
    if (!isFinite(priceVal)) {
      try { playErrorBeep(); } catch (e) { }
      toast.error('âŒ Producto sin precio válid✅ No se agregó al carrito');
      return false;
    }
    const useQty = (typeof qty === 'number' && qty > 0) ? qty : (pendingQuantity || 1);
    if (pendingQuantity) setPendingQuantity(null);
    let discriminar = shouldDiscriminateIVA(fiscalStatus || {});
    const prodIsMonotributo = (() => {
      try {
        const cond = fiscalStatus?.condicionIVA ?? fiscalStatus?.condicion ?? fiscalStatus?.tipoResponsable;
        if (cond == null) return false;
        if (Number(cond) === 6) return true;
        if (typeof cond === 'string' && /monotr/i.test(cond)) return true;
        return false;
      } catch (e) { return false; }
    })();
    if (prodIsMonotributo) discriminar = false;
    const cartUid = product.variantId ? `${product._id}:${product.variantId}` : product._id;
    const existing = cart.find((p) => (p.cartUid === cartUid) || (!p.cartUid && p.productId === product._id));
    let updatedCart;
    if (existing) {
      const productAllowsNeg = allowNegStock || (product && !!product.noControlStock) || (product && getLocalNoControl(product._id));
      if (!productAllowsNeg && (existing.quantity + useQty > product.stock)) {
        const confirmMsg = `âš ï¸ Stock insuficiente para "${product.name}".\n\nStock actual: ${product.stock}\nCantidad en carrito: ${existing.quantity}\nNueva cantidad: ${existing.quantity + useQty}\n\n¿Desea continuar con la venta sin stock?`;
        if (!window.confirm(confirmMsg)) {
          return;
        }
        // Si el usuario acepta, continuamos sin retornar
      }
      updatedCart = cart.map((p) =>
        (p.cartUid === cartUid || (!p.cartUid && p.productId === product._id))
          ? (() => {
            const newQty = p.quantity + useQty;
            const computedUnit = computeDiscountedPrice(p, newQty);
            const unitWithDisc = (overrideUnitPrice != null) ? Math.round(((p.quantity * (p.unitPriceWithDiscount || computedUnit)) + (useQty * overrideUnitPrice)) / (newQty) * 100) / 100 : computedUnit;
            return { ...p, quantity: newQty, unitPriceWithDiscount: unitWithDisc, subtotal: (unitWithDisc * newQty).toFixed(2) };
          })() : p
      );
      toast.success(`✅ Agregado: ${product.name} (cantidad ${existing.quantity + useQty})`, { autoClose: 1500, toastId: 'cart-add-toast' });
    } else {
      const productAllowsNeg = allowNegStock || (product && !!product.noControlStock) || (product && getLocalNoControl(product._id));
      if (!productAllowsNeg && product.stock < useQty) {
        const confirmMsg = `⚠️ El producto "${product.name}" no tiene suficiente stock.\n\nStock actual: ${product.stock}\nCantidad solicitada: ${useQty}\n\n¿Desea continuar con la venta sin stock?`;
        if (!window.confirm(confirmMsg)) {
          return;
        }
        // Si acepta, continuamos
      }
      const unitWithDiscount = overrideUnitPrice != null ? Math.round(Number(overrideUnitPrice) * 100) / 100 : computeDiscountedPrice(product, useQty);
      updatedCart = [
        ...cart,
        {
          productId: product._id, cartUid, variantId: product.variantId, variantName: product.variantName,
          name: product.variantName ? `${product.name} (${product.variantName})` : product.name,
          quantity: useQty, price: priceVal, unitPriceWithDiscount: unitWithDiscount, subtotal: (unitWithDiscount * useQty).toFixed(2),
          discountType: product.discountType || 'none', discountValue: typeof product.discountValue === 'number' ? product.discountValue : 0,
          discountTiers: Array.isArray(product.discountTiers) ? product.discountTiers : [],
          alicuotaIVACode: discriminar ? (product.alicuotaIVACode ?? product.alicuotaIVA ?? 5) : 0,
          stock: product.stock,
          ecommerceImages: Array.isArray(product.ecommerceImages) ? product.ecommerceImages : [],
        },
      ];
      toast.success(`✅ Agregado: ${product.name} (cantidad ${useQty})`, { autoClose: 1500, toastId: 'cart-add-toast' });
    }
    setCart(updatedCart);
    setSearch('');
    setSuggestions([]);
    if (!scanJustHappenedRef.current) {
      setTimeout(() => { try { searchInputRef.current?.focus(); } catch (e) { } }, 50);
    }
    return true;
  };

  const handleVariantSelected = (variant) => {
    if (!variantSelection) return;
    const { product, qty, overrideUnitPrice } = variantSelection;
    const productVariant = {
      ...product,
      variantId: variant.sku || variant._id || `${product._id}-${variant.attributes?.color}-${variant.attributes?.size}`,
      variantName: [variant.attributes?.color, variant.attributes?.size].filter(Boolean).join(' / '),
      stock: variant.stock,
      price: variant.price || product.price,
      salePrice: variant.price || product.salePrice,
      costPrice: variant.costPrice || product.costPrice,
      _selectedVariant: true
    };
    addToCart(productVariant, qty, overrideUnitPrice);
    setVariantSelection(null);
  };


  // 4. EFFECTS
  useEffect(() => {
    let active = true;
    httpRequest.get('/cash-register/current')
      .then(res => {
        if (active) {
          setCashRegisterOpen(res.data);
          setCashCheckFinished(true);
        }
      })
      .catch(() => {
        if (active) {
          setCashRegisterOpen(null);
          setCashCheckFinished(true);
        }
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('ðŸŒ Conexión restablecida. Sincronizando ventas...');
      processOfflineQueue();
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('ðŸ“¡ Modo Offline activad✅ Las ventas se guardarán localmente.');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    if (navigator.onLine) processOfflineQueue();
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [processOfflineQueue]);

  useEffect(() => {
    const handleOnlineRetry = async () => {
      if (pendingList.length === 0) return;
      console.log('[Sales] Network online detected. Retrying pending invoices...');
      toast.info('ðŸŒ Conexión restaurada: Reintentando facturas pendientes...');
      const queue = [...pendingList];
      for (const p of queue) {
        try {
          await handleRetry(p._id);
          await new Promise(r => setTimeout(r, 800));
        } catch (e) { console.error('Auto-retry failed for', p._id, e); }
      }
    };
    window.addEventListener('online', handleOnlineRetry);
    return () => window.removeEventListener('online', handleOnlineRetry);
  }, [pendingList, handleRetry]);

  useEffect(() => {
    fetchProducts();
    dispatch(getFiscalStatus());
    dispatch(getTiposComprobante());
    fetchPending();
  }, [dispatch, fetchProducts, fetchPending]);

  useEffect(() => {
    if (fiscalStatus?.puntosVenta?.length) {
      setPuntoVenta(fiscalStatus.puntosVenta[0].numero);
    }
  }, [fiscalStatus]);

  useEffect(() => {
    const hasCuit = Boolean(selectedCustomer?.cuit);
    const nextTipo = computeTipoComprobante(fiscalStatus?.condicionIVA, selectedCustomer?.tipoContribuyente, hasCuit);
    setTipoComprobanteSel(nextTipo);
  }, [fiscalStatus, selectedCustomer, computeTipoComprobante]);

  useEffect(() => {
    const handleGlobalEsc = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        const anyModalOpen = showPaymentModal || showPendingModal || showSuccessModal || scannerOpen || priceLookupOpen || !!variantSelection;
        if (!anyModalOpen && searchInputRef.current) {
          e.preventDefault();
          setSearch('');
          setSuggestions([]);
          setActiveSuggestionIdx(-1);
          searchInputRef.current.focus();
          if (typeof searchInputRef.current.select === 'function') {
            searchInputRef.current.select();
          }
        }
      }
    };
    window.addEventListener('keydown', handleGlobalEsc);
    return () => window.removeEventListener('keydown', handleGlobalEsc);
  }, [showPaymentModal, showPendingModal, showSuccessModal, scannerOpen, priceLookupOpen, variantSelection]);
  // Actualizar total
  useEffect(() => {
    // Recalculate subtotal and IVA from cart items using discounted unit prices and alicuota codes
    const subtotal = cart.reduce((acc, item) => acc + (Number(item.unitPriceWithDiscount || item.price || 0) * Number(item.quantity || 0)), 0);
    // Decide whether the emitter discriminates IVA (e.g. Responsable Inscripto)
    let discriminar = shouldDiscriminateIVA(fiscal);
    // Force no-discrimination when our reusable emitter flag says Monotributo
    if (emitterIsMonotributo) discriminar = false;

    // Debug logging (disabled by default; enable with localStorage.setItem('debugSales','1'))
    try { debugLog('fiscal:', fiscal, '-> discriminarIVA:', discriminar, 'cartItems:', cart); } catch (e) { }

    // IVA per item (Calculamos siempre para el total, discriminamos o no por configuración fiscal)
    const percents = new Set();
    const ivaTotal = cart.reduce((acc, item) => {
      const code = item.alicuotaIVACode ?? item.alicuotaIVA ?? 5;
      const pct = getAlicuotaPercent(code) || 0;
      if (pct > 0) percents.add(pct);
      const percent = pct / 100;
      const itemBase = Number(item.unitPriceWithDiscount || item.price || 0) * Number(item.quantity || 0);
      const itemIva = itemBase * percent;
      return acc + itemIva;
    }, 0);

    const roundedSubtotal = Math.round(subtotal * 100) / 100;
    const roundedIva = Math.round(ivaTotal * 100) / 100;
    const totalCalc = Math.round((roundedSubtotal + roundedIva) * 100) / 100;

    setSubtotalAmt(roundedSubtotal);
    // If emitter doesn't discriminate IVA, zero the IVA and set total = subtotal
    if (!discriminar) {
      // Si no discrimina (Monotributo), el IVA no se muestra como lí­nea aparte en el ticket
      // pero el TOTAL de la venta debe ser el final cobrado (Neto + IVA)
      setIvaAmt(0);
      setIvaLabel('0%');
      setTotal(Math.max(0, totalCalc - Number(saleDiscount || 0)));
      return;
    }

    setIvaAmt(roundedIva);
    // Determine IVA label: single percent or 'varios' or '0%'
    if (percents.size === 0) setIvaLabel('0%');
    else if (percents.size === 1) setIvaLabel(`${Array.from(percents)[0]}%`);
    else setIvaLabel('varios');
    setTotal(Math.max(0, totalCalc - Number(saleDiscount || 0)));
  }, [cart, fiscal, emitterIsMonotributo, debugLog, saleDiscount]);

  // Calcular cambio automáticamente
  useEffect(() => {
    if (paymentMethod === 'Efectivo' && amountPaid) {
      const cambio = parseFloat(amountPaid) - total;
      setChange(cambio >= 0 ? cambio : 0);
    } else {
      setChange(0);
    }
  }, [amountPaid, total, paymentMethod]);


  // Helper to compute discounted unit price given an item-like object or product

  const handleCustomerSearch = async (value) => {
    setCustomerSearch(value);
    if (!value || value.length < 2) {
      setCustomerSuggestions([]);
      return;
    }

    try {
      const res = await httpRequest.get(`${API_URL_CUSTOMERS}/search`, { params: { query: value } });
      setCustomerSuggestions(res.data);
    } catch (err) {
      console.error('Error buscando clientes:', err);
    }
  };

  // Precio: abrir modal de consulta de precio para el término actual o producto dado
  const openPriceLookup = useCallback(async (productCandidate) => {
    try {
      let prod = productCandidate || null;
      // If not provided, try to resolve from current search
      if (!prod && search) {
        // try exact local match first
        prod = products.find((p) => p.barcode === search || p.name.toLowerCase() === search.toLowerCase());
      }
      // If still not found and search looks numeric, ask backend by barcode
      if (!prod && search && /^\d+$/.test(search)) {
        try {
          const res = await httpRequest.get(`${API_URL_PRODUCTS}/search-by-barcode`, { params: { code: search } });
          let r = res.data;
          if (Array.isArray(r)) r = r.length ? r[0] : null;
          if (r && (r._id || r.barcode)) prod = r;
        } catch (e) {
          // ignore backend errors for lookup; we'll show not-found
        }
      }

      // Open modal regardless; if product found, prefill it, otherwise allow manual search inside modal
      setPriceLookupProduct(prod || null);
      setPriceLookupOpen(true);
    } catch (e) {
      console.error('openPriceLookup error', e);
      toast.error('Error abriendo consulta de precio');
    }
  }, [search, products]);

  const closePriceLookup = () => {
    setPriceLookupOpen(false);
    setPriceLookupProduct(null);
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.razonSocial || '');
    setCustomerSuggestions([]);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
  };

  // Manejar bíºsqueda y sugerencias
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    // reset active suggestion when user types
    setActiveSuggestionIdx(-1);
    if (!val) {
      setSuggestions([]);
      return;
    }

    // Detect quantity prefix like "5*" or "10*barcode"
    const qtyMatch = val.match(/^(\d+)\*(.*)$/);
    if (qtyMatch) {
      const qty = parseInt(qtyMatch[1], 10);
      const rest = qtyMatch[2] || '';
      // If user just typed '5*' set pending quantity and clear suggestions
      if (rest.trim() === '') {
        setPendingQuantity(qty);
        setSuggestions([]);
        // clear the visible search to give cleaner UX
        setSearch('');
        return;
      }
      // If there's remaining text after the prefix, treat it as the actual search term
      setPendingQuantity(qty);
      // If there's remaining text after the prefix, set the visible search to that text
      setSearch(rest);
      // proceed below using `rest` as the search term
      var term = rest;
    }

    const effectiveVal = typeof term !== 'undefined' ? term : val;

    // Si la entrada es solo níºmeros, no mostrar sugerencias (esperar Enter)
    if (/^\d+$/.test(effectiveVal)) {
      setSuggestions([]);
      return;
    }

    // Si hay letras, mostrar sugerencias por nombre o código
    const lower = effectiveVal.toLowerCase();
    let matches = (products || []).filter((p) =>
      (p.name && p.name.toLowerCase().includes(lower)) ||
      (p.barcode && String(p.barcode).toLowerCase().includes(lower))
    );

    setSuggestions(matches);
  };

  // Scroll helper: ensure the active suggestion is visible inside the box
  useEffect(() => {
    try {
      if (activeSuggestionIdx < 0) return;
      const box = suggestionsRef.current;
      if (!box) return;
      const child = box.children[activeSuggestionIdx];
      if (child && typeof child.scrollIntoView === 'function') {
        child.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    } catch (e) { }
  }, [activeSuggestionIdx]);

  const getDiscountBadgeForProduct = (prod, qty = 1) => {
    try {
      const base = Number(prod.salePrice ?? prod.price ?? prod.unitPrice ?? 0) || 0;
      const disc = computeDiscountedPrice(prod, qty);
      if (!isFinite(base) || base <= 0) return null;
      if (disc >= base) return null;
      const amount = Math.round((base - disc) * 100) / 100;
      const pct = Math.round((amount / base) * 100);
      if (pct > 0) return `-${pct}%`;
      return `-$${amount.toFixed(2)}`;
    } catch (e) { return null; }
  };

  // Incrementar cantidad de un producto
  const incrementQuantity = (cartUid) => {
    // Find item first to check stock
    const itemToUpdate = cart.find(item => (item.cartUid || item.productId) === cartUid);
    if (!itemToUpdate) return;

    const prod = products.find(p => p._id === itemToUpdate.productId);
    const prodAllowsNeg = allowNegStock || (prod && !!prod.noControlStock) || getLocalNoControl(itemToUpdate.productId);

    if (!prodAllowsNeg && (itemToUpdate.quantity + 1 > itemToUpdate.stock)) {
      const confirmMsg = `âš ï¸ Stock insuficiente para "${itemToUpdate.name}".\n\nStock actual: ${itemToUpdate.stock}\nCantidad actual: ${itemToUpdate.quantity}\nNueva cantidad: ${itemToUpdate.quantity + 1}\n\n¿Desea continuar con la venta sin stock?`;
      if (!window.confirm(confirmMsg)) {
        return;
      }
    }

    setCart(cart.map((item) => {
      const itemId = item.cartUid || item.productId;
      if (itemId === cartUid) {
        const newQty = item.quantity + 1;
        const unitWithDisc = computeDiscountedPrice(item, newQty);
        return {
          ...item,
          quantity: newQty,
          unitPriceWithDiscount: unitWithDisc,
          subtotal: (unitWithDisc * newQty).toFixed(2),
        };
      }
      return item;
    }));
  };

  // Decrementar cantidad de un producto
  const decrementQuantity = (cartUid) => {
    setCart(cart.map((item) => {
      const itemId = item.cartUid || item.productId;
      if (itemId === cartUid) {
        if (item.quantity <= 1) {
          toast.info('â„¹ï¸ Usa el botón X para eliminar el producto');
          return item;
        }
        const newQty = item.quantity - 1;
        const unitWithDisc = computeDiscountedPrice(item, newQty);
        return {
          ...item,
          quantity: newQty,
          unitPriceWithDiscount: unitWithDisc,
          subtotal: (unitWithDisc * newQty).toFixed(2),
        };
      }
      return item;
    }));
  };

  // Modificar precio unitario de un item en el carrito (solo admin/owner)
  const updateCartItemPrice = (cartUid, newPriceStr) => {
    // Permission check
    if (!user || (user.role !== 'admin' && user.role !== 'owner')) {
      toast.error('ðŸš« No tienes permiso para modificar precios');
      return;
    }

    const price = parseFloat(newPriceStr);
    if (isNaN(price) || price < 0) {
      toast.error('âŒ Precio inválido');
      return;
    }

    setCart(cart.map(item => {
      const itemId = item.cartUid || item.productId;
      if (itemId === cartUid) {
        const quantity = item.quantity;
        return {
          ...item,
          unitPriceWithDiscount: price,
          // Flag as manually edited if needed for UI, though logic below compares with base price
          subtotal: (price * quantity).toFixed(2)
        };
      }
      return item;
    }));
    toast.success('Precio modificado');
  };

  // Quitar un producto del carrito
  const handleRemove = (cartUid) => {
    try {
      const p = cart.find((c) => (c.cartUid || c.productId) === cartUid);
      if (!p) return;
      if (!window.confirm(`¿Eliminar ${p.name} del carrito?`)) return;
      const updated = cart.filter((c) => (c.cartUid || c.productId) !== cartUid);
      setCart(updated);
      toast.success(`ðŸ—‘ï¸ ${p.name} eliminado del carrito`);
    } catch (e) {
      console.error('Error quitando producto del carrito', e);
      toast.error('Error eliminando producto');
    }
  };

  // Quitar una lí­nea de descuento de combo manualmente: marcar combo como deshabilitado
  // para la venta actual y remover su lí­nea del carrito. Esto evita que detectAndApplyCombos
  // la vuelva a aí±adir mientras permanezca en la lista disabledComboIds.
  const handleRemoveComboDiscount = (comboId) => {
    try {
      if (!comboId) return;
      const idStr = String(comboId);
      setDisabledComboIds((prev) => Array.from(new Set([...(prev || []), idStr])));
      // remove any existing discount lines for that combo
      setCart((prev) => (prev || []).filter((it) => !(String(it.productId).startsWith(`combo-discount:${idStr}`))));
      toast.info('Descuento de combo removido manualmente');
    } catch (e) {
      console.error('Error removing combo discount', e);
      toast.error('Error quitando descuento de combo');
    }
  };


  // Simple wrapper: always add the product to cart. Combo UI interactions (discount lines)
  // are handled by `detectAndApplyCombos` which scans the cart and inserts discount items.
  const addOrOpenCombo = (product, qty = null, overrideUnitPrice = null) => {
    try {
      if (!product) return false;
      return addToCart(product, qty, overrideUnitPrice);
    } catch (e) {
      console.error('addOrOpenCombo error', e);
      return false;
    }
  };

  // Detectar combos completos en el carrito y aplicar el ajuste como una lí­nea de descuento separada.
  // Para cada producto definido como combo se determina cuántos bundles completos hay en el carrito.
  // Si el precio del combo (combo.comboPrice) es menor que la suma de precios base de los componentes,
  // se inserta/actualiza una lí­nea de descuento con valor negativo que representa el ahorro total.
  const detectAndApplyCombos = useCallback(() => {
    try {
      if (!applyBundlesAuto) return; // feature toggle: skip automatic application
      if (!Array.isArray(products) || products.length === 0 || !Array.isArray(cart) || cart.length === 0) return;

      // Build a map of cart quantities by productId for quick lookups
      const cartQtyById = {};
      cart.forEach((item) => {
        const id = String(item.productId);
        cartQtyById[id] = (cartQtyById[id] || 0) + (Number(item.quantity) || 0);
      });

      // Start from existing cart but remove any existing combo-discount lines â€” we'll re-add updated ones
      const oldDiscountLines = cart.filter((it) => String(it.productId).startsWith('combo-discount:'));
      let newCart = cart.filter((it) => !String(it.productId).startsWith('combo-discount:')).map((c) => ({ ...c }));

      const combos = (products || []).filter((p) => p && p.isCombo && Array.isArray(p.components) && p.components.length > 0);
      const newDiscounts = [];

      combos.forEach((combo) => {
        try {
          // Respect manual disable list: if cashier removed this combo discount, skip it
          if (disabledComboIds && disabledComboIds.includes(String(combo._id))) return;
          // Build requirement map from declared components
          const reqMap = {};
          combo.components.forEach((comp) => {
            if (!comp || !comp.productId) return;
            const pid = String(comp.productId);
            reqMap[pid] = (Number(comp.qty) || 1);
          });
          // UX convenience: if the combo was created by editing a product (the common flow
          // in this app) we treat the combo product itself as an implicit component with qty=1
          // when it's not explicitly listed. This allows marking product "Aceite" as combo
          // and having the bundle be Aceite + Arroz without needing to add Aceite to components.
          const comboSelfId = String(combo._id);
          let implicitSelfAdded = false;
          if (!reqMap[comboSelfId]) {
            reqMap[comboSelfId] = 1;
            implicitSelfAdded = true;
          }

          // Determine number of full bundles
          let bundles = Infinity;
          Object.entries(reqMap).forEach(([pid, reqQty]) => {
            const available = cartQtyById[pid] || 0;
            const possible = Math.floor(available / reqQty);
            bundles = Math.min(bundles, possible);
          });
          if (!isFinite(bundles) || bundles <= 0) return;

          // Combo target price per bundle
          const rawComboPrice = combo.comboPrice ?? combo.price ?? combo.salePrice ?? combo.unitPrice ?? 0;
          const comboTotal = parseFloat(String(rawComboPrice || '').replace(/[^0-9.-]/g, '')) || 0;
          if (!(comboTotal > 0)) return; // nothing to apply

          // Compute current subtotal for one bundle using actual cart line prices when available.
          // Use `reqMap` (which already includes an implicit self when added) so the implicit
          // component is counted in the sum as well. Map declared components by id to access
          // priceOverride when present.
          let currentOneBundle = 0;
          const compById = {};
          (combo.components || []).forEach((comp) => {
            if (comp && comp.productId) compById[String(comp.productId)] = comp;
          });
          // Iterate over reqMap entries (pid -> requiredQty) so implicit self is included
          Object.entries(reqMap).forEach(([pid, qtyReqRaw]) => {
            const qtyReq = Number(qtyReqRaw) || 1;
            const declaredComp = compById[pid] || null;
            // prefer priceOverride defined in declared combo component
            if (declaredComp && declaredComp.priceOverride != null && declaredComp.priceOverride !== '') {
              const unitBase = Number(declaredComp.priceOverride) || 0;
              currentOneBundle += unitBase * qtyReq;
              return;
            }
            // prefer the unit price present in the cart (unitPriceWithDiscount or price)
            const cartLine = cart.find((it) => String(it.productId) === pid);
            if (cartLine) {
              const unit = Number(cartLine.unitPriceWithDiscount ?? cartLine.price ?? 0) || 0;
              currentOneBundle += unit * qtyReq;
              return;
            }
            // fallback to product list price
            const prod = products.find((pp) => String(pp._id) === pid) || null;
            const unitBase = prod ? computeDiscountedPrice(prod, 1) : 0;
            currentOneBundle += unitBase * qtyReq;
          });

          // Discount per bundle is how much the customer should save to make the components equal the combo price
          const discountPerBundle = currentOneBundle - comboTotal;
          if (!(discountPerBundle > 0)) return; // combo is not cheaper than components, nothing to do

          const totalDiscount = Math.round(discountPerBundle * bundles * 100) / 100;

          // Determine alicuota code to apply to discount line. Prefer combo product's code (implicit self)
          // then fall back to first declared component. Default to 5.
          let alicuotaCode = 5; // default safe code
          if (implicitSelfAdded) {
            const selfProd = products.find((pp) => String(pp._id) === comboSelfId);
            if (selfProd && (selfProd.alicuotaIVACode || selfProd.alicuotaIVA)) alicuotaCode = selfProd.alicuotaIVACode ?? selfProd.alicuotaIVA;
          }
          if (alicuotaCode === 5) {
            for (const comp of combo.components) {
              const prod = products.find((pp) => String(pp._id) === String(comp.productId));
              if (prod && (prod.alicuotaIVACode || prod.alicuotaIVA)) { alicuotaCode = prod.alicuotaIVACode ?? prod.alicuotaIVA; break; }
            }
          }

          const discountItemId = `combo-discount:${combo._id}`;
          // Snapshot components with resolved unit prices used for the calculation so we have traceability
          // Build a snapshot of components used in the calculation (include implicit self when added)
          const componentsSnapshot = [];
          (combo.components || []).forEach((comp) => {
            const pid = String(comp.productId);
            const cartLine = cart.find((it) => String(it.productId) === pid) || {};
            const prod = products.find((pp) => String(pp._id) === pid) || null;
            const unitUsed = Number(cartLine.unitPriceWithDiscount ?? cartLine.price ?? (prod ? computeDiscountedPrice(prod, 1) : 0)) || 0;
            componentsSnapshot.push({
              productId: pid,
              qty: Number(comp.qty) || 1,
              priceOverride: comp.priceOverride != null ? Number(comp.priceOverride) : null,
              unitUsed,
            });
          });
          if (implicitSelfAdded) {
            const pid = comboSelfId;
            const cartLine = cart.find((it) => String(it.productId) === pid) || {};
            const prod = products.find((pp) => String(pp._id) === pid) || null;
            const unitUsed = Number(cartLine.unitPriceWithDiscount ?? cartLine.price ?? (prod ? computeDiscountedPrice(prod, 1) : 0)) || 0;
            componentsSnapshot.push({ productId: pid, qty: 1, priceOverride: null, unitUsed });
          }

          const discountItem = {
            productId: discountItemId,
            name: `Descuento combo: ${combo.name || String(combo._id)}`,
            quantity: bundles,
            unitPriceWithDiscount: -Math.round(discountPerBundle * 100) / 100,
            price: 0,
            subtotal: (-(totalDiscount)).toFixed(2),
            alicuotaIVACode: alicuotaCode,
            isComboDiscount: true,
            comboId: combo._id,
            comboName: combo.name || null,
            bundlesApplied: bundles,
            componentsSnapshot,
          };

          newDiscounts.push(discountItem);
        } catch (e) {
          console.debug('combo detect error', e);
        }
      });

      // Append new discount lines to cart
      if (newDiscounts.length > 0) {
        newCart = [...newCart, ...newDiscounts];
      }

      // Compare old discount lines with new ones to avoid unnecessary setState
      const oldSerialized = JSON.stringify(oldDiscountLines.map((d) => ({ id: d.productId, qty: d.quantity, subtotal: Number(d.subtotal || (Number(d.unitPriceWithDiscount || 0) * Number(d.quantity || 0))) })));
      const newSerialized = JSON.stringify(newDiscounts.map((d) => ({ id: d.productId, qty: d.quantity, subtotal: Number(d.subtotal || (Number(d.unitPriceWithDiscount || 0) * Number(d.quantity || 0))) })));

      if (oldSerialized !== newSerialized) {
        setCart(newCart);
      }
    } catch (e) {
      console.error('Error detectAndApplyCombos', e);
    }
  }, [cart, products, computeDiscountedPrice, disabledComboIds, applyBundlesAuto]);

  // Run detection whenever cart or products change
  useEffect(() => {
    try {
      detectAndApplyCombos();
    } catch (e) { }
  }, [cart, products, detectAndApplyCombos]);

  // Helper para analizar códigos de balanza (reutilizable en scanner y manual)
  const tryParseScaleBarcode = (code) => {
    try {
      const cleanCode = String(code || '').trim();
      if ((cleanCode.length !== 13 && cleanCode.length !== 12) || !cleanCode.startsWith('20')) return null;

      const normalize = (s) => String(s || '').replace(/[^0-9a-zA-Z]/g, '').replace(/^0+/, '');
      const pluRaw = cleanCode.substring(2, 7);
      const weightRaw = cleanCode.substring(7, 12);

      const nPlu = normalize(pluRaw);
      const foundPlu = products.find((p) => normalize(p.barcode) === nPlu);

      console.log('[ScaleDebug] Input:', cleanCode, 'PLU Raw:', pluRaw, 'Weight Raw:', weightRaw, 'Found Product:', foundPlu?.name);

      if (foundPlu) {
        const unitPrice = Number(foundPlu.salePrice || foundPlu.price || 0);
        // La balanza envía el peso en gramos en los últimos 5 dígitos (ej: 02500 -> 2500g -> 2.5kg)
        const weightGrams = parseInt(weightRaw, 10);
        const qtyKg = weightGrams / 1000;
        const totalPrice = Math.round(qtyKg * unitPrice * 100) / 100;

        console.log('[ScaleDebug] UnitPrice:', unitPrice, 'WeightGrams:', weightGrams, 'QtyKg:', qtyKg, 'TotalPrice:', totalPrice);

        if (unitPrice > 0) {
          return { product: foundPlu, qty: qtyKg, totalPrice };
        } else {
          toast.error(`⚠️ El producto ${foundPlu.name} no tiene precio asignado. Imposible calcular peso.`);
          return null; // Force exit
        }
      } else {
        console.warn('[ScaleDebug] Product with PLU', pluRaw, '(norm:', nPlu, ') not found in product list.');
        toast.error(`⚠️ Código de balanza detectado pero no existe producto con PLU ${pluRaw}`);
      }
    } catch (e) { return null; }
    return null;
  };

  // Manejar enter en input
  const handleKeyDown = (e) => {
    // Navigate suggestions with Arrow keys
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!suggestions || suggestions.length === 0) return;
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      setActiveSuggestionIdx((prev) => {
        const next = prev + dir;
        if (next < 0) return suggestions.length - 1;
        if (next >= suggestions.length) return 0;
        return next;
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      // If a suggestion is active, choose it
      if (activeSuggestionIdx >= 0 && suggestions[activeSuggestionIdx]) {
        try { addOrOpenCombo(suggestions[activeSuggestionIdx]); } catch (err) { console.error(err); }
        setActiveSuggestionIdx(-1);
        return;
      }

      if (search.trim() === '') return;

      // 1. Prioridad: Intentar detectar código de balanza (para que calcule peso)
      const scale = tryParseScaleBarcode(search);
      if (scale) {
        addOrOpenCombo(scale.product, scale.qty);
        // Toast redundant removed here (addToCart handles it)
        if (isMobile) hideMobileKeyboard();
        else {
          setSearch('');
          setTimeout(() => { try { if (searchInputRef && searchInputRef.current) searchInputRef.current.focus(); } catch (e) { } }, 80);
        }
        return;
      }

      // 2. Si no es balanza, buscar coincidencia exacta
      const exact = products.find(
        (p) => p.barcode === search || p.name.toLowerCase() === search.toLowerCase()
      );

      if (exact) {
        addOrOpenCombo(exact);
        if (isMobile) hideMobileKeyboard();
      } else if (/^\d+$/.test(search)) {
        toast.error(`âŒ Producto con código ${search} no encontrado`);
        try { playErrorBeep(); } catch (e) { }
        // Clear the search input so user can scan again without manual deletion
        try { if (!isMobile) { setSearch(''); setTimeout(() => { try { if (searchInputRef && searchInputRef.current) searchInputRef.current.focus(); } catch (e) { } }, 80); } else { setSearch(''); hideMobileKeyboard(); } } catch (e) { }
      } else {
        toast.error('âŒ Producto no encontrado');
        try { playErrorBeep(); } catch (e) { }
        // Clear the search to allow next attempt
        try { if (!isMobile) { setSearch(''); setTimeout(() => { try { if (searchInputRef && searchInputRef.current) searchInputRef.current.focus(); } catch (e) { } }, 80); } else { setSearch(''); hideMobileKeyboard(); } } catch (e) { }
      }
    }
  };

  // Handler para recibir el texto decodificado por la cámara
  const handleScanned = (decodedText) => {
    try {
      setScannerOpen(false);

      // Helper function to clean up mobile state after scan (success or failure)
      const cleanupMobileScanState = () => {
        try {
          // Clear the scanned code display after a short delay
          setTimeout(() => {
            try { setLastScannedCodeMobile(''); } catch (e) { }
            // Restore the search input to usable state
            try { restoreSearchInputForMobile(false); } catch (e) { }
          }, 300);
        } catch (e) { }
      };

      // Safety timeout: if something goes wrong, restore input after 3 seconds
      const safetyTimeout = setTimeout(() => {
        try {
          console.debug('[Sales] Safety timeout: restoring mobile input state');
          setLastScannedCodeMobile('');
          restoreSearchInputForMobile(false);
        } catch (e) { }
      }, 3000);

      // Force-remove focus from any input immediately after scanner closes to avoid mobile keyboard.
      try {
        setTimeout(() => {
          try { if (searchInputRef && searchInputRef.current) searchInputRef.current.blur(); } catch (e) { }
          try { if (document.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur(); } catch (e) { }
          try {
            // create a persistent, focusable guard and focus it to steal focus away from inputs
            let guard = document.getElementById('sales-focus-guard');
            if (!guard) {
              guard = document.createElement('div');
              guard.id = 'sales-focus-guard';
              guard.tabIndex = -1;
              guard.style.position = 'absolute';
              guard.style.left = '-10000px';
              guard.style.height = '1px';
              guard.style.width = '1px';
              document.body.appendChild(guard);
            }
            try { guard.focus(); } catch (e) { }
            // remove guard after a short delay
            setTimeout(() => { try { guard.parentNode && guard.parentNode.removeChild(guard); } catch (e) { } }, 1200);
          } catch (e) { }
        }, 40);
      } catch (e) { }
      const raw = String(decodedText || '').trim();
      if (!raw) {
        // Empty scan - clean up and return
        cleanupMobileScanState();
        clearTimeout(safetyTimeout);
        return;
      }
      const code = raw;
      console.debug('[Sales] scanned code:', code);
      // Mostrar en el search para que el usuario vea el valor.
      // En móvil evitamos setear el input para que no reciba foco y abra el teclad✅
      if (isMobile) {
        setLastScannedCodeMobile(code);
        // block the search input to guarantee keyboard won't open; user must press 'Usar'
        try { blockSearchInputForMobile(); } catch (e) { }
      } else {
        setSearch(code);
      }
      // Mark that a scan just happened to avoid focusing the input immediately
      try { scanJustHappenedRef.current = true; } catch (e) { }
      setTimeout(() => { try { scanJustHappenedRef.current = false; } catch (e) { } }, 2000);

      // Install a one-time focusin listener that will blur the input if it receives focus
      try {
        const onFocusIn = (evt) => {
          try {
            const tgt = evt.target;
            if (!tgt) return;
            // if the focus target is the search input and we just scanned, blur it
            if (scanJustHappenedRef && scanJustHappenedRef.current) {
              if (tgt === searchInputRef.current) {
                try { tgt.blur(); } catch (e) { }
                try { hideMobileKeyboard(); } catch (e) { }
              }
            }
          } catch (e) { }
        };
        document.addEventListener('focusin', onFocusIn, true);
        // remove listener after the guard window
        setTimeout(() => { try { document.removeEventListener('focusin', onFocusIn, true); } catch (e) { } }, 2500);
      } catch (e) { }
      // Ensure mobile keyboard is hidden if applicable
      if (isMobile) hideMobileKeyboard();
      // Enfocar y seleccionar el input de barcode para facilitar pruebas manuales
      try {
        setTimeout(() => {
          if (searchInputRef && searchInputRef.current) {
            // Evitar enfocar inmediatamente después de un escaneo (algunos navegadores fuerzan el teclado)
            if (!isMobile && !scanJustHappenedRef.current) {
              try { searchInputRef.current.focus(); } catch (e) { }
              try { if (typeof searchInputRef.current.select === 'function') searchInputRef.current.select(); } catch (e) { }
            } else {
              // Asegurar que no quede el foco en el input móvil.
              // Algunos navegadores móviles re-abren el teclado aun cuando hacemos blur;
              // estrategia más robusta: poner el input como readonly temporalmente, blurear,
              // y luego restaurar readOnly tras un pequeí±o delay.
              try {
                const inp = searchInputRef.current;
                if (inp) {
                  try { inp.readOnly = true; } catch (e) { }
                  try { if (typeof inp.blur === 'function') inp.blur(); } catch (e) { }
                  // doble-blur por si el primer blur no cierra el teclado en algunos Android
                  setTimeout(() => {
                    try { if (typeof inp.blur === 'function') inp.blur(); } catch (e) { }
                  }, 80);
                  // restaurar readOnly después de un breve periodo para permitir bíºsquedas manuales
                  setTimeout(() => {
                    try { inp.readOnly = false; } catch (e) { }
                  }, 700);
                }
              } catch (e) { }
            }
            // Additional robust blur: create a temporary inert element, focus then blur it to steal focus
            try {
              if (isMobile) {
                // Try multiple times to steal focus after camera stops; some browsers re-focus after a delay
                [120, 400, 900, 1400].forEach((t) => {
                  setTimeout(() => {
                    try {
                      const tmp = document.createElement('button');
                      tmp.style.position = 'absolute';
                      tmp.style.width = '1px';
                      tmp.style.height = '1px';
                      tmp.style.left = '-10000px';
                      tmp.setAttribute('aria-hidden', 'true');
                      tmp.tabIndex = -1;
                      document.body.appendChild(tmp);
                      try { tmp.focus(); } catch (e) { }
                      try { tmp.blur(); } catch (e) { }
                      document.body.removeChild(tmp);
                    } catch (e) { }
                  }, t);
                });
              }
            } catch (e) { }
          }
        }, 50);
      } catch (e) { }

      // Nota: el sonido de confirmación se reproducirá sólo después de
      // confirmar que el producto fue agregado (playSuccessBeep). Si no se
      // encuentra, reproduciremos un sonido de error (playErrorBeep) y no
      // agregaremos el product.

      // Normalizar barras y ceros: comparar varias formas
      const normalize = (s) => String(s || '').replace(/[^0-9a-zA-Z]/g, '').replace(/^0+/, '');
      const nCode = normalize(code);

      let found = products.find((p) => {
        try {
          const pb = normalize(p.barcode);
          if (!pb) return false;
          if (pb === nCode) return true;
          // match por _id también
          if (String(p._id) === code) return true;
        } catch (e) { return false; }
        return false;
      });

      // ⚖️ Lógica de Balanza (Scale Barcode Parser)
      // Si no se encontró producto exacto, intentar analizar como código de balanza EAN-13 (Prefijo 20)
      let scaleQty = null;
      if (!found && (code.length === 13 || code.length === 12) && code.startsWith('20')) {
        try {
          // Estructura Típica: 20 PPPPP VVVVV C
          // PPPPP: PLU del producto (5 dígitos).
          // VVVVV: Peso en gramos (5 dígitos). Ej: 02500 -> 2.500 kg

          const pluRaw = code.substring(2, 7);
          const weightRaw = code.substring(7, 12);

          // Buscar producto cuyo barcode coincida con el PLU (ej: '00010' o '10')
          const nPlu = normalize(pluRaw);
          const foundPlu = products.find((p) => normalize(p.barcode) === nPlu);

          if (foundPlu) {
            found = foundPlu; // Asignamos el producto encontrado por PLU

            const unitPrice = Number(found.salePrice || found.price || 0);

            if (unitPrice > 0) {
              const weightGrams = parseInt(weightRaw, 10);
              scaleQty = weightGrams / 1000; // 02500 -> 2.5 kg
              const totalPrice = Math.round(scaleQty * unitPrice * 100) / 100;

              console.debug(`[Scale] Detected PLU:${pluRaw} / Unit: $${unitPrice} * ${scaleQty}kg = $${totalPrice}`);
              toast.info(`⚖️ Balanza detectada: ${scaleQty} kg ($${totalPrice})`);
            } else {
              toast.error(`⚠️ El producto ${foundPlu.name} no tiene precio. Imposible calcular peso.`);
              found = null; // Revertir para que lance error general
            }
          } else {
            toast.error(`⚠️ Código de balanza detectado pero no existe PLU ${pluRaw}`);
          }
        } catch (err) {
          console.error('[Scale] Error parsing barcode', err);
        }
      }

      if (found) {
        // Clear safety timeout since we found a product
        clearTimeout(safetyTimeout);

        if (priceLookupOpen) {
          // If the price lookup modal is open, populate it instead of adding to cart
          try { setPriceLookupProduct(found); } catch (e) { }
          try { setPriceLookupOpen(true); } catch (e) { }
          toast.info(`Código detectado: ${code} â€” producto cargado en consulta: ${found.name}`);
          try { playSuccessBeep(); } catch (e) { }
          cleanupMobileScanState();
          return;
        }
        const added = addOrOpenCombo(found, scaleQty || null);
        if (added) {
          toast.success(`Código detectado: ${code} â€” agregado ${found.name}`);
          try { playSuccessBeep(); } catch (e) { }
          // clear mobile scanned indicator and restore input
          cleanupMobileScanState();
          // refocus main search input so next scan or manual search is ready (desktop only)
          try { if (!priceLookupOpen && !isMobile) { setTimeout(() => { try { if (searchInputRef && searchInputRef.current) { searchInputRef.current.focus(); if (typeof searchInputRef.current.select === 'function') searchInputRef.current.select(); } } catch (e) { } }, 80); } } catch (e) { }
        } else {
          try { playErrorBeep(); } catch (e) { }
          toast.error(`Código detectado: ${code}. No se pudo agregar el product.`);
          cleanupMobileScanState();
          try { if (!priceLookupOpen && !isMobile) { setTimeout(() => { try { if (searchInputRef && searchInputRef.current) { searchInputRef.current.focus(); } } catch (e) { } }, 80); } } catch (e) { }
        }
        return;
      }

      // Si no lo encontramos localmente, intentar preguntar al backend por el código (si hay sesión)
      (async () => {
        try {
          const res = await httpRequest.get(`${API_URL_PRODUCTS}/search-by-barcode`, { params: { code } });
          if (res && res.data) {
            // backend may return an object or an array
            let prod = res.data;
            if (Array.isArray(prod)) prod = prod.length > 0 ? prod[0] : null;
            // validate product shape
            if (prod && (prod._id || prod.barcode || prod.name)) {
              // Clear safety timeout since we found a product
              clearTimeout(safetyTimeout);

              if (priceLookupOpen) {
                try { setPriceLookupProduct(prod); } catch (e) { }
                try { setPriceLookupOpen(true); } catch (e) { }
                toast.info(`Código detectado: ${code} â€” producto cargado en consulta: ${prod.name}`);
                try { playSuccessBeep(); } catch (e) { }
                cleanupMobileScanState();
                return;
              }
              const added = addOrOpenCombo(prod);
              if (added) {
                toast.success(`Código detectado: ${code} â€” agregado ${prod.name}`);
                try { playSuccessBeep(); } catch (e) { }
                cleanupMobileScanState();
                return;
              }
            }
          }
        } catch (e) {
          // no panic: mostramos info al usuario
          console.debug('barcode backend lookup failed', e && e.response && e.response.data ? e.response.data : e.message);
        }

        // Product not found - clear safety timeout and clean up
        clearTimeout(safetyTimeout);

        try { playErrorBeep(); } catch (e) { }
        toast.error(`Código detectado: ${code}. No se encontró product.`);

        // Clean up mobile state after error
        cleanupMobileScanState();

        // If the price lookup modal is open, request it to refocus its hidden input so
        // the next scan can be received without manual interaction.
        try { if (priceLookupOpen && window && window.salesPriceLookupRefocus) window.salesPriceLookupRefocus(); } catch (e) { }
        // If modal is not open, clear the main search and refocus it on desktop so next scan can be entered
        try {
          if (!priceLookupOpen) {
            if (!isMobile) {
              try { setSearch(''); } catch (e) { }
              setTimeout(() => { try { if (searchInputRef && searchInputRef.current) { searchInputRef.current.focus(); } } catch (e) { } }, 80);
            } else {
              // on mobile keep behaviour of hiding keyboard and clearing visible search marker
              try { setSearch(''); } catch (e) { }
              try { hideMobileKeyboard(); } catch (e) { }
            }
          }
        } catch (e) { }
        if (isMobile) hideMobileKeyboard();
      })();
    } catch (err) {
      console.error('Error procesando código escaneado:', err);
      // Ensure cleanup even on unexpected errors
      try {
        setLastScannedCodeMobile('');
        restoreSearchInputForMobile(false);
      } catch (e) { }
    }
  };

  const handleFileImageInput = async (e) => {
    const file = e?.target?.files && e.target.files[0];
    if (!file) return;
    try {
      toast.info('Procesando imagen...');
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.src = url;
      await new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = (err) => reject(err);
      });
      const reader = new BrowserMultiFormatReader();
      try {
        const result = await reader.decodeFromImageElement(img);
        if (result) {
          handleScanned(result.getText());
        } else {
          toast.error('No se detectó un código en la imagen');
        }
      } catch (err) {
        console.error('decodeFromImageElement error', err);
        toast.error('No se pudo decodificar la imagen');
      }
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error procesando archivo de imagen', err);
      toast.error('Error cargando la imagen');
    } finally {
      try { e.target.value = ''; } catch (_) { }
    }
  };

  // â”€â”€ PRESUPUESTO â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleQuotation = () => {
    if (cart.length === 0) {
      toast.info('El carrito está vací­o');
      return;
    }
    setShowQuotationModal(true);
  };

  const handleSaveAndPrintQuotation = async () => {
    try {
      const items = cart.map((item) => {
        const basePrice = Number(item.unitPriceWithDiscount ?? item.price ?? 0);
        const original = Number(item.price ?? 0);
        const qty = item.quantity;

        return {
          productId: item.productId || item._id || undefined,
          descripcion: item.name,
          cantidad: qty,
          precioUnitario: basePrice, // Guardamos PRECIO BASE (Neto) en la DB
          originalPrice: original,
          alicuotaIVACode: item.alicuotaIVACode ?? item.alicuotaIVA ?? 5,
          subtotal: basePrice * qty // El backend lo usará para el b.subtotal si recalculase
        };
      });

      const payload = {
        customerName: selectedCustomer?.razonSocial || selectedCustomer?.name || 'Consumidor Final',
        customerId: selectedCustomer?._id || undefined,
        items,
        subtotal: subtotalAmt,
        discountTotal: saleDiscount,
        total: total, // Pasamos el total calculado por pantalla (IVA incluido si corresponde)
        notes: '',
        validityDays: 7
      };

      const res = await fetch('/api/quotations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error('Error guardando presupuesto');
      const data = await res.json();
      const quotation = data.quotation;
      toast.success(`Presupuesto ${quotation.referenceNumber} creado.`);
      setShowQuotationModal(false);

      // Normalizar items: el backend guarda 'descripcion' pero el template necesita 'name'
      // Usar el carrito local (que sí­ tiene el nombre) como fuente de verdad
      const itemsConNombre = items.map((it, idx) => {
        const qi = (quotation.items || [])[idx] || {};
        return {
          ...qi,
          name: it.descripcion || 'Item',
          quantity: it.cantidad,
          unitPriceWithDiscount: it.precioUnitario,
          price: it.originalPrice,
          alicuotaIVACode: it.alicuotaIVACode,
          subtotal: it.subtotal,
        };
      });
      const quotationConNombres = { ...quotation, items: itemsConNombre, saleNumber: quotation.referenceNumber, createdAt: quotation.createdAt || new Date().toISOString() };

      const storeAddress = (() => {
        if (fiscal?.domicilioFiscal) return fiscal.domicilioFiscal;
        if (fiscal?.domicilio) return fiscal.domicilio;
        return '';
      })();
      const storePhone = fiscal?.telefono || fiscal?.phone || '';
      const fallbackName = tenant?.businessName || user?.tenantId?.brandName || user?.tenantId?.name || 'Comprobante';
      const storeName = settingsLeftName || fiscal?.nombreFantasia || fiscal?.razonSocial || fallbackName;
      const logoData = settingsLogoData || fiscal?.logoUrl || null;

      if (quotationFormat === 'a4') {
        // â”€â”€ Generar HTML A4 en el FRONTEND (evita problemas de autenticación con window.open) â”€â”€
        const escHtml = s => s == null ? '' : String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const rowsHtml = itemsConNombre.map((it, i) => {
          const base = Number(it.unitPriceWithDiscount ?? it.precioUnitario ?? 0);
          const qty = Number(it.quantity ?? it.cantidad ?? 1);
          const ivaCode = it.alicuotaIVACode ?? it.alicuotaIVA ?? 5;
          const pct = getAlicuotaPercent(ivaCode) || 0;
          const precio = base * (1 + pct / 100); // Mostrar PRECIO FINAL al cliente en el PDF A4
          const sub = precio * qty;
          return `<tr>
            <td style="padding:8px 6px;border-bottom:1px solid #eee">${i + 1}</td>
            <td style="padding:8px 6px;border-bottom:1px solid #eee">${escHtml(it.name)}</td>
            <td style="padding:8px 6px;border-bottom:1px solid #eee;text-align:right">${qty}</td>
            <td style="padding:8px 6px;border-bottom:1px solid #eee;text-align:right">$${precio.toFixed(2)}</td>
            <td style="padding:8px 6px;border-bottom:1px solid #eee;text-align:right;font-weight:700">$${sub.toFixed(2)}</td>
          </tr>`;
        }).join('');

        const dateStr = new Date(quotation.createdAt || Date.now()).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const validStr = quotation.validityDate ? new Date(quotation.validityDate).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
        const logoHtml = logoData ? `<img src="${logoData}" style="max-height:70px;max-width:180px;object-fit:contain" alt="logo"/>` : '';

        const a4Html = `<!doctype html><html><head><meta charset="utf-8"/>
          <title>Presupuesto ${escHtml(quotation.referenceNumber)}</title>
          <style>
            @media print { @page { size: A4; margin: 15mm 20mm; } body { -webkit-print-color-adjust: exact; } }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; font-size: 13px; color: #222; background: #fff; padding: 30px; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; border-bottom: 3px solid #333; padding-bottom: 15px; }
            .store-info h1 { font-size: 24px; font-weight: 800; }
            .store-info p { font-size: 12px; color: #555; margin-top: 4px; }
            .doc-info { text-align: right; }
            .doc-title { font-size: 22px; font-weight: 800; color: #333; }
            .doc-ref { font-size: 13px; color: #666; margin-top: 4px; }
            .section { margin-bottom: 20px; }
            .section h3 { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #999; margin-bottom: 6px; }
            table { width: 100%; border-collapse: collapse; }
            thead th { background: #333; color: #fff; padding: 8px 6px; text-align: left; font-size: 12px; }
            thead th:last-child, thead th:nth-child(3), thead th:nth-child(4) { text-align: right; }
            .total-row { font-size: 16px; font-weight: 800; border-top: 2px solid #333; }
            .footer { margin-top: 40px; font-size: 11px; color: #888; text-align: center; }
            .badge { display: inline-block; background: #eee; border-radius: 4px; padding: 2px 8px; font-size: 11px; }
          </style>
        </head><body>
          <div class="header">
            <div class="store-info">
              ${logoHtml ? `<div style="margin-bottom:8px">${logoHtml}</div>` : ''}
              <h1>${escHtml(storeName)}</h1>
              ${storeAddress ? `<p>ðŸ“ ${escHtml(storeAddress)}</p>` : ''}
              ${storePhone ? `<p>ðŸ“ž ${escHtml(storePhone)}</p>` : ''}
            </div>
            <div class="doc-info">
              <div class="doc-title">PRESUPUESTO</div>
              <div class="doc-ref">${escHtml(quotation.referenceNumber)}</div>
              <div style="margin-top:8px;font-size:12px">Fecha: <b>${dateStr}</b></div>
              <div style="font-size:12px">Válido hasta: <b>${validStr}</b></div>
            </div>
          </div>

          <div class="section">
            <h3>Cliente</h3>
            <p>${escHtml(quotation.customerName || 'Consumidor Final')}</p>
          </div>

          <div class="section">
            <table>
              <thead><tr>
                <th style="width:40px">#</th>
                <th>Descripción</th>
                <th style="width:70px;text-align:right">Cant.</th>
                <th style="width:100px;text-align:right">Precio</th>
                <th style="width:110px;text-align:right">Subtotal</th>
              </tr></thead>
              <tbody>${rowsHtml}</tbody>
              <tfoot>
                <tr class="total-row">
                  <td colspan="4" style="padding:10px 6px;text-align:right">TOTAL</td>
                  <td style="padding:10px 6px;text-align:right">$${Number(quotation.total).toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p class="footer">Presupuesto sin valor fiscal · Válido por ${quotation.validityDays || 7} días · ${escHtml(storeName)}</p>
        </body></html>`;

        const newWin = window.open('', '_blank', 'toolbar=0,location=0,menubar=0,width=900,height=1100');
        if (newWin) {
          newWin.document.open();
          newWin.document.write(a4Html);
          newWin.document.close();
          newWin.onload = () => { setTimeout(() => { newWin.focus(); try { newWin.print(); } catch (_) { } setTimeout(() => { try { newWin.close(); } catch (_) { } }, 500); }, 250); };
        } else {
          toast.warning('Bloqueador de popups activo. Habilitá los popups para imprimir.');
        }

      } else {
        // ──────────────── Formato Ticket 80mm ────────────────
        const esc = s => s == null ? '' : String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const ticketHtml = buildNonFiscalTicketHtml(quotationConNombres, {
          products,
          emitterIsMonotributo,
          settingsLogoData: logoData,
          settingsLeftName: storeName,
          ticketHeader: storeAddress ? `${esc(storeAddress)}${storePhone ? ' Â· Tel: ' + storePhone : ''}` : (storePhone ? 'Tel: ' + storePhone : 'PRESUPUESTO - NO VíLIDO COMO FACTURA')
        });

        const newWin = window.open('', '_blank', 'toolbar=0,location=0,menubar=0,width=800,height=900');
        if (newWin) {
          try {
            newWin.document.open();
            newWin.document.write(ticketHtml);
            newWin.document.close();
            newWin.onload = () => { setTimeout(() => { newWin.focus(); try { newWin.print(); } catch (_) { } setTimeout(() => { try { newWin.close(); } catch (_) { } }, 500); }, 250); };
          } catch (errWin) { console.error('Error render ticket', errWin); }
        } else {
          toast.warning('Bloqueador de popups no permitió abrir el ticket.');
        }
      }

      // Limpia carrito al finalizar
      setCart([]);
      setDisabledComboIds([]);
      setSaleDiscount(0);

    } catch (err) {
      toast.error('No se pudo guardar el presupuesto: ' + err.message);
    }
  };

  // Obtiene los presupuestos pendientes
  const fetchQuotations = async () => {
    setLoadingQuotations(true);
    try {
      const res = await fetch('/api/quotation?status=draft', {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Error al obtener presupuestos');
      const data = await res.json();
      setPendingQuotations(data.items || []);
    } catch (e) {
      console.error(e);
      toast.error('Error cargando presupuestos');
    } finally {
      setLoadingQuotations(false);
    }
  };

  // Carga un presupuesto elegido al carrito
  const loadQuotationToCart = (q) => {
    if (q.items && q.items.length > 0) {
      // Intentar mapear con info real de products para el stock (si el backend lo devuelve, genial, sino aproximado)
      const newCart = q.items.map((it, idx) => {
        // Intentar encontrar el producto real por ID o por nombre como fallback
        const realProd = products.find(p => p._id === it.productId || p.name === it.descripcion) || { stock: 999 };
        return {
          ...it,
          productId: it.productId || realProd._id, // Asegurar que tenga un ID para la venta
          cartUid: Date.now() + '-' + idx,
          name: it.descripcion || 'Item',
          price: it.originalPrice || it.precioUnitario,
          unitPriceWithDiscount: it.precioUnitario,
          quantity: it.cantidad,
          stock: realProd.stock,
          subtotal: it.subtotal,
          alicuotaIVACode: it.alicuotaIVACode || 5
        };
      });
      // Opcionalmente recuperar el cliente
      if (q.customerId) {
        const custObj = customerSuggestions.find(c => c._id === q.customerId) || { _id: q.customerId, razonSocial: q.customerName };
        setSelectedCustomer(custObj);
      } else if (q.customerName && q.customerName !== 'Consumidor Final') {
        setSelectedCustomer({ razonSocial: q.customerName });
      }

      setCart([...cart, ...newCart]);
      setShowLoadQuotationModal(false);
      toast.success('Presupuesto cargado al carrito');
    } else {
      toast.error('El presupuesto no tiene í­tems');
    }
  };

  const handleOpenLoadQuotation = () => {
    setShowLoadQuotationModal(true);
    fetchQuotations();
  };
  // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€


  // NOTE: action buttons (Eliminar) were removed from the UI per mobile/UX update; keep function if needed later.

  // Vaciar carrito completo
  const handleClearCart = () => {
    if (cart.length === 0) {
      toast.info('â„¹ï¸ El carrito ya está vací­o');
      return;
    }
    if (window.confirm('¿Estás seguro de vaciar el carrito?')) {
      setCart([]);
      // Reset manual combo disables when cart cleared
      setDisabledComboIds([]);
      setSplitPayments([]);
      setSaleDiscount(0);
      toast.success('ðŸ—‘ï¸ Carrito vaciado');
    }
  };

  // ðŸ”¹ Abrir modal de pago
  const openPaymentModal = useCallback(() => {
    if (cart.length === 0) {
      toast.warning('âš ï¸ El carrito está vací­o');
      return;
    }
    setAmountPaid(total.toString());
    // Pre-rellenar con la nota por defecto si el usuario no escribió ninguna
    setSaleNote((prev) => prev || (typeof window !== 'undefined' ? localStorage.getItem('ticketDefaultNote') || '' : ''));
    setShowPaymentModal(true);
  }, [cart.length, total]);

  // Abrir el modal de scanner: inicializa/resume AudioContext en el gesto del usuario
  const openScanner = () => {
    setScannerOpen(true);
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
        if (audioCtxRef.current.state === 'suspended' && typeof audioCtxRef.current.resume === 'function') {
          audioCtxRef.current.resume().catch(() => { });
        }
      }
    } catch (e) {
      // ignore
    }
    // Kick off generation of a WAV fallback (non-blocking). We'll render a short sine and keep a blob URL.
  };

  // Pre-create an Audio element for the static asset to call play() synchronously in user gestures
  useEffect(() => {
    try {
      const a = new Audio('/assets/beep.wav');
      a.preload = 'auto';
      staticAudioRef.current = a;
      // Try a quick play test when the user first interacts to detect if autoplay blocked
      // We'll attempt to play silently (volume 0) and then restore; if it fails, fallback to embedded data URI
      const tryPlayTest = async () => {
        try {
          a.volume = 0;
          await a.play();
          try { a.pause(); } catch (e) { }
          a.volume = 1;
        } catch (e) {
          // fallback to embedded data URI
          try {
            const b = new Audio(EMBED_BEEP_DATAURI);
            b.preload = 'auto';
            staticAudioRef.current = b;
          } catch (err) {
            staticAudioRef.current = null;
          }
        }
      };
      // do not await here; let it run
      tryPlayTest().catch(() => { });
    } catch (e) {
      staticAudioRef.current = null;
    }
    // audioUnlockedRef remains false until user explicitly tests sound
    return () => {
      try { if (staticAudioRef.current) staticAudioRef.current.pause(); } catch (e) { }
      staticAudioRef.current = null;
    };
  }, []);

  const tryUnlockAudio = async () => {
    try {
      // Prefer the preloaded static audio
      if (staticAudioRef && staticAudioRef.current) {
        try {
          staticAudioRef.current.currentTime = 0;
          await staticAudioRef.current.play();
          audioUnlockedRef.current = true;
          toast.info('ðŸ”Š Sonido habilitado');
          return true;
        } catch (e) {
          // fall back to oscillator
        }
      }

      // fallback: quick oscillator
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
          const ctx = audioCtxRef.current;
          if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
            await ctx.resume().catch(() => { });
          }
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = 'sine'; o.frequency.value = 1000; g.gain.value = 0.12;
          o.connect(g); g.connect(ctx.destination);
          o.start();
          setTimeout(() => { try { o.stop(); } catch (e) { } }, 120);
          audioUnlockedRef.current = true;
          toast.info('ðŸ”Š Sonido habilitado (osc)');
          return true;
        }
      } catch (e) { }
    } catch (e) { }
    toast.warning('No se pudo habilitar audio automáticamente');
    return false;
  };

  // Usado por el botón "Tomar/Subir foto" para asegurar que el AudioContext
  // queda desbloqueado en el gesto del usuario y reproducir un beep inmediato
  const unlockAudioAndClickFile = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
        const ctx = audioCtxRef.current;
        if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
          ctx.resume().catch(() => { });
        }
        try {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = 'sine';
          o.frequency.value = 1000;
          g.gain.value = 0.12;
          o.connect(g);
          g.connect(ctx.destination);
          o.start();
          setTimeout(() => {
            try { o.stop(); } catch (e) { }
          }, 120);
        } catch (e) {
          // ignore
        }
      }
    } catch (e) {
      // ignore
    }
    try {
      // Try to play the preloaded static audio synchronously (best chance to succeed on mobile)
      try {
        if (staticAudioRef && staticAudioRef.current) {
          staticAudioRef.current.currentTime = 0;
          staticAudioRef.current.play().catch(() => { });
        }
      } catch (e) { }
      if (fileInputRef && fileInputRef.current) fileInputRef.current.click();
    } catch (e) { }
  };

  // Manejar logout desde la UI (botón móvil)
  const handleLogout = () => {
    try {
      dispatch(logout());
    } catch (e) {
      // ignore
    }
    try {
      dispatch(reset());
    } catch (e) { }
    try {
      navigate('/login');
    } catch (e) { }
  };




  // ðŸ”¹ Atajo: ir a la UI de Facturas
  const goToInvoices = () => {
    navigate('/invoices');
  };

  // --- Printing helpers moved above sale finalization to avoid TDZ / lints ---
  const handlePrint = useReactToPrint({
    content: () => ticketRef.current,
    // Evitar 'undefined' en el nombre de archivo: usar timestamp si no hay saleNumber
    documentTitle: `Ticket-${lastSale?.saleNumber ?? Date.now()}`,
    onAfterPrint: () => {
      try {
        if (!isMobile) {
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 150);
        }
      } catch (e) { }
    },
    onBeforeGetContent: async () => {
      try {
        if (!fiscalStatus || !fiscalStatus.cuit) {
          try {
            await dispatch(getFiscalStatus()).unwrap();
            setPrintReadyToggle((p) => !p);
            await new Promise((r) => setTimeout(r, 350));
          } catch (fsErr) {
            console.warn('No se pudo cargar fiscalStatus antes de imprimir:', fsErr);
          }
        }
        try {
          const fresh = await fiscalService.getFiscalStatus();
          if (fresh) setLocalFiscal(fresh);
        } catch (fsErr) {
          try { await dispatch(getFiscalStatus()).unwrap(); setLocalFiscal((prev) => prev || null); } catch (dErr) { }
        }
        if (lastSaleMode !== 'no-fiscal' && lastInvoice && !lastInvoice.qrData && lastInvoice.cae && fiscal?.cuit) {
          try {
            const maybeQr = await generateQrIfNeeded(lastInvoice);
            if (maybeQr) setLastInvoice((prev) => ({ ...(prev || {}), qrData: maybeQr }));
          } catch (err) { console.error('Error generating QR before print (helper):', err); }
        }
      } catch (err) {
        console.error('Error generating QR before print:', err);
      }

      try { debugLog('Printing - fiscal ticket?', lastSaleMode !== 'no-fiscal'); } catch (e) { }

      if (lastSaleMode !== 'no-fiscal') {
        await waitForQRImage(ticketRef, lastInvoice?.qrData);
        await waitForImages(ticketRef);
      }
    },
  });

  const printInvoiceInNewWindow = useCallback(async () => {
    try {
      if (!lastInvoice || !invoiceRef.current) return;
      let htmlContent = invoiceRef.current.innerHTML;
      if (!lastInvoice.qrData && lastInvoice.cae && fiscal?.cuit) {
        try {
          const maybeQr = await generateQrIfNeeded(lastInvoice);
          if (maybeQr) {
            setLastInvoice((prev) => ({ ...(prev || {}), qrData: maybeQr }));
            if (!htmlContent.includes('alt="QR AFIP"')) {
              const qrBlock = `<div style="margin:6px auto;width:130px;height:130px;display:flex;align-items:center;justify-content:center"><img src="${maybeQr}" alt="QR AFIP" style="width:100%;height:100%"/></div>`;
              htmlContent += qrBlock;
            }
          }
        } catch (e) {
          console.error('Failed to generate QR for new-window print (helper):', e);
        }
      }

      let newWin = printWindowRef.current;
      try {
        if (!newWin || newWin.closed) newWin = window.open('', '_blank', 'toolbar=0,location=0,menubar=0,width=900,height=1100');
      } catch (e) {
        try { newWin = window.open('', '_blank', 'toolbar=0,location=0,menubar=0,width=900,height=1100'); } catch (err) { newWin = null; }
      }
      if (!newWin) { console.error('No se pudo abrir nueva ventana para imprimir'); return; }

      const style = `
            <style>
              @media print { @page { size: A4; margin: 12mm; } }
              html,body { margin: 0; padding: 0; }
              body { font-family: Arial, sans-serif; color: #000; font-size: 12px; }
              img { max-width: 100%; }
              .no-break { page-break-inside: avoid; }
            </style>
      `;

      newWin.document.open();
      newWin.document.write(`<!doctype html><html><head><meta charset="utf-8">${style}</head><body>${htmlContent}</body></html>`);
      newWin.document.close();

      const waitForImagesInWindow = (w, timeout = 4000) => new Promise((resolve) => {
        try {
          const imgs = Array.from(w.document.images || []);
          if (imgs.length === 0) return resolve(true);
          let remaining = imgs.length;
          const onDone = () => { remaining -= 1; if (remaining === 0) resolve(true); };
          imgs.forEach((img) => {
            if (img.complete && img.naturalWidth !== 0) { remaining -= 1; if (remaining === 0) resolve(true); return; }
            img.addEventListener('load', onDone); img.addEventListener('error', onDone);
          });
          setTimeout(() => resolve(true), timeout);
        } catch (e) { resolve(true); }
      });

      await waitForImagesInWindow(newWin, 4500);
      newWin.focus(); newWin.print();
      setTimeout(() => {
        try { newWin.close(); } catch (e) { }
        try {
          if (!isMobile) {
            setTimeout(() => { searchInputRef.current?.focus(); }, 150);
          }
        } catch (f) { }
      }, 1000);
      try { if (printWindowRef.current === newWin) printWindowRef.current = null; } catch (e) { }
    } catch (e) { console.error('Error imprimiendo en nueva ventana:', e); }
  }, [lastInvoice, fiscal, generateQrIfNeeded, setLastInvoice, isMobile]);


  // ðŸ”¹ Finalizar venta con pago
  const handleFinishSale = useCallback(async (modeParam, mpPaymentDataOverride = null) => {
    // Prevent duplicate submissions
    if (isProcessingPayment) return;
    setIsProcessingPayment(true);
    let returnedSale = null;
    let finalSubtotal = 0;
    let finalIvaAmt = 0;
    let finalIvaLabel = 'Varios';

    // Validación método de pago efectivo
    if (paymentMethod === 'Efectivo' && amountPaid) {
      if (parseFloat(amountPaid) < total && parseFloat(amountPaid) > 0) {
        const missing = (total - parseFloat(amountPaid)).toFixed(2);
        toast.error(`âŒ Monto insuficiente. Faltan $${missing}`);
        setIsProcessingPayment(false);
        return;
      }
    }

    const saleData = {
      items: cart.map((p) => {
        const originalPrice = Number(p.price ?? p.unitPrice ?? p.salePrice ?? 0);
        const finalPrice = Number(p.unitPriceWithDiscount ?? originalPrice);
        const quantity = Number(p.quantity ?? 1);
        const discountAmount = Number((originalPrice - finalPrice).toFixed(2));
        const discountPercent = originalPrice > 0 ? Number(((discountAmount / originalPrice) * 100).toFixed(2)) : 0;
        const subtotal = Number((finalPrice * quantity).toFixed(2));
        return {
          productId: p.productId,
          variantId: p.variantId, // Incluir ID de variante
          variantName: p.variantName, // Incluir nombre de variante
          name: p.name,
          quantity,
          unitPrice: originalPrice, // base price
          originalPrice,
          discountAmount,
          discountPercent,
          discountType: p.discountType || 'none',
          discountValue: Number(p.discountValue || 0),
          discountTiers: Array.isArray(p.discountTiers) ? p.discountTiers : [],
          unitPriceWithDiscount: finalPrice,
          subtotal,
          // Propagar alí­cuota al backend en caso de que la venta se guarde y el producto cambie
          alicuotaIVACode: p.alicuotaIVACode ?? p.alicuotaIVA ?? 5,
        };
      }),
      total: parseFloat(total),
      date: new Date().toISOString(),
      paymentMethod,
      amountPaid: paymentMethod === 'Múltiple' ? parseFloat(total) : (parseFloat(amountPaid) || total),
      payments: paymentMethod === 'Múltiple' ? splitPayments : [], // Send split payments logic
      change: paymentMethod === 'Múltiple' ? 0 : change, // No change logic for split initially
      customer: selectedCustomer?._id || null,
      customerName: selectedCustomer?.razonSocial || selectedCustomer?.name || 'Consumidor Final',
      saleDiscount: Number(saleDiscount || 0),
      notes: saleNote ? saleNote.trim() : '',
      // Add financing details if credit card
      installments: paymentMethod === 'Tarjeta' ? installments : 1,
      interestRate: paymentMethod === 'Tarjeta' ? interestRate : 0,
      // Pass Mercado Pago transaction data if available
      mpPaymentData: mpPaymentDataOverride || mpPaymentData || null
    };

    // Attach a snapshot of the ticket HTML so reimpresiones desde Historial sean idénticas
    try {
      const logoData = typeof window !== 'undefined' ? localStorage.getItem('ticketLogoData') : null;
      const leftName = typeof window !== 'undefined' ? localStorage.getItem('ticketLeftName') : '';
      const fallbackName = tenant?.businessName || user?.tenantId?.brandName || user?.tenantId?.name || 'Comprobante';
      // Build ticket HTML from the same template used for Historial
      console.log('🎫 Generando ticket con customerName:', saleData.customerName, 'selectedCustomer:', selectedCustomer?.razonSocial);
      const ticketHtml = buildNonFiscalTicketHtml({ ...saleData, createdAt: new Date().toISOString() }, { products, settingsLogoData: logoData, settingsLeftName: leftName || fallbackName, ticketHeader: process.env.REACT_APP_TICKET_HEADER || '', emitterIsMonotributo });
      saleData.ticketHtml = ticketHtml;
    } catch (e) {
      // If ticket snapshot fails, continue without blocking the sale
      console.warn('No se pudo generar ticketHtml para snapshot:', e);
    }

    try { debugLog('Venta a enviar:', saleData); } catch (e) { }

    // No abrimos ventanas automáticamente desde el flujo de venta.
    // Dejamos `printWindowRef.current` en null y delegamos cualquier
    // apertura manual/desde el helper dev para evitar popups indeseados.
    // Bloquear el modal para evitar re-envíos simultáneos
    setIsProcessingPayment(true);
    try { printWindowRef.current = null; } catch (e) { printWindowRef.current = null; }

    let res;
    let isOfflineSuccess = false;

    try {
      res = await httpRequest.post(`${API_URL_SALES}/new`, saleData);
      // Venta registrada silenciosamente sin popups
    } catch (err) {
      if (!navigator.onLine || (err.message && /Network Error|Failed to fetch/i.test(err.message)) || (err.code === 'ERR_NETWORK')) {
        saveOfflineSale({ ...saleData, puntoVenta });
        toast.warning('📡 Sin conexión. Venta guardada localmente.');
        res = { data: { sale: { ...saleData, _id: `offline-${Date.now()}` } } };
        isOfflineSuccess = true;
      } else {
        console.error('Error creating sale:', err);
        toast.error('❌ Error registrando venta: ' + (err.response?.data?.message || err.message));
        setIsProcessingPayment(false);
        return;
      }
    }

    returnedSale = res?.data?.sale || (res?.data?.invoice?.sale) || null;
    if (returnedSale) {
      finalSubtotal = returnedSale.subtotal || 0;
      finalIvaAmt = returnedSale.importeIVA || 0;
      // Note: ivaLabel might not be in the model, we can compute it if needed or use a default
      finalIvaLabel = returnedSale.ivaLabel || '21%';
    } else {
      // Fallback calculation from cart
      finalSubtotal = cart.reduce((acc, item) => acc + (item.unitPriceWithDiscount * item.quantity), 0);
      finalIvaAmt = 0;
      finalIvaLabel = 'Sin IVA';
    }

    try {
      // SIEMPRE generar factura fiscal de AFIP
      // Determinar modo (parametro o estado)
      const mode = modeParam || invoiceMode;
      // Guardar el modo de la última venta para render condicional en ticket
      setLastSaleMode(mode);

      // Si es 'no-fiscal' NO generamos factura AFIP ni mostramos CAE/QR
      if (mode === 'no-fiscal' || isOfflineSuccess) {
        if (isOfflineSuccess && mode !== 'no-fiscal') toast.info('ℹ️ Facturación AFIP pospuesta (Offline)');
        const dummyInvoice = {
          ...saleData,
          fecha: new Date().toISOString(),
          importeTotal: saleData.total,
          // Para no-fiscal, ignoramos el interés visualmente según requerimiento
          installments: 1,
          interestRate: 0,
        };
        // NO calculamos interés agregado para el total en no-fiscal

        setLastInvoice(dummyInvoice);
      } else {
        try {
          // Build cliente object with robust fallbacks
          const cliente = {
            // Determine doc type: prefer CUIT if present, else DNI, else fallback
            tipoDocumento: (selectedCustomer?.cuit || selectedCustomer?.tipo === 'CUIT') ? 'CUIT' :
              (selectedCustomer?.dni || selectedCustomer?.tipo === 'DNI') ? 'DNI' :
                (selectedCustomer?.tipoDocumento === 80 ? 'CUIT' : (selectedCustomer?.tipoDocumento === 96 ? 'DNI' : 'Otro')),
            // Determine doc number: check all common fields
            numeroDocumento: selectedCustomer?.cuit || selectedCustomer?.dni || selectedCustomer?.numero || selectedCustomer?.numeroDocumento || '0',
            // Determine name: check razonSocial, name, nombre
            razonSocial: selectedCustomer?.razonSocial || selectedCustomer?.name || selectedCustomer?.nombre || 'Consumidor Final',
            // Determine tax condition: check tipoContribuyente, condicionIVA
            condicionIVA: (() => {
              const val = selectedCustomer?.tipoContribuyente || selectedCustomer?.condicionIVA;
              if (!val) return 5;
              // If already number, return it
              if (typeof val === 'number') return val;
              // If string number "1", return number
              if (!isNaN(Number(val)) && Number(val) > 0) return Number(val);

              // Map string description to code (case insensitive/loose)
              const s = String(val).toLowerCase().trim();
              if (s.includes('inscripto') && !s.includes('no inscripto')) return 1;
              if (s.includes('monotributo')) return 6;
              if (s.includes('exento')) return 4;
              if (s.includes('consumidor')) return 5;
              if (s.includes('no responsable')) return 3;
              if (s.includes('no inscripto')) return 2;

              return 5;
            })(),
          };

          const payload = {
            saleId: res.data.sale?._id,
            puntoVenta: puntoVenta,
            // No enviar tipoComprobante: la selección la realiza el servidor según la configuración fiscal del emisor.
            cliente,
          };

          try { debugLog('Generando factura AFIP con payload:', payload); } catch (e) { }

          const invRes = await dispatch(generateInvoiceFromSale(payload)).unwrap();
          if (invRes?.invoice) {
            const invoiceObj = { ...invRes.invoice };
            // Inject financing details from saleData into the invoice object for printing
            const inst = saleData.installments || 1;
            const rate = saleData.interestRate || 0;
            invoiceObj.installments = inst;
            invoiceObj.interestRate = rate;

            // If there's financing interest, update the total for the printed ticket (visual only)
            if (inst > 1 && rate > 0) {
              const baseTotal = Number(invoiceObj.total || invoiceObj.importeTotal || 0);
              const totalWithInterest = baseTotal * (1 + (rate / 100) * inst);
              invoiceObj.total = totalWithInterest;
              invoiceObj.importeTotal = totalWithInterest;
            }

            // Inject Mercado Pago payment details into the invoice object for printing
            invoiceObj.mpPaymentData = mpPaymentData || (returnedSale?.mpPaymentData || (invRes?.invoice?.mpPaymentData)) || null;

            // Ensure cliente fields are present for printing (merge with selectedCustomer when backend omits data)
            try {
              const sel = selectedCustomer || {};
              const existingCliente = invoiceObj.cliente || {};
              const condCode = existingCliente.condicionIVA || sel.tipoContribuyente || existingCliente.condicion;
              // Create a merged copy instead of mutating potentially read-only objects
              invoiceObj.cliente = {
                ...existingCliente,
                razonSocial: existingCliente.razonSocial || sel.razonSocial || sel.name || 'Consumidor Final',
                tipo: existingCliente.tipo || existingCliente.tipoDocumento || (sel.cuit ? 'CUIT' : (sel.dni ? 'DNI' : existingCliente.tipo)),
                numero: existingCliente.numero || existingCliente.numeroDocumento || sel.cuit || sel.dni || existingCliente.numero || '0',
                domicilio: existingCliente.domicilio || sel.domicilio || sel.direccion || (sel.domicilioFiscal ? `${sel.domicilioFiscal.calle || ''} ${sel.domicilioFiscal.numero || ''}`.trim() : ''),
                condicionIVADescripcion: existingCliente.condicionIVADescripcion || (condCode ? (tipoDescripcionMap[Number(condCode)] || String(condCode)) : existingCliente.condicionIVADescripcion || 'Consumidor Final')
              };
            } catch (e) {
              // don't block printing on merge errors
              console.warn('Warning merging cliente data for print:', e);
            }
            // Refresh pending list/count after attempting to generate invoice
            try { fetchPending(); } catch (e) { /* ignore */ }
            // Si el backend no devolvió qrData, generarlo en el cliente para la vista/imprenta (usa helper con cache)
            try {
              const maybeQr = await generateQrIfNeeded(invoiceObj);
              if (maybeQr) invoiceObj.qrData = maybeQr;
            } catch (e) {
              console.error('Error generando QR en frontend (helper):', e);
            }

            // Ya no dependemos de pdfUrl persistido; el PDF se genera on-demand

            setLastInvoice(invoiceObj);
            // Si el backend nos devolvió datos del emisor (emitter), guardarlos localmente para impresión inmediata
            try {
              const emitterFromResp = invRes?.emitter || invRes?.invoice?.emitter || invoiceObj?.emitter;
              if (emitterFromResp) setLocalFiscal(emitterFromResp);
            } catch (e) {
              // ignore
            }

            // Si el usuario activó "auto-print" en settings, lanzar la impresión ahora
            try {
              const autoRaw = localStorage.getItem('printAutoMode');
              const auto = autoRaw === null ? true : autoRaw === 'true';
              const method = localStorage.getItem('printDefaultMethod') || 'ticket';
              if (auto) {
                // Para método PDF, abrir endpoint de descarga/visualización on-demand
                if (method === 'pdf') {
                  const invoiceId = invoiceObj._id || invoiceObj.id;
                  if (invoiceId) {
                    try {
                      const resp = await httpRequest.get(`/invoices/${invoiceId}/pdf`, {
                        responseType: 'blob',
                        params: { inline: true }
                      });
                      const blob = new Blob([resp.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      try {
                        if (printWindowRef.current && !printWindowRef.current.closed) {
                          // Reutilizar la ventana abierta en el gesto del usuario
                          printWindowRef.current.location.href = url;
                        } else {
                          window.open(url, '_blank');
                        }
                      } catch (e) {
                        // fallback
                        try { window.open(url, '_blank'); } catch (err) { console.error('Error abriendo PDF en ventana:', err); }
                      }
                      setTimeout(() => URL.revokeObjectURL(url), 60_000);
                    } catch (err) {
                      console.error('Error descargando PDF on-demand:', err);
                      // Fallback a ticket
                      setLastInvoice(invoiceObj);
                      setTimeout(() => handlePrint(), 200);
                    }
                  } else {
                    // Fallback a ticket si no obtenemos id por algún motivo
                    setLastInvoice(invoiceObj);
                    setTimeout(() => handlePrint(), 200);
                  }
                } else if (method === 'a4') {
                  // Para A4 usamos la función que abre nueva ventana e imprime
                  // Aseguramos que invoiceObj esté en estado antes de imprimir
                  setLastInvoice(invoiceObj);
                  if (invoiceObj.qrData) {
                    // QR ya presente, imprimir
                    setTimeout(() => printInvoiceInNewWindow(), 150);
                  } else {
                    // Si no hay QR, generar en cliente y luego imprimir
                    (async () => {
                      try {
                        if (invoiceObj.cae && fiscal?.cuit) {
                          const qrPayload = buildQrPayload(invoiceObj, fiscalStatus?.cuit || fiscal?.cuit);
                          const json = JSON.stringify(qrPayload);
                          const base64 = window.btoa(unescape(encodeURIComponent(json)));
                          const qrUrl = `https://www.afip.gob.ar/fe/qr/?p=${base64}`;
                          const qrDataUrl = await QRCode.toDataURL(qrUrl, { errorCorrectionLevel: 'M', margin: 1, width: 200 });
                          setLastInvoice((prev) => ({ ...(prev || {}), qrData: qrDataUrl }));
                          setTimeout(() => printInvoiceInNewWindow(), 150);
                        } else {
                          // No hay CAE/QR, igual intentar imprimir A4 (backend may still have pdf)
                          setTimeout(() => printInvoiceInNewWindow(), 150);
                        }
                      } catch (e) {
                        console.error('Error generando QR antes de auto-print A4:', e);
                        setTimeout(() => printInvoiceInNewWindow(), 150);
                      }
                    })();
                  }
                } else if (method === 'ticket') {
                  // Imprimir ticket (incluso para ventas fiscales)
                  // Aseguramos que el estado lastInvoice esté actualizado
                  setLastInvoice(invoiceObj);
                  setTimeout(() => handlePrint(), 150);
                }
              }
            } catch (e) {
              console.error('Error al intentar auto-print tras generar factura:', e);
            }
          } else {
            setLastInvoice(null);
          }
        } catch (invErr) {
          console.error('❌ Error al generar factura AFIP:', invErr);
          // Try to surface server response if present (validation error for override etc.)
          const serverMsg = invErr?.response?.data?.message || invErr?.message || (typeof invErr === 'string' ? invErr : null);
          if (serverMsg) {
            toast.error(`⚠️ ${serverMsg}`);
          } else {
            toast.error('⚠️ Venta registrada pero fallo la facturación AFIP');
          }
          setLastInvoice(null);
          // Continuar sin bloquear la venta
        }
      }

      // Guardar última venta para imprimir (incluir subtotales e IVA calculados antes de limpiar carrito)
      // Use backend-provided numeric saleNumber if available, otherwise fallback to id/timestamp
      // const returnedSale already defined above
      const backendSaleNumber = returnedSale && (returnedSale.saleNumber !== undefined && returnedSale.saleNumber !== null) ? returnedSale.saleNumber : null;
      const fallbackNumber = returnedSale && returnedSale._id ? returnedSale._id : Date.now();
      setLastSale({
        ...saleData,
        saleNumber: backendSaleNumber !== null ? backendSaleNumber : fallbackNumber,
        date: new Date(),
        customerName: selectedCustomer?.razonSocial || 'Consumidor Final',
        customerPhone: selectedCustomer?.phone,
        subtotal: finalSubtotal,
        importeIVA: finalIvaAmt,
        ivaLabel: finalIvaLabel,
        mpPaymentData: mpPaymentDataOverride || mpPaymentData || (returnedSale?.mpPaymentData) || null
      });
      setWhatsappPhone(selectedCustomer?.phone || '');
      setShowPaymentModal(false);
      // Solo mostrar modal de éxito en móvil para compartir WhatsApp.
      // En escritorio no es necesario bloquear la pantalla con un modal extra.
      if (isMobile) {
        setShowSuccessModal(true);
      }

      // Si la venta es NO-FISCAL y el usuario activó auto-print, imprimir directamente
      try {
        const autoRaw = localStorage.getItem('printAutoMode');
        const auto = autoRaw === null ? true : autoRaw === 'true';
        const method = localStorage.getItem('printDefaultMethod') || 'ticket';

        console.log('=== AUTO-PRINT DEBUG ===');
        console.log('localStorage.printAutoMode:', autoRaw);
        console.log('Auto (parsed):', auto);
        console.log('Print method:', method);
        console.log('Mode:', mode);
        console.log('Will print?:', auto && (mode === 'no-fiscal' || mode === 'presupuesto'));

        if (auto && (mode === 'no-fiscal' || mode === 'presupuesto')) {
          console.log('✅ Triggering auto-print...');

          setTimeout(() => {
            try {
              if (method === 'ticket') {
                // Usar buildNonFiscalTicketHtml directamente con saleData para garantizar
                // que la nota este incluida sin depender del re-render de ticketRef
                const logoData = typeof window !== 'undefined' ? localStorage.getItem('ticketLogoData') : null;
                const leftName = typeof window !== 'undefined' ? localStorage.getItem('ticketLeftName') : '';
                const fallbackName = tenant?.businessName || user?.tenantId?.brandName || user?.tenantId?.name || 'Comprobante';
                const fullTicketHtml = buildNonFiscalTicketHtml(
                  { ...saleData, createdAt: new Date().toISOString(), saleNumber: backendSaleNumber !== null ? backendSaleNumber : fallbackNumber },
                  { products, settingsLogoData: logoData, settingsLeftName: leftName || fallbackName, ticketHeader: process.env.REACT_APP_TICKET_HEADER || '', emitterIsMonotributo }
                );
                const printWin = window.open('', '_blank', 'toolbar=0,location=0,menubar=0,width=800,height=900');
                if (printWin) {
                  printWin.document.open();
                  printWin.document.write(fullTicketHtml);
                  printWin.document.close();
                  
                  // Función para esperar imágenes y luego imprimir (mismo estilo que fiscal)
                  const doPrint = () => {
                    setTimeout(() => {
                      try {
                        printWin.focus();
                        printWin.print();
                        // Cerrar ventana después de imprimir
                        setTimeout(() => { 
                          try { printWin.close(); } catch (_) { }
                          if (!isMobile) setTimeout(() => searchInputRef.current?.focus(), 150);
                        }, 500);
                      } catch (e) {
                        console.error('Error printing ticket:', e);
                        try { printWin.close(); } catch (_) { }
                      }
                    }, 250);
                  };
                  
                  if (printWin.document.readyState === 'complete') {
                    doPrint();
                  } else {
                    printWin.onload = doPrint;
                  }
                } else {
                  toast.warning('Bloqueador de popups activo. Habilita los popups para imprimir.');
                }
              } else if (method === 'a4' && invoiceRef.current) {
                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.right = '0';
                iframe.style.bottom = '0';
                iframe.style.width = '0';
                iframe.style.height = '0';
                iframe.style.border = 'none';
                document.body.appendChild(iframe);

                const invoiceHTML = invoiceRef.current.innerHTML;
                const iframeDoc = iframe.contentWindow.document;
                iframeDoc.open();
                iframeDoc.write(`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <title>Factura A4</title>
                    <style>
                      @media print {
                        @page { margin: 1cm; size: A4; }
                        body { margin: 0; padding: 0; }
                      }
                      body { 
                        font-family: Arial, sans-serif; 
                        margin: 20px;
                        max-width: 210mm;
                      }
                    </style>
                  </head>
                  <body>
                    ${invoiceHTML}
                  </body>
                  </html>
                `);
                iframeDoc.close();

                iframe.onload = function () {
                  setTimeout(() => {
                    try {
                      iframe.contentWindow.focus();
                      iframe.contentWindow.print();
                      setTimeout(() => {
                        try { document.body.removeChild(iframe); } catch (err) { }
                        try {
                          if (!isMobile) {
                            setTimeout(() => { searchInputRef.current?.focus(); }, 150);
                          }
                        } catch (f) { }
                      }, 1000);
                    } catch (e) {
                      console.error('Error printing A4 from iframe:', e);
                      document.body.removeChild(iframe);
                    }
                  }, 100);
                };
              } else if (method === 'pdf') {
                // PDF method - just show a message (PDF generation would need backend endpoint)
                toast.info('Formato PDF seleccionado. Usa el botón de compartir para generar PDF.');
              } else {
                console.error('âŒ Método de impresión no válido o ref no existe');
              }
            } catch (e) {
              console.error('Error al imprimir ticket no-fiscal automáticamente:', e);
              toast.error('Error al abrir ventana de impresión');
            }
          }, 500);
        } else {
          console.log('âŒ Auto-print NOT triggered');
        }
      } catch (e) {
        console.error('Error en bloque auto-print:', e);
      }

      // NOTE: auto-print is now handled when invoice generation returns (event-based).

      // Limpiar pantalla
      setCart([]);
      setDisabledComboIds([]);
      setSplitPayments([]);
      setSaleDiscount(0);
      setSaleNote('');
      setTotal(0);
      setSearch('');
      setSuggestions([]);
      setPaymentMethod('Efectivo');
      setAmountPaid('');
      setChange(0);
      clearCustomer();
      setMpPaymentData(null); // Clear payment data after use

      // Refrescar productos para actualizar stock
      fetchProducts();

      // En escritorio, asegurar que el foco vuelva al buscador para la siguiente venta
      if (!isMobile) {
        setTimeout(() => {
          try {
            searchInputRef.current?.focus();
            if (typeof searchInputRef.current?.select === 'function') {
              searchInputRef.current?.select();
            }
          } catch (f) { }
        }, 350);
      }
    } catch (err) {
      console.error(err.response?.data || err.message);
      toast.error('âŒ Error al registrar la venta');
      // Ensure payment modal is closed so user can continue
      setShowPaymentModal(false);
    } finally {
      setIsProcessingPayment(false);
    }
  }, [
    cart,
    products,
    total,
    paymentMethod,
    mpPaymentData,
    amountPaid,
    change,
    selectedCustomer,
    puntoVenta,
    invoiceMode,
    dispatch,
    generateQrIfNeeded,
    fetchPending,
    fetchProducts,
    fiscalStatus,
    fiscal,
    isProcessingPayment,
    setIsProcessingPayment,
    saleNote,

    handlePrint,
    printInvoiceInNewWindow,
    installments,
    interestRate,
    isMobile,
    debugLog,
    buildQrPayload,
    saleDiscount,
    splitPayments,
    emitterIsMonotributo
  ]);

  // Helper: wait until all images inside a ref are loaded (useful for data-url QR images)
  const waitForImages = (ref) => {
    return new Promise((resolve) => {
      try {
        const root = ref.current;
        if (!root) return resolve();
        const imgs = Array.from(root.querySelectorAll('img'));
        if (imgs.length === 0) return resolve();
        let remaining = imgs.length;
        imgs.forEach((img) => {
          if (img.complete && img.naturalWidth !== 0) {
            remaining -= 1;
            if (remaining === 0) resolve();
            return;
          }
          const onDone = () => {
            img.removeEventListener('load', onDone);
            img.removeEventListener('error', onDone);
            remaining -= 1;
            if (remaining === 0) resolve();
          };
          img.addEventListener('load', onDone);
          img.addEventListener('error', onDone);
        });
        // Safety timeout in case something never fires
        setTimeout(() => resolve(), 2000);
      } catch (e) {
        resolve();
      }
    });
  };

  // Helper: wait until an img element with alt 'QR AFIP' exists inside a ref
  // If it doesn't appear within the timeout, optionally inject one using qrData
  const waitForQRImage = async (ref, qrData, timeout = 2500) => {
    const start = Date.now();
    const check = () => {
      try {
        if (!ref.current) return false;
        // Try to find the QR <img> by alt attribute (ticket uses alt 'QR AFIP')
        const img = ref.current.querySelector('img[alt="QR AFIP"]');
        if (img) return true;
        return false;
      } catch (e) {
        return false;
      }
    };

    while (Date.now() - start < timeout) {
      if (check()) return true;
      // wait a bit
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 50));
    }

    // If we reach here, the <img> did not appear in time. Inject one if qrData provided.
    try {
      if (ref.current && qrData) {
        const existing = ref.current.querySelector('img[alt="QR AFIP"]');
        if (!existing) {
          const container = document.createElement('div');
          container.style.margin = '6px auto';
          container.style.width = '130px';
          container.style.height = '130px';
          container.style.display = 'flex';
          container.style.alignItems = 'center';
          container.style.justifyContent = 'center';
          const img = document.createElement('img');
          img.alt = 'QR AFIP';
          img.src = qrData;
          img.style.width = '100%';
          img.style.height = '100%';
          container.appendChild(img);
          // append near the end; safe fallback for printing
          ref.current.appendChild(container);
          // wait a tiny bit for image to start loading
          await new Promise((r) => setTimeout(r, 50));
          return true;
        }
      }
    } catch (e) {
      // ignore injection errors
    }

    return false;
  };

  // Configurar impresión
  // Emisor IIBB (normalizar a string siempre)
  const emitterIIBB = (() => {
    try {
      const v = fiscal?.ingresosBrutos;
      if (!v) return String(fiscal?.cuit || '');
      if (typeof v === 'object') {
        return String(v.numero || v.padron || v.id || '');
      }
      return String(v);
    } catch {
      return String(fiscal?.cuit || '');
    }
  })();

  // Fuente de cliente para mostrar en ticket: preferir la factura (lastInvoice) y si no existe usar el cliente seleccionado
  const clienteForTicket = (lastInvoice && lastInvoice.cliente) ? lastInvoice.cliente : (selectedCustomer || null);





  const shareInvoicePdf = async () => {
    if (!lastInvoice || !lastInvoice._id) return;

    // Determine phone
    let phone = whatsappPhone || (lastSale?.customerPhone || '');
    phone = phone.replace(/[^0-9]/g, '');

    try {
      setLoadingPdf(true);
      // Solicitamos formato ticket80 explí­citamente para el PDF compartido por celular
      const res = await httpRequest.get(`/invoices/${lastInvoice._id}/pdf?format=ticket80&t=${Date.now()}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });

      // 1. Siempre descargamos el archivo primero (íºtil para arrastrar/adjuntar)
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Factura-${lastInvoice.puntoVenta}-${lastInvoice.numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      // 2. Si tenemos níºmero, priorizamos abrir el chat directo (wa.me)
      // Esto resuelve el problema de "contactos no guardados" que tiene el Native Share.
      if (phone) {
        const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const msg = isMobileDevice
          ? 'ðŸ“„ Descargad✅ Toca ðŸ“Ž y busca en "Documentos".'
          : 'ðŸ“„ PDF descargado. Arrástralo o adjíºntalos al chat.';

        toast.info(msg, { autoClose: 6000 });

        setTimeout(() => {
          const waUrl = `https://wa.me/${phone}`;
          window.open(waUrl, '_blank');
        }, 1500);
        return;
      }

      // 3. Si no hay níºmero, intentamos Native Share como fallback general (ej: para enviar por mail)
      const file = new File([blob], `Factura-${lastInvoice.puntoVenta}-${lastInvoice.numero}.pdf`, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: 'Comprobante de Venta',
            text: 'Hola! Aquí­ tienes tu comprobante.'
          });
        } catch (shareErr) {
          if (shareErr.name !== 'AbortError') {
            console.error('Share error:', shareErr);
          }
        }
      } else {
        toast.info('ðŸ“„ PDF descargado.');
      }
    } catch (e) {
      console.error(e);
      toast.error('âŒ Error al obtener o compartir PDF');
    } finally {
      setLoadingPdf(false);
    }
  };

  // ðŸ”¹ Enviar por WhatsApp

  const blockSearchInputForMobile = () => {
    try {
      const inp = searchInputRef && searchInputRef.current;
      if (!inp) return;
      inputBlockedRef.current = true;
      try { inp.tabIndex = -1; } catch (e) { }
      try { inp.style.pointerEvents = 'none'; } catch (e) { }
      try { inp.setAttribute('aria-disabled', 'true'); } catch (e) { }
    } catch (e) { }
  };

  const restoreSearchInputForMobile = (focus = false) => {
    try {
      const inp = searchInputRef && searchInputRef.current;
      if (!inp) return;
      inputBlockedRef.current = false;
      try { inp.tabIndex = 0; } catch (e) { }
      try { inp.style.pointerEvents = ''; } catch (e) { }
      try { inp.removeAttribute('aria-disabled'); } catch (e) { }
      if (focus) {
        try { inp.focus(); } catch (e) { }
      }
    } catch (e) { }
  };

  // helper to focus the main search input on desktop so scanners that act as keyboards
  // and manual typing are accepted without extra clicks
  const focusMainSearch = useCallback((select = true) => {
    try {
      if (isMobile) return;
      setTimeout(() => {
        try {
          if (searchInputRef && searchInputRef.current) {
            try { searchInputRef.current.focus(); } catch (e) { }
            if (select && typeof searchInputRef.current.select === 'function') {
              try { searchInputRef.current.select(); } catch (e) { }
            }
          }
        } catch (e) { }
      }, 60);
    } catch (e) { }
  }, [isMobile]);

  // --- TERMINAL POINT LOGIC ---
  const initiateTerminalPayment = async (mode) => {
    try {
      // 1. Fetch devices
      toast.info('ðŸ” Buscando terminales MP Point...');
      const devRes = await httpRequest.get('/mp-point/devices');
      const devices = devRes.data?.devices || (Array.isArray(devRes.data) ? devRes.data : []);

      console.log('ðŸ” Dispositivos encontrados:', JSON.stringify(devices, null, 2));

      if (!devices || devices.length === 0) {
        if (window.confirm('âš ï¸ No se encontraron terminales Point conectadas. ¿Deseas registrar el pago manualmente sin enviar a terminal?')) {
          handleFinishSale(mode);
        }
        return;
      }



      // Filter for PDV mode devices
      const pdvDevices = devices.filter(d => d.operating_mode === 'PDV');



      if (pdvDevices.length === 0) {
        // Warn if devices exist but only in STANDALONE mode
        if (devices.length > 0) {
          const deviceToFix = devices[0];
          if (window.confirm(`âš ï¸ La terminal "${deviceToFix.id}" está en modo "STANDALONE".\n\n¿Deseas intentar ponerla en modo "PDV" automáticamente?`)) {
            try {
              toast.info('ðŸ”„ Cambiando modo de terminal a PDV...');
              await httpRequest.patch(`/mp-point/devices/${deviceToFix.id}`, { mode: 'PDV' });
              toast.success('✅ odo actualizado! Reintentando pago en 3 segundos...');

              // Retry recursively after slight delay
              setTimeout(() => initiateTerminalPayment(mode), 3000);
              return;
            } catch (err) {
              console.error('Error auto-fixing device:', err);
              toast.error('âŒ No se pudo cambiar el modo automáticamente. Hazlo desde el meníº de la terminal.');
            }
          }
          // Fallback to manual
          handleFinishSale(mode);
          return;
        }
      }

      // Auto-select first PDV device
      const device = pdvDevices[0] || devices[0]; // Fallback to first if somehow check fails, but unlikely due to above
      const deviceId = device.id;



      setWaitingForTerminal(true);
      setTerminalStatus('Enviando orden a terminar...');
      setPendingSaleMode(mode);



      // Determine amount to charge
      let amountToCharge = total;
      if (paymentMethod === 'Múltiple') {
        const digitalPayment = splitPayments.find(p => p.method === 'Tarjeta' || p.method === 'QR');
        if (digitalPayment) {
          amountToCharge = digitalPayment.amount;
        }
      }

      // 2. Create Payment Intent
      const payload = {
        amount: amountToCharge,
        deviceId: deviceId,
        description: `Venta POS ${waitingForTerminal || ''}`,
        paymentType: paymentMethod === 'Múltiple' ? 'Múltiple' : paymentMethod
      };

      const intentRes = await httpRequest.post('/mp-point/payment-intent', payload);



      const intentId = intentRes.data?.id;

      if (!intentId) throw new Error('No se recibió ID de intención de pago');

      setCurrentPaymentIntentId(intentId);
      setTerminalStatus('Esperando pago en terminal...');
      setActiveDeviceId(deviceId);

      // 3. Start Polling
      startPolling(intentId);

    } catch (error) {
      console.error('Terminal Error:', error);
      const errMsg = error.response?.data?.details || error.response?.data?.message || error.message || 'Error desconocido';

      toast.error(`âŒ Error con terminal: ${errMsg}`, { autoClose: 5000 });
      setWaitingForTerminal(false);
      setPendingSaleMode(null);
    }
  };

  const startPolling = (intentId) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);

    pollTimerRef.current = setInterval(async () => {
      try {
        const res = await httpRequest.get(`/mp-point/payment-intent/${intentId}`);
        const status = res.data?.state; // OPEN, PROCESSED, CANCELED, FINISHED ? Check MP docs
        // Map common statuses:
        // OPEN: Waiting
        // PROCESSED: Success (usually)
        // CANCELED: Canceled
        // ON_TERMINAL: Processing on machine

        console.log('Terminal Status Poll:', status);

        if (status === 'PROCESSED' || status === 'FINISHED' || status === 'APPROVED') {
          // Success! Capture payment data
          clearInterval(pollTimerRef.current);

          // Extract payment details from MP response
          const payment = res.data?.payment || res.data || {};
          const paymentData = {
            transactionId: payment.id || intentId,
            paymentDate: payment.date_created || payment.date_approved || new Date().toISOString(),
            paymentMethod: payment.payment_type_id === 'credit_card' ? 'Tarjeta de Crédito' :
              payment.payment_type_id === 'debit_card' ? 'Tarjeta de Débito' : 'Terminal Point',
            paymentMethodDetail: payment.payment_method_id,
            cardBrand: payment.card?.first_six_digits ?
              (payment.payment_method_id?.toUpperCase() || 'TARJETA') :
              (payment.payment_method_id?.toUpperCase() || null),
            cardLastFourDigits: payment.card?.last_four_digits || null,
            status: status === 'PROCESSED' || status === 'FINISHED' || status === 'APPROVED' ? 'APROBADO' : status?.toUpperCase(),
            amount: payment.transaction_amount || res.data?.amount || total,
            authorizationCode: payment.authorization_code || null,
            operationNumber: payment.id || intentId
          };

          setMpPaymentData(paymentData);
          toast.success('✅ ago aprobado en terminal!');
          setWaitingForTerminal(false);
          setTerminalStatus('Pago Aprobado');

          // Finalize sale immediately - pass paymentData directly
          finishSaleAfterTerminal(paymentData);
        } else if (status === 'CANCELED') {
          clearInterval(pollTimerRef.current);
          toast.error('âŒ Pago cancelado en terminal');
          cancelTerminalProcess(false); // Don't call API cancel again
        }
      } catch (err) {
        console.warn('Polling error', err);
      }
    }, 3000); // Poll every 3 seconds
  };

  // Wrapper to access state in closure correctly or via ref
  const pendingSaleModeRef = useRef(null);
  useEffect(() => { pendingSaleModeRef.current = pendingSaleMode; }, [pendingSaleMode]);

  const finishSaleAfterTerminal = (paymentData = null) => {
    const mode = pendingSaleModeRef.current || 'no-fiscal';
    handleFinishSale(mode, paymentData);
    resetTerminalState();
    // Asegurar que todos los modales se cierren
    setShowPaymentModal(false);
    setShowPaymentMethodModal(false);
  };

  // --- QR INTEGRATION LOGIC ---
  const initiateQRPayment = async (mode) => {
    try {
      setWaitingForQR(true);
      setPendingSaleMode(mode);

      toast.info('ðŸš€ Creando orden QR...');

      // Determine amount to charge
      let amountToCharge = total;
      if (paymentMethod === 'Múltiple') {
        const digitalPayment = splitPayments.find(p => p.method === 'Tarjeta' || p.method === 'QR');
        if (digitalPayment) {
          amountToCharge = digitalPayment.amount;
        }
      }

      const payload = {
        amount: amountToCharge,
        title: `Venta POS $${amountToCharge.toFixed(2)}`,
        description: 'Venta presencial'
      };

      const res = await httpRequest.post('/mp-qr/order', payload);
      const extRef = res.data.external_reference;
      const qrString = res.data.qr_data;

      if (!extRef) throw new Error('No se recibió referencia externa del QR');

      setQrExternalRef(extRef);
      setQrData(qrString); // Guardar string QR para mostrar
      toast.success('✅ rden QR creada. Pidale al cliente que escanee el QR.');

      // Start polling
      startQRPolling(extRef);

    } catch (error) {
      console.error('QR Error:', error);
      toast.error('âŒ Error creando orden QR: ' + (error.response?.data?.message || error.message));
      setWaitingForQR(false);
    }
  };

  const startQRPolling = (extRef) => {
    if (qrPollTimerRef.current) clearInterval(qrPollTimerRef.current);

    qrPollTimerRef.current = setInterval(async () => {
      try {
        const res = await httpRequest.get(`/mp-qr/status/${extRef}`);
        if (res.data.status === 'PAID') {
          clearInterval(qrPollTimerRef.current);

          // Extract payment details from MP response
          const payment = res.data.payment || {};
          const paymentData = {
            transactionId: payment.id || extRef,
            paymentDate: payment.date_created || payment.date_approved || new Date().toISOString(),
            paymentMethod: payment.payment_type_id === 'credit_card' ? 'Tarjeta de Crédito' :
              payment.payment_type_id === 'debit_card' ? 'Tarjeta de Débito' :
                payment.payment_type_id === 'account_money' ? 'Dinero en Cuenta' : 'QR Mercado Pago',
            paymentMethodDetail: payment.payment_method_id,
            cardBrand: payment.card?.first_six_digits ?
              (payment.payment_method_id?.toUpperCase() || 'TARJETA') : null,
            cardLastFourDigits: payment.card?.last_four_digits || null,
            status: payment.status === 'approved' ? 'APROBADO' : payment.status?.toUpperCase() || 'APROBADO',
            amount: res.data.total_paid || payment.transaction_amount,
            authorizationCode: payment.authorization_code || null,
            operationNumber: payment.id || extRef
          };

          setMpPaymentData(paymentData);
          toast.success('✅ ago QR Aprobado!');
          setWaitingForQR(false);
          // Finish sale - pass paymentData directly to avoid async state issues
          const mode = pendingSaleModeRef.current || 'no-fiscal';
          handleFinishSale(mode, paymentData);

          // Cerrar todos los modales
          setShowPaymentModal(false);
          setShowPaymentMethodModal(false);

          // Limpiar orden del mostrador
          httpRequest.delete('/mp-qr/order/QRPRINCIPAL').catch(e => console.warn('Error clearing QR', e));
        }
      } catch (e) {
        console.warn('Polling QR error', e);
      }
    }, 3000);
  };

  const cancelQRProcess = () => {
    if (qrPollTimerRef.current) clearInterval(qrPollTimerRef.current);
    httpRequest.delete('/mp-qr/order/QRPRINCIPAL').catch(() => { }); // Clean up
    setWaitingForQR(false);
    setQrExternalRef(null);
    toast.info('Operación QR cancelada');
  };



  const cancelTerminalProcess = async (callApi = true) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);

    if (callApi && currentPaymentIntentId && activeDeviceId) {
      try {
        toast.info('Cancelando orden en terminal...');
        await httpRequest.delete(`/mp-point/devices/${activeDeviceId}/payment-intents/${currentPaymentIntentId}`);
        toast.info('Orden cancelada');
      } catch (e) {
        console.error('Error cancelling intent', e);
        // toast.warning('No se pudo cancelar en API (¿ya estaba cerrada?)');
      }
    }
    resetTerminalState();
  };

  const resetTerminalState = () => {
    setWaitingForTerminal(false);
    setTerminalStatus('');
    setCurrentPaymentIntentId(null);
    setPendingSaleMode(null);
    setActiveDeviceId(null);
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
  };

  const latestRequestRef = useRef(0);

  const verifyRecentPayments = async (isBackground = false) => {
    const requestTime = Date.now();
    latestRequestRef.current = requestTime;

    try {
      if (!isBackground) {
        setLoadingMpPayments(true);
        setShowMpPaymentsList(true);
      }
      const res = await httpRequest.get('/mp-qr/recent-payments');

      // Race condition check: Only update if this request is still the latest one
      if (requestTime === latestRequestRef.current) {
        if (res.data && res.data.payments) {
          setRecentMpPayments(res.data.payments);
        }
      }
    } catch (e) {
      // Check timestamp even for errors to avoid showing old errors
      if (requestTime === latestRequestRef.current) {
        if (!isBackground) {
          console.error(e);
          toast.error('Error consultando MP');
        }
      }
    } finally {
      if (requestTime === latestRequestRef.current) {
        if (!isBackground) setLoadingMpPayments(false);
      }
    }
  };

  // Auto-activar verificación al elegir Transferencia
  useEffect(() => {
    let interval = null;
    if (showPaymentModal && paymentMethod === 'Transferencia') {
      // Marcar tiempo de inicio para destacar nuevos pagos
      setOpenTransferTime(Date.now());

      // Primera carga visual
      verifyRecentPayments(false);

      // Polling silencioso cada 8 segundos
      interval = setInterval(() => {
        verifyRecentPayments(true);
      }, 8000);
    } else {
      setShowMpPaymentsList(false);
      setOpenTransferTime(null);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaymentModal, paymentMethod]);

  // Interceptor for "Finalizar" buttons
  const handleFinalizeClick = (mode) => {
    // Check if MP integration is enabled. Default to false if not set.
    const mpEnabled = tenant?.settings?.enableMercadoPagoIntegration || false;

    const hasDigitalInMultiple = paymentMethod === 'Múltiple' && splitPayments.some(p => p.method === 'Tarjeta' || p.method === 'QR');

    if (mpEnabled && (paymentMethod === 'Tarjeta' || paymentMethod === 'QR' || hasDigitalInMultiple)) {
      // Show modal to choose: Automatic (terminal) or Manual
      setPendingFinalizeMode(mode);
      setShowPaymentMethodModal(true);
    } else {
      // Standard flow for cash/transfer OR if MP integration is disabled (manual fallback)
      handleFinishSale(mode);
    }
  };

  useEffect(() => {
    try {
      const mq = window.matchMedia('(max-width:768px)');
      // Prefer matchMedia but fall back to actual window.innerWidth to handle
      // devices where CSS pixels / meta viewport make matchMedia unreliable.
      const update = () => {
        try {
          const byMedia = Boolean(mq.matches);
          const byInner = typeof window.innerWidth === 'number' && window.innerWidth <= 768;
          setIsMobile(byMedia || byInner);
        } catch (e) {
          setIsMobile(false);
        }
      };
      update();
      if (typeof mq.addEventListener === 'function') mq.addEventListener('change', update);
      else if (typeof mq.addListener === 'function') mq.addListener(update);
      // Also listen resize as a robust fallback
      window.addEventListener('resize', update);
      return () => {
        try {
          if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', update);
          else if (typeof mq.removeListener === 'function') mq.removeListener(update);
        } catch (e) { }
        try { window.removeEventListener('resize', update); } catch (e) { }
      };
    } catch (e) {
      // ignore on SSR or unsupported
    }
  }, []);

  // Focus main search input when running on desktop so scanner (keyboard) input is captured
  useEffect(() => {
    try {
      if (!isMobile) focusMainSearch();
    } catch (e) { }
  }, [isMobile, focusMainSearch]);

  // Shortcut: F10 opens the price lookup modal (or refocuses its hidden input when open)
  useEffect(() => {
    const onKey = (e) => {
      try {
        if (e.key === 'F10') {
          e.preventDefault();
          if (priceLookupOpen) {
            try { if (window && window.salesPriceLookupRefocus) window.salesPriceLookupRefocus(); } catch (err) { }
          } else {
            try { openPriceLookup(); } catch (err) { }
          }
        }
      } catch (err) { }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [priceLookupOpen, openPriceLookup]);

  // Shortcut: F2 opens the payment modal (Finalizar Venta)
  useEffect(() => {
    const onKey = (e) => {
      try {
        const isF2 = (e.key === 'F2') || (e.code === 'F2') || (typeof e.keyCode === 'number' && e.keyCode === 113);
        if (isF2) {
          e.preventDefault();
          if (!showPaymentModal && cart.length > 0) {
            try { openPaymentModal(); } catch (err) { }
          }
        }
      } catch (err) { }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [openPaymentModal, showPaymentModal, cart.length]);

  // Limpiar estados de pago si el carrito se vací­a (previene datos residuales de ventas anteriores o canceladas)
  useEffect(() => {
    if (cart.length === 0) {
      setSplitPayments([]);
      setAmountPaid('');
      setPaymentMethod('Efectivo');
    }
  }, [cart]);

  // Shortcut: F7 -> Ticket (No Fiscal), F9 -> Factura Fiscal (cuando está abierto el modal de pago)
  useEffect(() => {
    const onKey = (e) => {
      try {
        const isF7 = (e.key === 'F7') || (e.code === 'F7') || (typeof e.keyCode === 'number' && e.keyCode === 118);
        const isF9 = (e.key === 'F9') || (e.code === 'F9') || (typeof e.keyCode === 'number' && e.keyCode === 120);
        if (!isF7 && !isF9) return;
        // Only handle shortcuts when the payment modal is visible to avoid surprising global behavior
        if (!showPaymentModal) return;
        e.preventDefault();
        if (isF7) {
          try { handleFinishSale('no-fiscal'); } catch (err) { /* ignore */ }
        } else if (isF9) {
          try { handleFinishSale('fiscal'); } catch (err) { /* ignore */ }
        }
      } catch (err) {
        // ignore
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [showPaymentModal, handleFinishSale]);

  // When the price lookup modal closes, ensure main search input regains focus (desktop)
  useEffect(() => {
    try {
      if (!priceLookupOpen && !isMobile) focusMainSearch();
    } catch (e) { }
  }, [priceLookupOpen, isMobile, focusMainSearch]);

  // Note: logout/test-sound handlers were removed (not used in UI) to avoid unused-function warnings

  return (
    <div style={pageBg}>
      {isBlockedByCash && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(255,255,255,0.95)', zIndex: 99999,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(8px)', padding: '20px', textAlign: 'center'
        }}>
          <div style={{ fontSize: '80px', marginBottom: '20px' }}>ðŸ”</div>
          <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#1f2937' }}>Caja Cerrada</h2>
          <p style={{ margin: '15px 0 30px 0', fontSize: '18px', color: '#4b5563', maxWidth: '400px' }}>
            Tu usuario no tiene permisos para vender sin una caja abierta. Por favor, abre una caja para continuar.
          </p>
          <button
            onClick={() => navigate('/cash-register')}
            style={{
              padding: '16px 40px', background: '#007bff', color: 'white',
              border: 'none', borderRadius: '12px', fontSize: '18px', fontWeight: 'bold',
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,123,255,0.3)', width: '100%', maxWidth: '300px'
            }}
          >
            ðŸ”“ Abrir Caja Ahora
          </button>
          <button
            onClick={() => {
              dispatch(logout());
              dispatch(reset());
              navigate('/login');
            }}
            style={{
              marginTop: '16px', padding: '12px 30px', background: 'transparent', color: '#dc3545',
              border: '2px solid #dc3545', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold',
              cursor: 'pointer', width: '100%', maxWidth: '300px'
            }}
          >
            🚫 Cerrar Sesión (Salir)
          </button>
        </div>
      )}
      {!isOnline && (
        <div style={{
          position: 'fixed', bottom: 12, left: 12, zIndex: 9999,
          background: '#dc3545', color: 'white', fontWeight: 'bold', padding: '8px 16px', borderRadius: '50px',
          boxShadow: '0 4px 12px rgba(220,53,69,0.4)', fontSize: '14px',
          display: 'flex', alignItems: 'center', gap: '8px', pointerEvents: 'none'
        }}>
          🚫 MODO OFFLINE
        </div>
      )}
      {priceLookupOpen && (
        <PriceLookupModal open={priceLookupOpen} product={priceLookupProduct} products={products} openScanner={openScanner} onClose={closePriceLookup} onAdd={addToCart} isMobile={isMobile} onManualScan={handleScanned} />
      )}
      <div className="container-safe" style={{ padding: isMobile ? '12px' : '30px' }}>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }} className="stack-sm">
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            📊 Ventas
            {SHOW_WORLD_CUP_DECORATION && (
              <>
                <style>{`
                  @keyframes bounce-ball {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    50% { transform: translateY(-8px) rotate(180deg); }
                  }
                  @keyframes wave-flag {
                    0%, 100% { transform: skewY(0deg) translateY(0); }
                    25% { transform: skewY(4deg) translateY(-1.5px); }
                    75% { transform: skewY(-4deg) translateY(1.5px); }
                  }
                `}</style>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginLeft: '12px', verticalAlign: 'middle' }}>
                  {/* Pure CSS Waving Argentine Flag */}
                  <div 
                    title="Argentina"
                    style={{
                      width: '38px',
                      height: '24px',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
                      animation: 'wave-flag 2s infinite ease-in-out',
                      transformOrigin: 'left center',
                      position: 'relative'
                    }}
                  >
                    <div style={{ flex: 1, backgroundColor: '#74acdf' }}></div>
                    <div style={{ flex: 1, backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <span style={{ fontSize: '10px', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', lineHeight: 1 }}>☀️</span>
                    </div>
                    <div style={{ flex: 1, backgroundColor: '#74acdf' }}></div>
                  </div>
                  <span style={{ 
                    fontSize: '22px', 
                    animation: 'bounce-ball 2.2s infinite ease-in-out', 
                    display: 'inline-block' 
                  }}>⚽</span>
                </div>
              </>
            )}
          </h1>
          <div style={{ display: 'flex', gap: '10px' }} className="stack-sm">
            {!isMobile && (
              <>
                <button style={btnNav} onClick={() => navigate('/')}>🏠 Dashboard</button>
                <button style={{ ...btnNav, background: '#17a2b8' }} onClick={() => navigate('/sales-history')}>📋 Historial</button>

                <button
                  style={{
                    ...btnNav,
                    background: newOnlineOrdersCount > 0 ? '#f44336' : '#e3f2fd',
                    color: newOnlineOrdersCount > 0 ? '#fff' : '#1976d2',
                    border: newOnlineOrdersCount > 0 ? 'none' : '1px solid #1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: '800'
                  }}
                  onClick={() => {
                    // Marcar todos los pedidos actuales como vistos
                    localStorage.setItem('seenOnlineOrdersCount', String(onlineOrdersCount));
                    setNewOnlineOrdersCount(0);
                    navigate('/sales-history');
                  }}
                >
                  🌐 Pedidos
                  {newOnlineOrdersCount > 0 && (
                    <span style={{
                      background: '#fff',
                      color: '#f44336',
                      borderRadius: '10px',
                      padding: '1px 7px',
                      fontSize: '12px',
                      fontWeight: '800'
                    }}>
                      {newOnlineOrdersCount}
                    </span>
                  )}
                </button>

                <button style={{ ...btnNav, background: '#007bff' }} onClick={() => navigate('/products')}>🛍️ Productos</button>

                <button
                  style={{ ...btnNav, background: '#ffc107', position: 'relative' }}
                  onClick={() => setShowPendingModal(true)}
                  title="Pendientes"
                >
                  🔔 Pendientes{pendingCount ? ` (${pendingCount})` : ''}
                </button>
                <button style={{ ...btnNav, background: '#28a745' }} onClick={goToInvoices} title="Ir a Facturas">
                  🧾 Factura
                </button>
              </>
            )}

            {isMobile && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnSmallGray} onClick={() => { tryUnlockAudio(); }} title="Probar/activar sonido">🔊 Sonido</button>
                <button style={btnSmallRed} onClick={() => { handleLogout(); }} title="Cerrar sesión">🚫 Salir</button>
              </div>
            )}
          </div>
        </div>
        {/* AFIP ambiente banner (pequeño, informativo) */}
        <div style={{ marginBottom: 12 }}>
          <div style={{
            display: 'inline-block', padding: '6px 10px', borderRadius: 8, fontWeight: 700, fontSize: 13,
            background: afipAmbiente === 'production' ? '#d1fae5' : '#fff7ed',
            color: afipAmbiente === 'production' ? '#065f46' : '#92400e',
            border: '1px solid', borderColor: afipAmbiente === 'production' ? '#86efac' : '#fecaca'
          }}
            title={afipAmbiente === 'production' ? 'Ambiente AFIP: Producción' : 'Ambiente AFIP: Testing / Homologación'}>
            Ambiente AFIP: {afipAmbiente === 'production' ? 'Producción' : 'Testing / Homologación'}
          </div>
        </div>

        {/* Search + Scan area: column layout only on mobile, row layout on desktop */}
        {isMobile ? (
          <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div>
              {/* Input de búsqueda ocupa todo el ancho en móvil */}
              <input
                ref={searchInputRef}
                type="text"
                placeholder={/^\d+$/.test(search) ? "🔢 Presiona Enter para buscar por códig✅.." : "🔍 Escribe para buscar por nombre..."}
                value={search}
                onChange={handleSearchChange}
                onFocus={(e) => {
                  try {
                    if (scanJustHappenedRef && scanJustHappenedRef.current) {
                      try { e.target.blur(); } catch (err) { }
                      try { hideMobileKeyboard(); } catch (err) { }
                    }
                  } catch (err) { }
                }}
                onKeyDown={handleKeyDown}
                style={{
                  ...inputSearch,
                  width: '100%',
                  backgroundColor: /^\d+$/.test(search) ? '#fff9dc' : '#ffffff',
                }}
              />
              {lastScannedCodeMobile && (
                <div style={{ marginTop: 8, padding: '8px 10px', background: '#eef6ff', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, color: '#055160' }}>Código escaneado: <strong>{lastScannedCodeMobile}</strong></div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...btnSmallGray }} onClick={() => { setSearch(lastScannedCodeMobile); setLastScannedCodeMobile(''); try { restoreSearchInputForMobile(true); } catch (e) { } }}>Usar</button>
                    <button style={{ ...btnSmallRed }} onClick={() => setLastScannedCodeMobile('')}>✖</button>
                  </div>
                </div>
              )}
              {pendingQuantity && (
                <span onClick={() => setPendingQuantity(null)} style={{ marginLeft: '8px', padding: '6px 10px', background: '#ffeeba', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }} title="Cancelar cantidad pendiente">x{pendingQuantity}</span>
              )}
              {suggestions.length > 0 && (
                <div style={suggestionsBox} ref={suggestionsRef}>
                  {suggestions.map((p, idx) => {
                    const badge = getDiscountBadgeForProduct(p, pendingQuantity || 1);
                    const isActive = idx === activeSuggestionIdx;
                    return (
                      <div
                        key={p._id}
                        style={{ ...suggestionItem, background: isActive ? '#c8e2ff' : undefined }}
                        onMouseEnter={() => setActiveSuggestionIdx(idx)}
                        onMouseLeave={() => setActiveSuggestionIdx(-1)}
                        onClick={() => { addOrOpenCombo(p); setActiveSuggestionIdx(-1); }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                            {p.ecommerceImages && p.ecommerceImages[0] ? (
                              <img
                                src={p.ecommerceImages[0]}
                                alt={p.name}
                                onClick={(e) => { e.stopPropagation(); setImagePreviewUrl(p.ecommerceImages[0]); }}
                                style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 5, cursor: 'zoom-in', border: '1px solid #ddd', flexShrink: 0 }}
                              />
                            ) : null}
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>{p.name}</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {badge && <span style={discountBadge}>{badge}</span>}
                            <div style={{ fontWeight: 800 }}>{'$' + (() => {
                              const basePrice = Number(p.salePrice ?? p.price ?? p.unitPrice ?? 0);
                              if (showFinalPriceWithIVA) {
                                const ivaCode = p.alicuotaIVACode ?? p.alicuotaIVA ?? 5;
                                if (ivaCode === 0) return basePrice.toFixed(2);
                                const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                                return (basePrice * (1 + ivaPct / 100)).toFixed(2);
                              }
                              return basePrice.toFixed(2);
                            })()}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              {/* Escanear debajo del input, full width en móvil */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...btnNav, width: '100%' }} onClick={openScanner} title="Escanear con cámara">📷 Escanear</button>
                <button style={{ ...btnSmallGray }} onClick={() => openPriceLookup()} title="Consultar precio (F10)">🔍 (F10)</button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '20px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder={/^\d+$/.test(search) ? "🔢 Presiona Enter para buscar por códig✅.." : "🔍 Escribe para buscar por nombre..."}
                value={search}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                style={{
                  ...inputSearch,
                  backgroundColor: /^\d+$/.test(search) ? '#fff9dc' : '#ffffff',
                }}
              />
              {pendingQuantity && (
                <span onClick={() => setPendingQuantity(null)} style={{ marginLeft: '8px', padding: '6px 10px', background: '#ffeeba', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }} title="Cancelar cantidad pendiente">x{pendingQuantity}</span>
              )}
              {suggestions.length > 0 && (
                <div style={suggestionsBox} ref={suggestionsRef}>
                  {suggestions.map((p, idx) => {
                    const badge = getDiscountBadgeForProduct(p, pendingQuantity || 1);
                    const isActive = idx === activeSuggestionIdx;
                    return (
                      <div
                        key={p._id}
                        style={{ ...suggestionItem, background: isActive ? '#c8e2ff' : undefined }}
                        onMouseEnter={() => setActiveSuggestionIdx(idx)}
                        onMouseLeave={() => setActiveSuggestionIdx(-1)}
                        onClick={() => { addOrOpenCombo(p); setActiveSuggestionIdx(-1); }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>{p.name}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {badge && <span style={discountBadge}>{badge}</span>}
                            <div style={{ fontWeight: 800 }}>{'$' + (() => {
                              const basePrice = Number(p.salePrice ?? p.price ?? p.unitPrice ?? 0);
                              if (showFinalPriceWithIVA) {
                                const ivaCode = p.alicuotaIVACode ?? p.alicuotaIVA ?? 5;
                                if (ivaCode === 0) return basePrice.toFixed(2);
                                const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                                return (basePrice * (1 + ivaPct / 100)).toFixed(2);
                              }
                              return basePrice.toFixed(2);
                            })()}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ minWidth: 240, display: 'flex', gap: '8px' }}>
              {/* En desktop ocultamos el botón de "Escanear" (solo disponible en móvil) */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button style={{ ...btnSmallGray }} onClick={() => openPriceLookup()} title="Consultar precio (F10)">🔍 Consultar precio (F10)</button>
              </div>
            </div>
          </div>
        )}

        {/* Carrito: tarjeta en móvil, tabla en desktop (incluye IVA si corresponde) */}
        <div style={{ marginTop: 10 }}>
          {cart.length === 0 ? (
            <div style={{ padding: '12px', color: '#666' }}>El carrito está vacío</div>
          ) : (
            isMobile ? (
              <div style={{ display: 'grid', gap: 12, marginTop: 6 }}>
                {cart.map((item) => (
                  <div key={item.cartUid || item.productId} style={cartItemContainer}>
                    <div style={cartItemLeft}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {item.ecommerceImages && item.ecommerceImages[0] ? (
                          <img
                            src={item.ecommerceImages[0]}
                            alt={item.name}
                            onClick={() => setImagePreviewUrl(item.ecommerceImages[0])}
                            style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', border: '1px solid #e2e8f0', flexShrink: 0 }}
                          />
                        ) : null}
                        <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                        {Number(item.unitPriceWithDiscount || 0) < Number(item.price || 0) && Math.round(((Number(item.price) - Number(item.unitPriceWithDiscount)) / Number(item.price)) * 100) > 0 && (
                          <span style={discountBadge}>{`${Math.round(((Number(item.price) - Number(item.unitPriceWithDiscount)) / Number(item.price)) * 100)}%`}</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button style={{ ...btnQty, padding: '6px 10px' }} onClick={() => decrementQuantity(item.cartUid || item.productId)}>−</button>
                        <div style={{ fontWeight: 800, minWidth: 28, textAlign: 'center' }}>{item.quantity}</div>
                        <button style={{ ...btnQty, padding: '6px 10px' }} onClick={() => incrementQuantity(item.cartUid || item.productId)}>+</button>
                      </div>
                    </div>
                    <div style={cartItemRight}>
                      <div style={{ alignSelf: 'flex-end' }}>
                        <button aria-label={`Eliminar ${item.name}`} title="Eliminar" style={{ ...btnSmallRed, padding: '6px 8px', marginBottom: 6 }} onClick={() => handleRemove(item.cartUid || item.productId)}>✖</button>
                        {item.isComboDiscount && (
                          <button title="Quitar descuento" style={{ ...btnSmallGray, padding: '6px 8px', marginTop: 6 }} onClick={() => handleRemoveComboDiscount(item.comboId)}>Quitar descuento</button>
                        )}
                      </div>
                      <div style={{ fontWeight: 800 }}>{'$' + (() => {
                        const baseSubtotal = parseFloat(item.subtotal);
                        if (showFinalPriceWithIVA) {
                          const ivaCode = item.alicuotaIVACode ?? item.alicuotaIVA ?? 5;
                          if (ivaCode === 0) return baseSubtotal.toFixed(2);
                          const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                          return (baseSubtotal * (1 + ivaPct / 100)).toFixed(2);
                        }
                        return baseSubtotal.toFixed(2);
                      })()}</div>
                      {/* Mostrar precio original tachado si existe descuento o modificación manual */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                        {(user.role === 'admin' || user.role === 'owner') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const current = item.unitPriceWithDiscount ?? item.price;
                              const val = prompt('Nuevo precio unitario:', current);
                              if (val !== null) updateCartItemPrice(item.cartUid || item.productId, val);
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', fontSize: '11px', color: '#007bff' }}
                            title="Editar precio"
                          >
                            ✏️ Editar Precio
                          </button>
                        )}
                        {Number(item.unitPriceWithDiscount || 0).toFixed(2) !== Number(item.price || 0).toFixed(2) ? (
                          <div style={{ fontSize: 13, color: '#555', display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ textDecoration: 'line-through', color: '#888' }}>{'$' + (() => {
                              const base = Number(item.price || 0);
                              if (showFinalPriceWithIVA) {
                                const ivaCode = item.alicuotaIVACode ?? item.alicuotaIVA ?? 5;
                                if (ivaCode === 0) return base.toFixed(2);
                                const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                                return (base * (1 + ivaPct / 100)).toFixed(2);
                              }
                              return base.toFixed(2);
                            })()}</span>
                            <span style={{ fontWeight: 800, color: '#000' }}>{'$' + (() => {
                              const base = Number(item.unitPriceWithDiscount || 0);
                              if (showFinalPriceWithIVA) {
                                const ivaCode = item.alicuotaIVACode ?? item.alicuotaIVA ?? 5;
                                if (ivaCode === 0) return base.toFixed(2);
                                const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                                return (base * (1 + ivaPct / 100)).toFixed(2);
                              }
                              return base.toFixed(2);
                            })()}</span>
                          </div>
                        ) : (
                          <div style={{ fontSize: 13, color: '#555' }}>{'Precio: $' + (() => {
                            const basePrice = parseFloat(item.price);
                            if (showFinalPriceWithIVA) {
                              const ivaCode = item.alicuotaIVACode ?? item.alicuotaIVA ?? 5;
                              if (ivaCode === 0) return basePrice.toFixed(2);
                              const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                              return (basePrice * (1 + ivaPct / 100)).toFixed(2);
                            }
                            return basePrice.toFixed(2);
                          })()}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Desktop: tabla con columnas Producto | Cantidad | Precio | IVA? | Subtotal | Acciones
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left' }}>
                      <th style={{ padding: '10px 12px' }}>Producto</th>
                      <th style={{ padding: '10px 12px', width: 160 }}>Cantidad</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right' }}>Precio</th>
                      {(fiscal && !emitterIsMonotributo) && <th style={{ padding: '10px 12px', textAlign: 'right' }}>IVA</th>}
                      <th style={{ padding: '10px 12px', textAlign: 'right' }}>Subtotal</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((item) => (
                      <tr key={item.cartUid || item.productId} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {item.ecommerceImages && item.ecommerceImages[0] ? (
                              <img
                                src={item.ecommerceImages[0]}
                                alt={item.name}
                                onClick={() => setImagePreviewUrl(item.ecommerceImages[0])}
                                style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in', border: '1px solid #e2e8f0', flexShrink: 0 }}
                              />
                            ) : null}
                            <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 380 }}>{item.name}</div>
                            {Number(item.unitPriceWithDiscount || 0) < Number(item.price || 0) && Math.round(((Number(item.price) - Number(item.unitPriceWithDiscount)) / Number(item.price)) * 100) > 0 && (
                              <span style={discountBadge}>{`${Math.round(((Number(item.price) - Number(item.unitPriceWithDiscount)) / Number(item.price)) * 100)}%`}</span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button style={btnQty} onClick={() => decrementQuantity(item.cartUid || item.productId)}>−</button>
                            <div style={{ fontWeight: 800, minWidth: 28, textAlign: 'center' }}>{item.quantity}</div>
                            <button style={btnQty} onClick={() => incrementQuantity(item.cartUid || item.productId)}>+</button>
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '5px' }}>
                            {/* Edit Button */}
                            {(user.role === 'admin' || user.role === 'owner') && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const current = item.unitPriceWithDiscount ?? item.price;
                                  const val = prompt('Nuevo precio unitario:', current);
                                  if (val !== null) updateCartItemPrice(item.cartUid || item.productId, val);
                                }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', fontSize: '10px' }}
                                title="Editar precio"
                              >
                                ✏️ 
                              </button>
                            )}
                            {Number(item.unitPriceWithDiscount || 0).toFixed(2) !== Number(item.price || 0).toFixed(2) ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <span style={{ textDecoration: 'line-through', color: '#888', fontSize: 13 }}>{'$' + (() => {
                                  const base = Number(item.price || 0);
                                  if (showFinalPriceWithIVA) {
                                    const ivaCode = item.alicuotaIVACode ?? item.alicuotaIVA ?? 5;
                                    if (ivaCode === 0) return base.toFixed(2);
                                    const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                                    return (base * (1 + ivaPct / 100)).toFixed(2);
                                  }
                                  return base.toFixed(2);
                                })()}</span>
                                <span style={{ fontWeight: 800 }}>{'$' + (() => {
                                  const base = Number(item.unitPriceWithDiscount || 0);
                                  if (showFinalPriceWithIVA) {
                                    const ivaCode = item.alicuotaIVACode ?? item.alicuotaIVA ?? 5;
                                    if (ivaCode === 0) return base.toFixed(2);
                                    const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                                    return (base * (1 + ivaPct / 100)).toFixed(2);
                                  }
                                  return base.toFixed(2);
                                })()}</span>
                              </div>
                            ) : (
                              <div>{'$' + (() => {
                                const basePrice = parseFloat(item.price);
                                if (showFinalPriceWithIVA) {
                                  const ivaCode = item.alicuotaIVACode ?? item.alicuotaIVA ?? 5;
                                  if (ivaCode === 0) return basePrice.toFixed(2);
                                  const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                                  return (basePrice * (1 + ivaPct / 100)).toFixed(2);
                                }
                                return basePrice.toFixed(2);
                              })()}</div>
                            )}
                          </div>
                        </td>
                        {(fiscal && !emitterIsMonotributo) && (
                          <td style={{ padding: '12px', textAlign: 'right' }}>{(getAlicuotaPercent(item.alicuotaIVACode ?? item.alicuotaIVA ?? 5) || 0) + '%'}</td>
                        )}
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 800 }}>${(() => {
                          const baseSubtotal = parseFloat(item.subtotal);
                          if (showFinalPriceWithIVA) {
                            const ivaCode = item.alicuotaIVACode ?? item.alicuotaIVA ?? 5;
                            if (ivaCode === 0) return baseSubtotal.toFixed(2);
                            const ivaPct = getAlicuotaPercent(ivaCode) || 0;
                            return (baseSubtotal * (1 + ivaPct / 100)).toFixed(2);
                          }
                          return baseSubtotal.toFixed(2);
                        })()}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          {item.isComboDiscount && (
                            <button title="Quitar descuento" style={{ ...btnSmallGray, padding: '6px 8px', marginRight: 8 }} onClick={() => handleRemoveComboDiscount(item.comboId)}>Quitar descuento</button>
                          )}
                          <button aria-label={`Eliminar ${item.name}`} title="Eliminar" style={{ ...btnSmallRed, padding: '6px 8px' }} onClick={() => handleRemove(item.cartUid || item.productId)}>✖</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>

        {/* Sección de cliente */}
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h3 style={{ marginBottom: '10px', fontSize: '16px' }}>👤 Cliente</h3>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="🔍 Buscar cliente por razón social o CUIT..."
              value={customerSearch}
              onChange={(e) => handleCustomerSearch(e.target.value)}
              style={{ ...inputSearch, width: '100%' }}
            />
            {selectedCustomer && (
              <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#d4edda', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{selectedCustomer.razonSocial || selectedCustomer.name}</strong>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedCustomer.cuit ? `CUIT: ${selectedCustomer.cuit}` : ''} {selectedCustomer.phone && `• ${selectedCustomer.phone}`} {selectedCustomer.email && `• ${selectedCustomer.email}`}
                  </div>
                </div>
                <button style={{ ...btnSmallRed, padding: '4px 8px' }} onClick={clearCustomer}>✖</button>
              </div>
            )}
            {customerSuggestions.length > 0 && !selectedCustomer && (
              <div style={{ ...suggestionsBox, position: 'absolute', top: '100%', width: '100%', zIndex: 10 }}>
                {customerSuggestions.map((c) => (
                  <div key={c._id} style={suggestionItem} onClick={() => selectCustomer(c)}>
                    <strong>{c.razonSocial || c.name}</strong> {c.cuit && `- ${c.cuit}`} {c.phone && `• ${c.phone}`}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button
              style={{ ...btnNav, background: '#17a2b8' }}
              onClick={() => setShowCustomerModal(true)}
            >
              ➕ Nuevo Cliente
            </button>
            <button
              style={{ ...btnNav, background: '#6c757d' }}
              onClick={handleOpenLoadQuotation}
            >
              📋 Cargar Presupuesto
            </button>
          </div>
        </div>

        {!isMobile && (
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
            {cart.length === 0 ? (
              <div style={{
                padding: '12px 24px',
                background: '#f8f9fa',
                borderLeft: '5px solid #6c757d',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <h2 style={{ fontSize: '24px', margin: 0, color: '#495057' }}>Total: <span style={{ color: '#212529', fontWeight: '900' }}>$0.00</span></h2>
              </div>
            ) : (
              <div style={{
                padding: '16px 24px',
                background: '#e7f3ff',
                borderLeft: '5px solid #007bff',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,123,255,0.1)'
              }}>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#555' }}>Subtotal: ${subtotalAmt.toFixed(2)}</div>
                {(fiscal && !emitterIsMonotributo) && (
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#555' }}>IVA ({ivaLabel}): ${ivaAmt.toFixed(2)}</div>
                )}
                {/* Descuento */}
                <div style={{ fontSize: '14px', fontWeight: '600', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label htmlFor="saleDiscount" style={{ color: '#dc3545' }}>Descuento $:</label>
                  <input
                    id="saleDiscount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={saleDiscount || ''}
                    onChange={(e) => setSaleDiscount(Math.max(0, parseFloat(e.target.value) || 0))}
                    style={{
                      width: '100px',
                      padding: '4px 8px',
                      fontSize: '14px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontWeight: '700'
                    }}
                    placeholder="0.00"
                  />
                </div>
                <div style={{ fontSize: '32px', fontWeight: '900', marginTop: '8px', color: '#000' }}>Total: ${total.toFixed(2)}</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={{ ...btnGreen, background: '#17a2b8' }} onClick={handleQuotation} disabled={cart.length === 0}>📋 Presupuesto</button>
              <button style={btnClearCart} onClick={handleClearCart} disabled={cart.length === 0}>🗑️ Vaciar Carrito</button>
              <button style={btnGreen} onClick={openPaymentModal} disabled={cart.length === 0}>✅ Finalizar Venta (F2)</button>
            </div>
          </div>
        )}

        {/* Footer fijo para móvil: muestra total y boton grande de pago */}
        {isMobile && cart.length > 0 && (
          <div style={mobileFooter}>
            <div style={mobileFooterTotal}>
              <div style={{ fontSize: 12 }}>Total</div>
              <div style={{ fontWeight: 800, fontSize: 18 }}>${total.toFixed(2)}</div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ ...mobileFooterBtn, background: '#17a2b8', padding: '0 12px' }} onClick={handleQuotation}>📋 Presup.</button>
              <button style={mobileFooterBtn} onClick={openPaymentModal}>✅ Pagar (F2)</button>
            </div>
          </div>
        )}

        {/* Modal de pago */}
        {showPaymentModal && (
          <div style={modalOverlay} onClick={() => { if (!isProcessingPayment) setShowPaymentModal(false); }}>
            <div style={modalContent} onClick={(e) => e.stopPropagation()} aria-busy={isProcessingPayment}>
              <h2 style={{ marginBottom: '20px' }}>💳 Método de Pago</h2>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                  Selecciona el método de pago:
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    style={paymentMethod === 'Efectivo' ? btnPaymentActive : btnPayment}
                    onClick={() => setPaymentMethod('Efectivo')}
                  >
                    💵 Efectivo
                  </button>
                  <button
                    style={paymentMethod === 'Tarjeta' ? btnPaymentActive : btnPayment}
                    onClick={() => setPaymentMethod('Tarjeta')}
                  >
                    💳 Tarjeta
                  </button>
                  <button
                    style={paymentMethod === 'Transferencia' ? btnPaymentActive : btnPayment}
                    onClick={() => setPaymentMethod('Transferencia')}
                  >
                    📲 Transferencia
                  </button>
                  <button
                    style={paymentMethod === 'QR' ? btnPaymentActive : btnPayment}
                    onClick={() => setPaymentMethod('QR')}
                  >
                    📱 QR
                  </button>
                  <button
                    style={paymentMethod === 'Múltiple' ? btnPaymentActive : btnPayment}
                    onClick={() => setPaymentMethod('Múltiple')}
                  >
                    🔢 Múltiple
                  </button>
                  {selectedCustomer && selectedCustomer.cuentaCorrienteEnabled && (
                    <button
                      style={paymentMethod === 'Cuenta corriente' ? btnPaymentActive : btnPayment}
                      onClick={() => setPaymentMethod('Cuenta corriente')}
                    >
                      📒 Cuenta corriente
                    </button>
                  )}
                </div>
              </div>

              {paymentMethod === 'Efectivo' && (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                      Monto recibido:
                    </label>
                    <input
                      type="number"
                      value={amountPaid}
                      onChange={(e) => setAmountPaid(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      style={{ ...inputSearch, width: '100%' }}
                      placeholder="0.00"
                      autoFocus
                    />
                  </div>
                  {change > 0 && (
                    <div style={{ padding: '15px', backgroundColor: '#d4edda', borderRadius: '6px', marginBottom: '15px' }}>
                      <strong style={{ fontSize: '18px', color: '#155724' }}>Vuelto a entregar: ${change.toFixed(2)}</strong>
                    </div>
                  )}
                  {amountPaid && parseFloat(amountPaid) > 0 && parseFloat(amountPaid) < total && (
                    <div style={{ padding: '15px', backgroundColor: '#f8d7da', borderRadius: '6px', marginBottom: '15px', border: '1px solid #f5c6cb' }}>
                      <strong style={{ fontSize: '18px', color: '#721c24' }}>⚠️ Faltan: ${(total - parseFloat(amountPaid)).toFixed(2)}</strong>
                    </div>
                  )}
                </>
              )}

              {paymentMethod === 'Tarjeta' && (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                      Cantidad de cuotas:
                    </label>
                    <select
                      value={installments}
                      onChange={(e) => setInstallments(Number(e.target.value))}
                      style={{ ...inputSearch, width: '100%' }}
                    >
                      {[...Array(12)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1} {i + 1 === 1 ? 'cuota' : 'cuotas'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                      Interés por cuota (%):
                    </label>
                    <input
                      type="number"
                      value={interestRate}
                      onChange={(e) => setInterestRate(Number(e.target.value))}
                      style={{ ...inputSearch, width: '100%' }}
                      placeholder="0"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  {interestRate > 0 && (
                    <div style={{ padding: '15px', backgroundColor: '#fff3cd', borderRadius: '6px', marginBottom: '15px' }}>
                      <p style={{ margin: '0 0 8px 0' }}>
                        <strong>Total con interés:</strong> ${(total * (1 + (interestRate / 100) * installments)).toFixed(2)}
                      </p>
                      <p style={{ margin: 0, fontSize: '13px', color: '#856404' }}>
                        Valor por cuota: ${((total * (1 + (interestRate / 100) * installments)) / installments).toFixed(2)}
                      </p>
                    </div>
                  )}
                </>
              )}
              {paymentMethod === 'Transferencia' && (
                <div style={{ marginBottom: '15px', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showMpPaymentsList ? '8px' : '0' }}>
                    <span style={{ fontSize: '13px', color: '#4a5568', display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '5px' }}>📡</span>
                      {loadingMpPayments ? 'Buscando pagos...' : 'Últimos ingresos detectados:'}
                    </span>
                    <button
                      onClick={() => verifyRecentPayments(false)}
                      title="Refrescar lista"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#3182ce',
                        cursor: 'pointer',
                        fontSize: '12px',
                        textDecoration: 'underline'
                      }}
                    >
                      Refrescar
                    </button>
                  </div>

                  {showMpPaymentsList && (
                    <div style={{ maxHeight: '130px', overflowY: 'auto', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
                      {recentMpPayments.length === 0 ? (
                        <div style={{ padding: '10px', textAlign: 'center', color: '#718096', fontSize: '12px' }}>
                          No se encontraron pagos recientes.
                        </div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                          <thead style={{ background: '#f7fafc', color: '#4a5568', position: 'sticky', top: 0 }}>
                            <tr>
                              <th style={{ padding: '4px 6px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Hora</th>
                              <th style={{ padding: '4px 6px', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Monto</th>
                              <th style={{ padding: '4px 6px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Pagador</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentMpPayments.map((p, index) => {
                              const dateObj = new Date(p.date);
                              const isToday = dateObj.toDateString() === new Date().toDateString();
                              const timeStr = dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                              const dateStr = dateObj.toLocaleDateString([], { day: '2-digit', month: '2-digit' });

                              // Highlight if new since modal opened
                              const isNew = openTransferTime && dateObj.getTime() > openTransferTime;
                              const isFirst = index === 0;

                              let rowBg = '#fff';
                              let borderLeft = 'none';

                              if (isNew) {
                                rowBg = '#f0fff4'; // Green for brand new
                                borderLeft = '3px solid #48bb78';
                              } else if (isFirst) {
                                rowBg = '#ebf8ff'; // Light blue for most recent
                                borderLeft = '3px solid #3182ce';
                              }

                              const dateColor = isNew || isFirst ? '#2c5282' : '#718096';
                              const amountColor = isNew || isFirst ? '#2b6cb0' : '#4a5568';
                              const fontWeight = isNew || isFirst ? 'bold' : 'normal';

                              return (
                                <tr key={p.id} style={{ borderBottom: '1px solid #edf2f7', backgroundColor: rowBg, borderLeft: borderLeft }}>
                                  <td style={{ padding: '4px 6px', color: dateColor, fontWeight: fontWeight, fontSize: '11px' }}>
                                    {isNew && <span style={{ fontSize: '9px', color: '#fff', background: '#38a169', padding: '1px 3px', borderRadius: '3px', marginRight: '4px' }}>NUEVO</span>}
                                    {isFirst && !isNew && <span style={{ fontSize: '9px', color: '#fff', background: '#3182ce', padding: '1px 3px', borderRadius: '3px', marginRight: '4px' }}>ÚLTIMO</span>}
                                    {isToday ? <span style={{ color: isFirst ? '#2c5282' : '#2b6cb0', fontWeight: isFirst ? 'bold' : 'normal' }}>Hoy {timeStr}</span> : `${dateStr} ${timeStr}`}
                                  </td>
                                  <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: fontWeight, color: amountColor }}>
                                    ${p.amount?.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                  </td>
                                  <td style={{ padding: '4px 6px', color: isNew || isFirst ? '#2d3748' : '#718096', fontWeight: isFirst ? '500' : 'normal' }}>
                                    {p.payer}
                                  </td>
                                </tr>
                              );
                            })
                            }
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              )}

              {paymentMethod === 'Múltiple' && (
                <div style={{ marginBottom: '15px', padding: '15px', border: '1px solid #dee2e6', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '15px' }}>Pagos Registrados</h4>

                  {splitPayments.length > 0 ? (
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 15px' }}>
                      {splitPayments.map((p, idx) => (
                        <li key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', borderBottom: '1px solid #eee', background: '#fff', borderRadius: '4px', marginBottom: '4px' }}>
                          <span><strong>{p.method}</strong>: ${p.amount.toFixed(2)}</span>
                          <button
                            onClick={() => setSplitPayments(prev => prev.filter((_, i) => i !== idx))}
                            style={{ ...btnSmallRed, padding: '2px 8px', fontSize: '12px', marginLeft: '10px' }}
                            title="Eliminar pago"
                          >
                            ✖
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ color: '#6c757d', fontStyle: 'italic', fontSize: '13px' }}>No hay pagos registrados aún.</p>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '15px', fontWeight: 'bold' }}>
                    <span>Total Registrado: ${(splitPayments.reduce((acc, p) => acc + p.amount, 0)).toFixed(2)}</span>
                    <span style={{ color: (splitPayments.reduce((acc, p) => acc + p.amount, 0) >= total - 0.01) ? '#28a745' : '#dc3545' }}>
                      Faltante: ${Math.max(0, total - splitPayments.reduce((acc, p) => acc + p.amount, 0)).toFixed(2)}
                    </span>
                  </div>

                  {splitPayments.reduce((acc, p) => acc + p.amount, 0) < total - 0.01 && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', borderTop: '1px solid #dee2e6', paddingTop: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Método</label>
                        <select
                          value={newSplitPaymentMethod}
                          onChange={(e) => setNewSplitPaymentMethod(e.target.value)}
                          style={{ ...inputSearch, width: '100%', padding: '8px' }}
                        >
                          <option value="Efectivo">Efectivo</option>
                          <option value="Tarjeta">Tarjeta</option>
                          <option value="Transferencia">Transferencia</option>
                          <option value="QR">QR</option>
                          {selectedCustomer?.cuentaCorrienteEnabled && <option value="Cuenta corriente">Cuenta corriente</option>}
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Monto</label>
                        <input
                          type="number"
                          value={newSplitPaymentAmount}
                          onChange={(e) => setNewSplitPaymentAmount(e.target.value)}
                          placeholder={Math.max(0, total - splitPayments.reduce((acc, p) => acc + p.amount, 0)).toFixed(2)}
                          style={{ ...inputSearch, width: '100%' }}
                        />
                      </div>
                      <button
                        onClick={() => {
                          let amt = parseFloat(newSplitPaymentAmount);
                          const currentTotal = splitPayments.reduce((acc, p) => acc + p.amount, 0);
                          const remaining = total - currentTotal;

                          if (!amt && amt !== 0) {
                            amt = remaining;
                          }

                          if (amt <= 0) return toast.warning('Ingrese un monto válido');
                          if (amt > remaining + 0.05) return toast.warning('El monto supera el restante de la venta');

                          setSplitPayments([...splitPayments, { method: newSplitPaymentMethod, amount: amt }]);
                          setNewSplitPaymentAmount('');
                        }}
                        style={{ ...btnNav, background: '#28a745', padding: '8px 12px', height: 'auto', alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}
                      >
                        ➕
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: '20px' }}>
                <p><strong>Total a pagar:</strong> ${total.toFixed(2)}</p>
                {selectedCustomer && (
                  <p><strong>Cliente:</strong> {selectedCustomer.razonSocial || selectedCustomer.name}</p>
                )}
              </div>

              {/* Notas opcionales para el ticket */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px', color: '#444' }}>
                  📝 Nota en el ticket <span style={{ fontWeight: '400', color: '#888', fontSize: '12px' }}>(opcional)</span>
                </label>
                <textarea
                  id="sale-note-input"
                  value={saleNote}
                  onChange={(e) => setSaleNote(e.target.value)}
                  placeholder="Ej: Entregar en turno tarde, sin cebolla, etc."
                  maxLength={300}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #ced4da',
                    fontSize: '13px',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    color: '#333',
                    lineHeight: '1.4'
                  }}
                />
              </div>

              {/* Selección de comprobante simplificada: usaremos los datos fiscales cargados */}
              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '13px', color: '#333' }}>
                  La venta se facturará automáticamente según los datos fiscales cargados. No es necesario seleccionar punto de venta ni tipo de comprobante.
                </p>
                <div style={{ marginTop: '8px' }}>
                  <div style={{ fontSize: '13px', color: '#333' }}>
                    <strong>Tipo de comprobante sugerido:</strong> {tipoComprobanteSel === 1 ? 'A' : tipoComprobanteSel === 6 ? 'B' : 'C'} (determinado por Datos Fiscales)
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  style={{
                    ...btnNav,
                    background: '#6c757d',
                    opacity: isProcessingPayment ? 0.6 : 1,
                    transform: pressedBtn === 'cancel' ? 'translateY(1px)' : undefined,
                    boxShadow: pressedBtn === 'cancel' ? 'inset 0 2px 6px rgba(0,0,0,0.12)' : undefined
                  }}
                  onClick={() => { if (!isProcessingPayment) setShowPaymentModal(false); }}
                  onMouseDown={() => setPressedBtn('cancel')}
                  onMouseUp={() => setPressedBtn(null)}
                  onMouseLeave={() => setPressedBtn(null)}
                  onTouchStart={() => setPressedBtn('cancel')}
                  onTouchEnd={() => setPressedBtn(null)}
                  disabled={isProcessingPayment}
                  aria-disabled={isProcessingPayment}
                >
                  Cancelar
                </button>
                <button
                  style={{
                    ...btnGreen,
                    background: '#ffc107',
                    color: '#000',
                    opacity: (isProcessingPayment || (paymentMethod !== 'Efectivo' && paymentMethod !== 'Múltiple') || (paymentMethod === 'Múltiple' && splitPayments.reduce((acc, p) => acc + p.amount, 0) < total - 0.05)) ? 0.6 : 1,
                    transform: pressedBtn === 'ticket' ? 'translateY(1px)' : undefined,
                    boxShadow: pressedBtn === 'ticket' ? 'inset 0 2px 6px rgba(0,0,0,0.12)' : undefined,
                    cursor: (paymentMethod !== 'Efectivo' && paymentMethod !== 'Múltiple') ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => {
                    if (isProcessingPayment) return;
                    if (paymentMethod === 'Múltiple' && splitPayments.reduce((acc, p) => acc + p.amount, 0) < total - 0.05) return;
                    if (paymentMethod === 'Efectivo' || paymentMethod === 'Múltiple') handleFinalizeClick('no-fiscal');
                  }}
                  onMouseDown={() => setPressedBtn('ticket')}
                  onMouseUp={() => setPressedBtn(null)}
                  onMouseLeave={() => setPressedBtn(null)}
                  onTouchStart={() => setPressedBtn('ticket')}
                  onTouchEnd={() => setPressedBtn(null)}
                  title={(paymentMethod !== 'Efectivo' && paymentMethod !== 'Múltiple') ? 'Ticket no fiscal solo disponible para pagos en efectivo o múltiple' : 'Atajo: F7'}
                  disabled={isProcessingPayment || (paymentMethod !== 'Efectivo' && paymentMethod !== 'Múltiple') || (paymentMethod === 'Múltiple' && splitPayments.reduce((acc, p) => acc + p.amount, 0) < total - 0.05)}
                  aria-disabled={isProcessingPayment || (paymentMethod !== 'Efectivo' && paymentMethod !== 'Múltiple')}
                >
                  🧾 Ticket (No Fiscal) (F7)
                </button>
                <button
                  style={{
                    ...btnGreen,
                    background: '#17a2b8',
                    opacity: (isProcessingPayment || (paymentMethod === 'Múltiple' && splitPayments.reduce((acc, p) => acc + p.amount, 0) < total - 0.05)) ? 0.6 : 1,
                    transform: pressedBtn === 'fiscal' ? 'translateY(1px)' : undefined,
                    boxShadow: pressedBtn === 'fiscal' ? 'inset 0 2px 6px rgba(0,0,0,0.12)' : undefined
                  }}
                  onClick={() => {
                    if (isProcessingPayment) return;
                    if (paymentMethod === 'Múltiple' && splitPayments.reduce((acc, p) => acc + p.amount, 0) < total - 0.05) return;
                    handleFinalizeClick('fiscal');
                  }}
                  onMouseDown={() => setPressedBtn('fiscal')}
                  onMouseUp={() => setPressedBtn(null)}
                  onMouseLeave={() => setPressedBtn(null)}
                  onTouchStart={() => setPressedBtn('fiscal')}
                  onTouchEnd={() => setPressedBtn(null)}
                  title="Atajo: F9"
                  disabled={isProcessingPayment || (paymentMethod === 'Múltiple' && splitPayments.reduce((acc, p) => acc + p.amount, 0) < total - 0.05)}
                  aria-disabled={isProcessingPayment}
                >
                  🧾 Factura Fiscal (AFIP) (F9)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scanner modal */}
        {scannerOpen && (
          <div style={modalOverlay} onClick={() => setScannerOpen(false)}>
            <div style={modalContent} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ marginBottom: '10px' }}>📷 Escanear código</h2>
              <BarcodeScannerHtml5Qrcode onDetected={handleScanned} />
              <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                <button style={{ ...btnNav, background: '#17a2b8' }} onClick={unlockAudioAndClickFile} title="Tomar o subir foto">📸 Tomar/Subir foto</button>
                <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFileImageInput} />
                <div style={{ marginLeft: 'auto' }}>
                  <button style={{ ...btnNav, background: '#6c757d' }} onClick={() => setScannerOpen(false)}>Cerrar</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pending invoices modal */}
        {showPendingModal && (
          <div style={modalOverlay} onClick={() => setShowPendingModal(false)}>
            <div style={{ ...modalContent, maxWidth: '720px' }} onClick={(e) => e.stopPropagation()}>
              <h2>Facturas pendientes de envío</h2>
              <p>Estas facturas no pudieron enviarse a AFIP y se encuentran en la cola para reintentos automáticos.</p>
              <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '12px' }}>
                {pendingList.length === 0 ? (
                  <div style={{ padding: '12px' }}>No hay facturas pendientes.</div>
                ) : (
                  <div className="responsive-table">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th>Id</th>
                          <th>Referencia</th>
                          <th>Estado</th>
                          <th>Retries</th>
                          <th>Último error</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingList.map((p) => (
                          <tr key={p._id} style={{ borderTop: '1px solid #eee' }}>
                            <td style={{ padding: '8px' }}>{p._id}</td>
                            <td style={{ padding: '8px' }}>{p.externalRef || 'Manual'}</td>
                            <td style={{ padding: '8px' }}>{p.status}</td>
                            <td style={{ padding: '8px' }}>{p.retries || 0}</td>
                            <td style={{ padding: '8px' }}>{p.lastError?.message ? String(p.lastError.message).slice(0, 80) : '-'}</td>
                            <td style={{ padding: '8px' }}>
                              {(user.role === 'owner' || user.role === 'admin') && (
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button style={{ ...btnSmallRed, padding: '6px 8px' }} onClick={() => handleRetry(p._id)}>Reintentar</button>
                                  <button style={{ ...btnSmall, background: '#6c757d', padding: '6px 8px' }} onClick={() => handleDismiss(p._id)}>Descartar</button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button style={{ ...btnNav, background: '#6c757d' }} onClick={() => { setShowPendingModal(false); fetchPending(); }}>Cerrar</button>
                <button style={{ ...btnNav, background: '#17a2b8' }} onClick={() => fetchPending()}>Refrescar</button>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div style={modalOverlay} onClick={() => { }}>
            <div style={modalContent} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ marginBottom: '15px', color: '#28a745' }}>✅ Venta Finalizada</h2>

              <div style={{ marginBottom: '20px' }}>
                <p><strong>Total:</strong> ${lastSale?.total?.toFixed(2)}</p>
                {lastSale?.customerName && <p><strong>Cliente:</strong> {lastSale.customerName}</p>}
                {lastInvoice?.cae && (
                  <div style={{ marginTop: '8px', padding: '10px', background: '#e9ecef', borderRadius: '4px', fontSize: '13px' }}>
                    <p style={{ margin: 0 }}><strong>Factura AFIP Generada:</strong></p>
                    <p style={{ margin: 0 }}>CAE: {lastInvoice.cae}</p>
                    <p style={{ margin: 0 }}>Nro: {lastInvoice.puntoVenta}-{lastInvoice.numero}</p>
                  </div>
                )}
              </div>

              {/* WhatsApp Input - Only visible on Mobile */}
              {isMobile && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '14px', marginBottom: '5px' }}>
                    📲 Número de WhatsApp (Cliente):
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="tel"
                      value={whatsappPhone}
                      onChange={(e) => setWhatsappPhone(e.target.value)}
                      placeholder="Ej: 1112345678"
                      style={{ ...inputSearch, flex: 1 }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>

                {/* Mobile Actions: Only PDF Button */}
                {isMobile && lastInvoice && lastInvoice.cae && (
                  <button
                    style={{ ...btnNav, background: '#17a2b8', display: 'flex', alignItems: 'center', gap: 4, flex: 1, justifyContent: 'center' }}
                    onClick={shareInvoicePdf}
                    disabled={loadingPdf}
                  >
                    {loadingPdf ? '⏳...' : '📄 Enviar PDF WhatsApp'}
                  </button>
                )}

                {/* Desktop Actions: Print Buttons */}
                {!isMobile && (
                  <>
                    <button style={{ ...btnNav, background: '#6c757d' }} onClick={() => handlePrint()}>
                      🖨️ Imprimir Ticket
                    </button>
                    {lastInvoice && (
                      <button style={{ ...btnNav, background: '#007bff' }} onClick={() => printInvoiceInNewWindow()}>
                        📄 A4 / PDF
                      </button>
                    )}
                  </>
                )}

                <button
                  style={{ ...btnGreen, marginLeft: 'auto', padding: '12px 24px', fontSize: '16px' }}
                  onClick={() => {
                    setShowSuccessModal(false);
                    setWhatsappPhone('');
                    if (!isMobile) {
                      setTimeout(() => {
                        searchInputRef.current?.focus();
                      }, 200);
                    }
                  }}
                >
                  ✨ Nueva Venta
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Nota: la pantalla de confirmación/vista previa fue eliminada según configuración del usuario. */}

        {/* Espaciador para que la barra fija no tape contenido en móviles */}
        <div className="show-on-mobile" style={{ display: 'none', height: '80px' }} />

        {/* Botones de Logout/Probar sonido eliminados (no se muestran en ninguna plataforma) */}

        {/* Barra fija inferior en móvil con total y acción principal */}
        {cart.length > 0 && (
          <div className="show-on-mobile mobile-sticky-footer" style={{ display: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontSize: '12px', color: '#555' }}>TOTAL</div>
                <div style={{ fontSize: '20px', fontWeight: 800 }}>${total.toFixed(2)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnClearCart} onClick={handleClearCart}>Vaciar</button>
                <button style={btnGreen} onClick={openPaymentModal}>Finalizar</button>
              </div>
            </div>
          </div>
        )}

        {/* Componentes de impresión (siempre en el DOM pero fuera de la pantalla para permitir carga de imágenes) */}
        {/* Ticket para imprimir (formato ticket_303.pdf) */}
        <div style={{ position: 'fixed', left: '-10000px', top: 0, zIndex: -1 }}>
          <div ref={ticketRef} style={{ width: '80mm', padding: '10px', fontFamily: 'Arial, sans-serif', fontSize: '11px' }}>
            {lastSale && (
              <>
                {/* Encabezado */}
                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  {/* Header layout: for fiscal tickets match A4: centered title + Cod, then logo left and PV/Factura/Fecha right */}
                  {lastSaleMode === 'no-fiscal' ? (
                    <div style={{ width: '100%' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 'bold', border: '2px solid black', padding: '6px', display: 'inline-block' }}>TICKET</div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                        <div style={{ width: '40%', textAlign: 'left' }}>
                          {settingsLogoData ? (
                            <img src={settingsLogoData} alt="logo" style={{ maxWidth: '100%', maxHeight: '50px' }} />
                          ) : fiscal?.logoUrl ? (
                            <img src={fiscal.logoUrl} alt="logo" style={{ maxWidth: '100%', maxHeight: '50px' }} />
                          ) : (
                            <div style={{ fontSize: '20px', fontWeight: '700' }}>{settingsLeftName || fiscal?.nombreFantasia || fiscal?.razonSocial || ''}</div>
                          )}
                        </div>
                        <div style={{ width: '55%', textAlign: 'right' }}>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#000' }}>Fecha: {new Date(lastSale?.date).toLocaleDateString('es-AR')}</div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#000', marginTop: '2px' }}>
                            Venta N°: {(() => {
                              try {
                                const sn = lastSale?.saleNumber;
                                const n = Number(sn);
                                if (Number.isFinite(n)) return String(n).padStart(6, '0');
                                return String(sn ?? '').slice(0, 24);
                              } catch (e) { return String(lastSale?.saleNumber ?? '').slice(0, 24); }
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ width: '100%' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '18px', fontWeight: 'bold' }}>FACTURA {lastInvoice ? (lastInvoice.tipoComprobanteDescripcion?.split(' ')[1] || 'C') : 'C'}</div>
                        <div style={{ fontSize: '12px', marginTop: '4px' }}>Cod. {String(lastInvoice?.tipoComprobante || tipoComprobanteSel).padStart(3, '0')}</div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                        <div style={{ width: '40%', textAlign: 'left' }}>
                          {/* logo/configured left text: prefer user-provided logo or left name, then fiscal logo, then razonSocial */}
                          {settingsLogoData ? (
                            <img src={settingsLogoData} alt="logo" style={{ maxWidth: '100%', maxHeight: '50px' }} />
                          ) : fiscal?.logoUrl ? (
                            <img src={fiscal.logoUrl} alt="logo" style={{ maxWidth: '100%', maxHeight: '50px' }} />
                          ) : (
                            <div style={{ fontSize: '20px', fontWeight: '700' }}>{settingsLeftName || fiscal?.nombreFantasia || fiscal?.razonSocial || ''}</div>
                          )}
                        </div>
                        <div style={{ width: '55%', textAlign: 'right' }}>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#000' }}>PV: {String(lastInvoice?.puntoVenta || puntoVenta).padStart(4, '0')}</div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#000', marginTop: '2px' }}>Factura: {String(lastInvoice?.numero || '').padStart(8, '0')}</div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#000', marginTop: '2px' }}>Fecha: {new Date(lastInvoice?.fechaComprobante || lastInvoice?.fecha || lastSale?.date).toLocaleDateString('es-AR')}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ borderTop: '1px solid #000', margin: '8px 0' }} />

                {/* Datos del emisor: solo para ticket fiscal */}
                {lastSaleMode !== 'no-fiscal' && (
                  <div style={{ fontSize: '10px', marginBottom: '8px', color: '#000' }}>
                    <div><strong>Razón Social:</strong> {fiscal?.razonSocial || 'N/A'}</div>
                    <div><strong>Domicilio:</strong> {fiscal?.domicilioFiscal?.calle || ''} {fiscal?.domicilioFiscal?.numero || ''}{fiscal?.domicilioFiscal?.localidad ? `, ${fiscal.domicilioFiscal.localidad}` : ''}</div>
                    <div><strong>C.U.I.T.:</strong> {fiscal?.cuit || 'N/A'}</div>
                    <div><strong>IIBB:</strong> {emitterIIBB || 'N/A'}</div>
                    <div><strong>{fiscal?.tipoResponsable || ''}</strong></div>
                    <div><strong>Inicio Act.:</strong> {fiscal?.inicioActividades ? new Date(fiscal.inicioActividades).toLocaleDateString('es-AR') : 'N/A'}</div>
                  </div>
                )}

                <div style={{ borderTop: '1px solid #000', margin: '8px 0' }} />

                {/* Cliente */}
                <div style={{ fontSize: '13px', marginBottom: '10px', color: '#000' }}>
                  {
                    clienteForTicket ? (
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: '800', marginBottom: '2px' }}><strong>CLIENTE:</strong> {clienteForTicket.razonSocial || `${clienteForTicket.tipo}: ${clienteForTicket.numero}`}</div>
                        {/* Mostrar CUIT/DNI si está disponible */}
                        <div style={{ fontSize: '12px' }}><strong>{clienteForTicket?.tipo || 'Doc.:'}</strong> {clienteForTicket?.numero || ''}</div>
                        {/* Mostrar domicilio y condicion IVA para tickets */}
                        {clienteForTicket?.domicilio && <div style={{ fontSize: '12px' }}><strong>Domicilio:</strong> {clienteForTicket.domicilio}</div>}
                        <div style={{ fontSize: '12px' }}><strong>Cond. IVA:</strong> {clienteForTicket?.condicionIVADescripcion || (clienteForTicket?.condicionIVA ? (tipoDescripcionMap[Number(clienteForTicket.condicionIVA)] || String(clienteForTicket.condicionIVA)) : 'Consumidor Final')}</div>
                        {/* Mostrar IIBB del cliente si está disponible */}
                        {(() => {
                          const ib = (clienteForTicket && (clienteForTicket.ingresosBrutos || clienteForTicket.iibb));
                          let ibText = '';
                          if (ib) {
                            if (typeof ib === 'object') ibText = String(ib.numero || ib.padron || ib.id || '');
                            else ibText = String(ib);
                          }
                          return ibText ? (<div><strong>IIBB:</strong> {ibText}</div>) : null;
                        })()}
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: '800' }}><strong>CLIENTE:</strong> {lastSale?.customerName || 'CONSUMIDOR FINAL'}</div>
                      </div>
                    )
                  }
                </div>

                <div style={{ borderTop: '1px solid #000', margin: '8px 0' }} />

                {/* Items */}
                <table style={{ width: '100%', fontSize: '10px', marginBottom: '8px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #000' }}>
                      <th style={{ textAlign: 'left', width: '15%', fontSize: '11px' }}>CANT.</th>
                      <th style={{ textAlign: 'left', width: '50%', fontSize: '11px' }}>DESCRIPCIÓN</th>
                      <th style={{ textAlign: 'right', width: '35%', fontSize: '11px' }}>TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(lastInvoice?.items || lastSale.items || []).map((item, idx) => {
                      const isFacturaA = [1, 3, 51, 53].includes(Number(lastInvoice?.tipoComprobante));
                      const showDiscriminated = isFacturaA && lastSaleMode !== 'no-fiscal';
                      const qty = item.quantity || item.cantidad || 1;
                      // Detectar precios originales y con descuento en diferentes formas posibles
                      let original = Number(item.originalPrice ?? item.precioUnitarioOriginal ?? item.price ?? item.unitPrice ?? item.salePrice ?? 0);
                      let discounted = Number(item.unitPriceWithDiscount ?? item.precioUnitario ?? item.price ?? original);
                      if (!showDiscriminated && (fiscal && !emitterIsMonotributo)) {
                        const code = item.alicuotaIVA ?? item.alicuotaIVACode ?? 5;
                        const pct = getAlicuotaPercent(code) || 21;
                        const factor = 1 + (pct / 100);
                        original *= factor;
                        discounted *= factor;
                      }


                      const sub = qty * original;
                      const desc = item.name || item.descripcion || item.title || '';
                      return (
                        <tr key={idx}>
                          <td style={{ paddingTop: '10px', paddingBottom: '6px', fontSize: '13px', verticalAlign: 'top' }}>{qty}</td>
                          <td style={{ paddingTop: '10px', paddingBottom: '6px' }}>
                            <div style={{ fontSize: '14px', fontWeight: 800, color: '#000', lineHeight: '1.1', marginBottom: '6px' }}>{desc}</div>
                            <div style={{ fontSize: '11px', color: '#000', fontWeight: 600, lineHeight: '1.3' }}>
                              Precio unit.: ${Number(original).toFixed(2)}
                            </div>
                            {(fiscal && !emitterIsMonotributo && showDiscriminated) && (
                              <div style={{ fontSize: '8px', color: '#666', marginTop: '4px', lineHeight: '1.3' }}>
                                {/** Mostrar IVA por item solo si en la configuración del emisor se discrimina IVA */}
                                {shouldDiscriminateIVA(fiscal) ? (() => {
                                  const code = item.alicuotaIVACode ?? item.alicuotaIVA ?? 5;
                                  const pct = getAlicuotaPercent(code) || 0;
                                  const ivaVal = Math.round(((qty * discounted) * (pct / 100)) * 100) / 100;
                                  return `IVA: $${ivaVal.toFixed(2)}`;
                                })() : 'Sin discriminación IVA'}
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: 'right', paddingTop: '10px', paddingBottom: '6px', fontSize: '15px', fontWeight: 800, verticalAlign: 'top' }}>${Number(sub).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Totales */}
                <div style={{ fontSize: '11px', fontWeight: 'bold', marginTop: '6px' }}>
                  {(() => {
                    const tipoCmp = Number(lastInvoice?.tipoComprobante);
                    const isFacturaA = [1, 3, 51, 53].includes(tipoCmp);
                    const showDiscriminated = isFacturaA && lastSaleMode !== 'no-fiscal';
                    const itemsList = (lastInvoice?.items || lastSale.items || []);

                    const rawSubtotal = itemsList.reduce((acc, it) => {
                      const qty = Number(it.quantity || it.cantidad || 1);
                      let original = Number(it.originalPrice ?? it.precioUnitarioOriginal ?? it.price ?? it.unitPrice ?? it.salePrice ?? it.precioUnitario ?? it.unitPriceWithDiscount ?? 0);
                      if (!showDiscriminated && (fiscal && !emitterIsMonotributo)) {
                        const code = it.alicuotaIVA ?? it.alicuotaIVACode ?? 5;
                        const pct = getAlicuotaPercent(code) || 21;
                        original = original * (1 + (pct / 100));
                      }
                      return acc + (original * qty);
                    }, 0);
                    const discountTotal = itemsList.reduce((acc, it) => {
                      const qty = Number(it.quantity || it.cantidad || 1);
                      let original = Number(it.originalPrice ?? it.precioUnitarioOriginal ?? it.price ?? it.unitPrice ?? it.salePrice ?? it.precioUnitario ?? it.unitPriceWithDiscount ?? 0);
                      let discounted = Number(it.unitPriceWithDiscount ?? it.precioUnitario ?? it.price ?? original);
                      if (!showDiscriminated && (fiscal && !emitterIsMonotributo)) {
                        const code = it.alicuotaIVA ?? it.alicuotaIVACode ?? 5;
                        const pct = getAlicuotaPercent(code) || 21;
                        const factor = 1 + (pct / 100);
                        original *= factor;
                        discounted *= factor;
                      }
                      const disc = original - discounted;
                      return acc + (disc * qty);
                    }, 0);
                    const ivaShown = Number(lastInvoice?.importeIVA ?? lastSale?.importeIVA ?? ivaAmt ?? 0);
                    const totalShown = Number(lastInvoice?.importeTotal || lastInvoice?.total || total || lastSale?.total || 0);
                    // Para Factura A: IVA sobre precio original (no prorateado por AFIP)
                    const origIvaForA = isFacturaA ? itemsList.reduce((acc, it) => {
                      const qty = Number(it.quantity || it.cantidad || 1);
                      const orig = Number(it.originalPrice ?? it.precioUnitarioOriginal ?? it.price ?? it.unitPrice ?? it.salePrice ?? it.precioUnitario ?? 0);
                      const code = it.alicuotaIVA ?? it.alicuotaIVACode ?? 5;
                      const pct = getAlicuotaPercent(code) || 21;
                      return acc + (orig * qty * (pct / 100));
                    }, 0) : 0;
                    // Para Factura A: descuento = (neto original + IVA original) - total real
                    const discountForA = isFacturaA
                      ? Math.max(0, Math.round((rawSubtotal + origIvaForA - totalShown) * 100) / 100)
                      : 0;
                    // IVA a mostrar: para Factura A usamos el calculado sobre precio original
                    const ivaToShow = isFacturaA ? origIvaForA : ivaShown;
                    const totalShownFinal = totalShown;
                    const isBOrC = tipoCmp === 6 || tipoCmp === 11;
                    // Calcular IVA contenido para B/C: si el importeIVA es 0, derivarlo desde los items (precio final)
                    const ivaContenidoCalc = (() => {
                      // IVA contenido solo aplica para comprobantes B/C
                      if (!isBOrC) return 0;
                      // Si ya viene el IVA discriminado, usarlo
                      if (ivaShown > 0) return ivaShown;
                      try {
                        const list = (lastInvoice?.items || lastSale.items || []);
                        const globalPct = (() => {
                          const lbl = String(lastInvoice?.ivaLabel || lastSale?.ivaLabel || '').trim();
                          if (/^\d+(\.\d+)?%$/.test(lbl)) return parseFloat(lbl);
                          return 21;
                        })();
                        const fromItems = list.reduce((acc, it) => {
                          const qty = Number(it.quantity || it.cantidad || 0);
                          const priceFinal = Number(it.precioUnitario ?? it.unitPriceWithDiscount ?? it.price ?? 0);
                          const gross = qty * priceFinal;
                          const code = it.alicuotaIVACode ?? it.alicuotaIVA ?? 0;
                          const pct = getAlicuotaPercent(code) || globalPct;
                          if (gross > 0 && pct > 0) {
                            const net = gross / (1 + (pct / 100));
                            return acc + (gross - net);
                          }
                          return acc;
                        }, 0);
                        return fromItems;
                      } catch (e) {
                        return 0;
                      }
                    })();
                    return (
                      <>
                        {/* Ocultar SUBTOTAL e IVA en tickets NO FISCALES */}
                        {lastSaleMode !== 'no-fiscal' && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>SUBTOTAL:</span>
                              <span>${rawSubtotal.toFixed(2)}</span>
                            </div>
                            {(fiscal && !emitterIsMonotributo && showDiscriminated) && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                                <span>IVA ({lastInvoice?.ivaLabel || lastSale?.ivaLabel || ivaLabel}):</span>
                                <span>${ivaToShow.toFixed(2)}</span>
                              </div>
                            )}
                          </>
                        )}
                        {/* Descuento para Factura A: un único valor derivado */}
                        {isFacturaA && discountForA > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', color: '#000', fontWeight: 'bold' }}>
                            <span>DESCUENTO:</span>
                            <span>-${discountForA.toFixed(2)}</span>
                          </div>
                        )}
                        {/* Descuento para comprobantes no-A: item-level discount */}
                        {!isFacturaA && discountTotal > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', color: '#000', fontWeight: 'bold' }}>
                            <span>DESCUENTO:</span>
                            <span>-${discountTotal.toFixed(2)}</span>
                          </div>
                        )}
                        {/* saleDiscount adicional solo cuando NO es Factura A y difiere del discountTotal */}
                        {!isFacturaA && (lastSale?.saleDiscount && Number(lastSale.saleDiscount) > 0 && Number(lastSale.saleDiscount).toFixed(2) !== Number(discountTotal).toFixed(2)) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', color: '#000', fontWeight: 'bold' }}>
                            <span>DESCUENTO ADICIONAL:</span>
                            <span>-${Number(lastSale.saleDiscount).toFixed(2)}</span>
                          </div>
                        )}
                        {/* Recargo por financiación (si existe) */}
                        {(() => {
                          const inst = Number(lastInvoice?.installments || 1);
                          const rate = Number(lastInvoice?.interestRate || 0);
                          if (inst > 1 || rate > 0) {
                            const tot = Number(totalShownFinal);
                            const factor = 1 + (rate / 100) * inst;
                            // Revert calculation to find base amount
                            const base = tot / factor;
                            const surcharge = tot - base;
                            const valCuota = tot / inst;
                            return (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                                  <span>Recargo Fin. ({inst} cuotas):</span>
                                  <span>${surcharge.toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#555' }}>
                                  <span>Valor Cuota:</span>
                                  <span>${valCuota.toFixed(2)}</span>
                                </div>
                              </>
                            );
                          }
                          return null;
                        })()}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', borderTop: '1px solid #000', paddingTop: '6px', fontSize: '14px', fontWeight: 800, color: '#000' }}>
                          <span>TOTAL A PAGAR:</span>
                          <span>${totalShownFinal.toFixed(2)}</span>
                        </div>
                        {isBOrC && (
                          <>
                            <div style={{ marginTop: '6px', fontSize: '9px' }}>
                              Régimen de Transparencia Fiscal al Consumidor - Ley 27.743
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                              <span>IVA contenido:</span>
                              <span>${ivaContenidoCalc.toFixed(2)}</span>
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div style={{ borderTop: '1px solid #000', margin: '8px 0' }} />


                {/* CAE y QR: solo fiscal (nunca en ticket no-fiscal) */}
                {lastSaleMode !== 'no-fiscal' && lastInvoice && (
                  <div style={{ textAlign: 'center', marginTop: '6px' }}>
                    <div style={{ fontSize: '9px', marginBottom: '6px' }}>
                      <div><strong>CAE:</strong> {lastInvoice.cae || 'N/A'}</div>
                      <div><strong>Vto CAE:</strong> {lastInvoice.caeVencimiento ? new Date(lastInvoice.caeVencimiento).toLocaleDateString('es-AR') : 'N/A'}</div>
                    </div>
                    {(lastInvoice.cae && lastInvoice.qrData) && (
                      <div style={{ margin: '6px auto', width: '130px', height: '130px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={lastInvoice.qrData} alt="QR AFIP" style={{ width: '100%', height: '100%' }} />
                      </div>
                    )}
                  </div>
                )}

                {/* Pie */}
                {lastSaleMode !== 'no-fiscal' && (
                  <div style={{ textAlign: 'center', fontSize: '9px', marginTop: '10px', borderTop: '1px solid #000', paddingTop: '8px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '11px' }}>ARCA</div>
                    <div style={{ fontSize: '10px' }}>AGENCIA DE RECAUDACIÓN Y CONTROL ADUANERO</div>
                    <div style={{ fontStyle: 'italic', marginTop: '6px' }}>Documento generado electrónicamente</div>
                  </div>
                )}

                {/* Mercado Pago Payment Info - Al final con separación */}
                {(() => {
                  const payData = lastSale?.mpPaymentData || lastInvoice?.mpPaymentData;
                  if (!payData) return null;
                  return (
                    <div style={{
                      fontSize: '9px',
                      marginTop: '25px',
                      padding: '10px 6px',
                      borderTop: '1px dashed #000',
                      color: '#000'
                    }}>
                      <div style={{ fontWeight: 800, marginBottom: '6px', textAlign: 'center' }}>*** COMPROBANTE DE PAGO ELECTRÓNICO ***</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Nro. Operación:</span>
                        <span>{payData.operationNumber || payData.transactionId}</span>
                      </div>
                      {payData.authorizationCode && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Cód. Autorización:</span>
                          <span>{payData.authorizationCode}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Método:</span>
                        <span>{payData.paymentMethod} {payData.cardBrand ? `(${payData.cardBrand})` : ''}</span>
                      </div>
                      {payData.cardLastFourDigits && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Tarjeta:</span>
                          <span>**** {payData.cardLastFourDigits}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Fecha y Hora:</span>
                        <span>{new Date(payData.paymentDate).toLocaleString('es-AR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                        <span>Estado:</span>
                        <span>{payData.status}</span>
                      </div>
                      <div style={{ marginTop: '6px', fontSize: '8px', fontStyle: 'italic', textAlign: 'center' }}>
                        Gracias por su pago con Mercado Pago
                      </div>
                    </div>
                  );
                })()}

                {/* Nota de la venta al final absoluto */}
                {lastSale?.notes && String(lastSale.notes).trim() && (
                  <div style={{ marginTop: '20px', padding: '10px 0', borderTop: '1px solid #000' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Nota:</div>
                    <div style={{ fontSize: '12px', color: '#000', lineHeight: '1.4', whiteSpace: 'pre-wrap', fontWeight: 'bold' }}>{String(lastSale.notes).trim()}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Factura fiscal para imprimir */}
        <div style={{ position: 'fixed', left: '-10000px', top: 0, zIndex: -1 }}>
          <div ref={invoiceRef} style={invoiceStyle}>
            {lastInvoice && (
              <>
                {/* Encabezado */}
                <div style={{ textAlign: 'center', borderBottom: '3px solid #000', paddingBottom: '10px', marginBottom: '12px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <h1 style={{ fontSize: '28px', margin: '0 0 6px 0' }}>FACTURA {lastInvoice.tipoComprobanteDescripcion?.split(' ')[1] || 'B'}</h1>
                    <div style={{ fontSize: '12px', marginBottom: '6px' }}>Cod. {String(lastInvoice.tipoComprobante).padStart(3, '0')}</div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                    <div style={{ width: '40%', textAlign: 'left' }}>
                      {settingsLogoData ? (
                        <img src={settingsLogoData} alt="logo" style={{ maxWidth: '100%', maxHeight: '70px' }} />
                      ) : fiscalStatus?.logoUrl ? (
                        <img src={fiscalStatus.logoUrl} alt="logo" style={{ maxWidth: '100%', maxHeight: '70px' }} />
                      ) : (
                        <div style={{ fontSize: '20px', fontWeight: '700' }}>{settingsLeftName || fiscalStatus?.nombreFantasia || fiscalStatus?.razonSocial || ''}</div>
                      )}
                    </div>
                    <div style={{ width: '55%', textAlign: 'right', fontSize: '13px' }}>
                      <div>PV: {String(lastInvoice.puntoVenta).padStart(4, '0')}</div>
                      <div>Factura: {String(lastInvoice.numero).padStart(8, '0')}</div>
                      <div>Fecha: {new Date(lastInvoice.fechaComprobante || lastInvoice.fecha).toLocaleDateString('es-AR')}</div>
                    </div>
                  </div>
                </div>

                {/* Datos del Emisor */}
                <div style={{ marginBottom: '20px', borderBottom: '2px solid #ddd', paddingBottom: '15px' }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '10px', borderBottom: '1px solid #000', paddingBottom: '5px' }}>
                    DATOS DEL EMISOR
                  </h3>
                  <p style={{ margin: '5px 0', fontSize: '13px' }}>
                    <strong>Razón Social:</strong> {fiscalStatus?.razonSocial || 'N/A'}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '13px' }}>
                    <strong>CUIT:</strong> {fiscalStatus?.cuit || 'N/A'}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '13px' }}>
                    <strong>Condición IVA:</strong> {fiscalStatus?.tipoResponsable || 'N/A'}
                  </p>
                  {fiscalStatus?.ingresosBruto?.numero && (
                    <>
                      <p style={{ margin: '5px 0', fontSize: '13px' }}>
                        <strong>Ingresos Brutos:</strong> {fiscalStatus.ingresosBrutos.numero}
                      </p>
                      {fiscalStatus.ingresosBrutos.condicion && (
                        <p style={{ margin: '5px 0', fontSize: '13px' }}>
                          <strong>Condición IIBB:</strong> {fiscalStatus.ingresosBrutos.condicion}
                        </p>
                      )}
                    </>
                  )}
                  <p style={{ margin: '5px 0', fontSize: '13px' }}>
                    <strong>Inicio de Actividades:</strong> {fiscalStatus?.inicioActividades ? new Date(fiscalStatus.inicioActividades).toLocaleDateString('es-AR') : 'N/A'}
                  </p>
                  {fiscalStatus?.domicilioFiscal && (
                    <p style={{ margin: '5px 0', fontSize: '13px' }}>
                      <strong>Domicilio:</strong> {fiscalStatus.domicilioFiscal.calle} {fiscalStatus.domicilioFiscal.numero}, {fiscalStatus.domicilioFiscal.localidad}, {fiscalStatus.domicilioFiscal.provincia}
                    </p>
                  )}
                </div>

                {/* Datos del Cliente */}
                <div style={{ marginBottom: '20px', borderBottom: '2px solid #ddd', paddingBottom: '15px' }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '10px', borderBottom: '1px solid #000', paddingBottom: '5px' }}>
                    DATOS DEL CLIENTE
                  </h3>
                  <p style={{ margin: '5px 0', fontSize: '13px' }}>
                    <strong>Razón Social:</strong> {lastInvoice.cliente?.razonSocial || 'Consumidor Final'}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '13px' }}>
                    <strong>{lastInvoice.cliente?.tipo}:</strong> {lastInvoice.cliente?.numero || '0'}
                  </p>
                  {(() => {
                    const ib = lastInvoice?.cliente && (lastInvoice.cliente.ingresosBrutos || lastInvoice.cliente.iibb);
                    let ibText = '';
                    if (ib) {
                      if (typeof ib === 'object') ibText = String(ib.numero || ib.padron || ib.id || '');
                      else ibText = String(ib);
                    }
                    return ibText ? (
                      <p style={{ margin: '5px 0', fontSize: '13px' }}>
                        <strong>IIBB:</strong> {ibText}
                      </p>
                    ) : null;
                  })()}
                  <p style={{ margin: '5px 0', fontSize: '13px' }}>
                    <strong>Condición IVA:</strong> {lastInvoice.cliente?.condicionIVADescripcion || (lastInvoice.cliente?.condicionIVA ? (tipoDescripcionMap[Number(lastInvoice.cliente.condicionIVA)] || String(lastInvoice.cliente.condicionIVA)) : 'Consumidor Final')}
                  </p>
                </div>

                {/* Detalle de Items */}
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '10px', borderBottom: '1px solid #000', paddingBottom: '5px' }}>
                    DETALLE
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #000' }}>
                        <th style={{ textAlign: 'left', padding: '8px' }}>Descripción</th>
                        <th style={{ textAlign: 'center', padding: '8px', width: '80px' }}>Cant.</th>
                        <th style={{ textAlign: 'right', padding: '8px', width: '100px' }}>Precio Unit.</th>
                        <th style={{ textAlign: 'right', padding: '8px', width: '100px' }}>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastInvoice.items?.map((item, idx) => {
                        const qty = item.cantidad || item.quantity || 1;
                        const original = Number(item.originalPrice ?? item.precioUnitarioOriginal ?? item.price ?? item.unitPrice ?? item.salePrice ?? 0);
                        const discounted = Number(item.precioUnitario ?? item.unitPriceWithDiscount ?? item.price ?? original);
                        const discountAmt = Number(item.discountAmount ?? (original - discounted));
                        const discountPct = original > 0 ? Math.round((discountAmt / original) * 10000) / 100 : 0;
                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '8px' }}>{item.descripcion}</td>
                            <td style={{ textAlign: 'center', padding: '8px' }}>{qty}</td>
                            <td style={{ textAlign: 'right', padding: '8px' }}>
                              ${discounted?.toFixed(2)}
                              {discountAmt > 0 && (
                                <div style={{ fontSize: '11px', color: '#b00', fontWeight: 700 }}>
                                  <span style={{ textDecoration: 'line-through', color: '#444', fontWeight: 600, marginRight: '6px' }}>Orig: ${original.toFixed(2)}</span>
                                  <span>• -${discountAmt.toFixed(2)} ({discountPct}%)</span>
                                </div>
                              )}
                            </td>
                            <td style={{ textAlign: 'right', padding: '8px' }}>${Number(item.subtotal ?? item.subTotal ?? (qty * discounted)).toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Totales */}
                <div style={{ marginTop: '30px', borderTop: '3px solid #000', paddingTop: '15px' }}>
                  <div style={{ textAlign: 'right', fontSize: '14px' }}>
                    {(() => {
                      const itemsList = lastInvoice.items || [];
                      const rawSubtotal = itemsList.reduce((acc, it) => {
                        const qty = Number(it.cantidad || it.quantity || 1);
                        const original = Number(it.originalPrice ?? it.precioUnitarioOriginal ?? it.price ?? it.unitPrice ?? it.salePrice ?? it.precioUnitario ?? it.unitPriceWithDiscount ?? 0);
                        return acc + (original * qty);
                      }, 0);
                      const discountTotal = itemsList.reduce((acc, it) => {
                        const qty = Number(it.cantidad || it.quantity || 1);
                        const original = Number(it.originalPrice ?? it.precioUnitarioOriginal ?? it.price ?? it.unitPrice ?? it.salePrice ?? it.precioUnitario ?? it.unitPriceWithDiscount ?? 0);
                        const discounted = Number(it.precioUnitario ?? it.unitPriceWithDiscount ?? it.price ?? original);
                        const disc = Number(it.discountAmount ?? (original - discounted));
                        return acc + (disc * qty);
                      }, 0);
                      const ivaShown = Number(lastInvoice?.importeIVA ?? lastSale?.importeIVA ?? ivaAmt ?? 0);
                      const totalShown = Number(lastInvoice.total ?? lastInvoice.importeTotal ?? lastSale.total ?? 0);
                      const tipoCmp = Number(lastInvoice?.tipoComprobante);
                      const isBOrC = tipoCmp === 6 || tipoCmp === 11;
                      const ivaContenidoCalc = (() => {
                        if (!isBOrC) return 0;
                        if (ivaShown > 0) return ivaShown;
                        try {
                          const list = (lastInvoice.items || []);
                          const globalPct = (() => {
                            const lbl = String(lastInvoice?.ivaLabel || '').trim();
                            if (/^\d+(\.\d+)?%$/.test(lbl)) return parseFloat(lbl);
                            return 21;
                          })();
                          const fromItems = list.reduce((acc, it) => {
                            const qty = Number(it.cantidad || it.quantity || 0);
                            const priceFinal = Number(it.precioUnitario ?? it.unitPriceWithDiscount ?? it.price ?? 0);
                            const gross = qty * priceFinal;
                            const code = it.alicuotaIVACode ?? it.alicuotaIVA ?? 0;
                            const pct = getAlicuotaPercent(code) || globalPct;
                            if (gross > 0 && pct > 0) {
                              const net = gross / (1 + (pct / 100));
                              return acc + (gross - net);
                            }
                            return acc;
                          }, 0);
                          if (fromItems > 0) return fromItems;
                          const grossTotal = Number(totalShown || 0);
                          const pctGlobal = globalPct || 21;
                          if (grossTotal > 0 && pctGlobal > 0) {
                            const net = grossTotal / (1 + (pctGlobal / 100));
                            return grossTotal - net;
                          }
                          return 0;
                        } catch { return 0; }
                      })();
                      return (
                        <>
                          <p style={{ margin: '5px 0' }}>
                            <strong>Subtotal:</strong> ${rawSubtotal.toFixed(2)}
                          </p>
                          {!emitterIsMonotributo && (
                            <p style={{ margin: '5px 0' }}>
                              <strong>IVA ({lastInvoice?.ivaLabel || lastSale?.ivaLabel || ivaLabel}):</strong> ${ivaShown.toFixed(2)}
                            </p>
                          )}
                          {discountTotal > 0 && (
                            <p style={{ margin: '5px 0', color: '#dc3545' }}>
                              <strong>DESCUENTO:</strong> -${discountTotal.toFixed(2)}
                            </p>
                          )}
                          <p style={{ margin: '10px 0 0 0', fontSize: '18px', fontWeight: 'bold', borderTop: '2px solid #000', paddingTop: '10px' }}>
                            TOTAL: ${totalShown.toFixed(2)}
                          </p>
                          {isBOrC && (
                            <>
                              <p style={{ margin: '8px 0 0 0', fontSize: '12px', fontStyle: 'italic', textAlign: 'left' }}>
                                Régimen de Transparencia Fiscal al Consumidor - Ley 27.743
                              </p>
                              <p style={{ margin: '4px 0 0 0', textAlign: 'left' }}>
                                <strong>IVA contenido:</strong> ${ivaContenidoCalc.toFixed(2)}
                              </p>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* CAE y QR: centrar CAE arriba del QR en A4 */}
                <div style={{ marginTop: '30px', padding: '15px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <div style={{ background: '#f5f5f5', padding: '10px 16px', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', margin: '2px 0', fontWeight: '700' }}>CAE: {lastInvoice.cae || 'N/A'}</div>
                    <div style={{ fontSize: '13px', margin: '2px 0' }}>Vto CAE: {lastInvoice.caeVencimiento ? new Date(lastInvoice.caeVencimiento).toLocaleDateString('es-AR') : 'N/A'}</div>
                  </div>

                  {/* QR: mostrar en la factura A4 si está disponible */}
                  {lastInvoice?.qrData && (
                    <div style={{ marginTop: '6px', width: '130px', height: '130px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={lastInvoice.qrData} alt="QR AFIP" style={{ width: '100%', height: '100%' }} />
                    </div>
                  )}
                </div>

                {/* Pie de página */}
                <div style={{ marginTop: '40px', textAlign: 'center', fontSize: '11px', color: '#666', borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                  <p>¡Gracias por su compra!</p>
                </div>

                {/* Mercado Pago Payment Info para Factura A4 */}
                {(() => {
                  const payData = lastSale?.mpPaymentData || lastInvoice?.mpPaymentData;
                  if (!payData) return null;
                  return (
                    <div style={{
                      marginTop: '30px',
                      padding: '15px',
                      border: '1px dashed #000',
                      borderRadius: '8px',
                      fontSize: '11px',
                      background: '#fff',
                      maxWidth: '300px',
                      marginLeft: '0'
                    }}>
                      <div style={{ fontWeight: 800, marginBottom: '8px', textDecoration: 'underline' }}>COMPROBANTE DE PAGO ELECTRÓNICO</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span>Nro. Operación:</span>
                        <span style={{ fontWeight: 700 }}>{payData.operationNumber || payData.transactionId}</span>
                      </div>
                      {payData.authorizationCode && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span>Cód. Autorización:</span>
                          <span style={{ fontWeight: 700 }}>{payData.authorizationCode}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span>Método de Pago:</span>
                        <span>{payData.paymentMethod} {payData.cardBrand ? `(${payData.cardBrand})` : ''}</span>
                      </div>
                      {payData.cardLastFourDigits && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span>Tarjeta:</span>
                          <span>**** **** **** {payData.cardLastFourDigits}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span>Fecha y Hora:</span>
                        <span>{new Date(payData.paymentDate).toLocaleString('es-AR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Estado:</span>
                        <span style={{ fontWeight: 700, color: '#28a745' }}>{payData.status}</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Nota de la venta al final absoluto (A4) */}
                {lastSale?.notes && String(lastSale.notes).trim() && (
                  <div style={{ marginTop: '20px', padding: '10px 0', borderTop: '1px solid #000' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Nota:</div>
                    <div style={{ fontSize: '12px', color: '#000', lineHeight: '1.4', whiteSpace: 'pre-wrap', fontWeight: 'bold' }}>{String(lastSale.notes).trim()}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Modal de selección de variante */}
        {variantSelection && variantSelection.product && variantSelection.product.variants && (
          <div style={modalOverlay} onClick={() => setVariantSelection(null)}>
            <div style={{ ...modalContent, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
              <div style={{ ...modalHeader, marginBottom: 16 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Seleccionar Variante</div>
                <button onClick={() => setVariantSelection(null)} style={modalCloseBtn}>✖</button>
              </div>
              <div style={{ marginBottom: 10, fontWeight: 700 }}>{variantSelection.product.name}</div>
              <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: 10 }}>Variante</th>
                      <th style={{ padding: 10 }}>SKU</th>
                      <th style={{ padding: 10, textAlign: 'center' }}>Stock</th>
                      <th style={{ padding: 10 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {variantSelection.product.variants.map((v, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: 10 }}>
                          {[v.attributes?.color, v.attributes?.size].filter(Boolean).join(' / ')}
                        </td>
                        <td style={{ padding: 10, fontSize: 13, color: '#666' }}>{v.sku}</td>
                        <td style={{ padding: 10, fontWeight: 'bold', textAlign: 'center', color: v.stock <= 0 ? '#dc3545' : 'inherit' }}>{v.stock}</td>
                        <td style={{ padding: 10, textAlign: 'right' }}>
                          <button
                            style={{ ...btnGreen, padding: '6px 12px', fontSize: 13, opacity: (v.stock <= 0 && !variantSelection.product.noControlStock && !variantSelection.product.allowNegStock) ? 0.5 : 1 }}
                            disabled={v.stock <= 0 && !variantSelection.product.noControlStock && !variantSelection.product.allowNegStock}
                            onClick={() => handleVariantSelected(v)}
                          >
                            Seleccionar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={() => setVariantSelection(null)} style={{ ...btnSmallRed, marginTop: 16, width: '100%', padding: 10 }}>Cancelar</button>
            </div>
          </div>
        )}

        {/* Payment Method Selection Modal (Auto vs Manual) */}
        {showPaymentMethodModal && (
          <div style={modalOverlay} onClick={() => setShowPaymentMethodModal(false)}>
            <div style={{ ...modalContent, maxWidth: 500, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
              <h2 style={{ marginBottom: 20 }}>Procesar Pago con {paymentMethod}</h2>
              <div style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 30, color: '#28a745' }}>
                ${(paymentMethod === 'Múltiple' ? (splitPayments.find(p => p.method === 'Tarjeta' || p.method === 'QR')?.amount || total) : total).toFixed(2)}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                {/* AUTO BUTTON: Terminal or QR */}
                {(paymentMethod === 'Mercado Pago' || paymentMethod === 'QR' || (paymentMethod === 'Múltiple' && splitPayments.some(p => p.method === 'QR'))) ? (
                  <button
                    onClick={() => {
                      setShowPaymentMethodModal(false);
                      initiateQRPayment(pendingFinalizeMode);
                    }}
                    style={{
                      padding: '20px', borderRadius: '12px', border: '2px solid #009ee3',
                      background: '#e1f5fe', color: '#009ee3',
                      fontSize: '16px', fontWeight: 'bold', cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10
                    }}
                  >
                    <span style={{ fontSize: 32 }}>📱</span>
                    <span>ACTIVAR QR</span>
                    <span style={{ fontSize: 12, fontWeight: 'normal' }}>(Mostrar en mostrador)</span>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowPaymentMethodModal(false);
                      initiateTerminalPayment(pendingFinalizeMode);
                    }}
                    style={{
                      padding: '20px', borderRadius: '12px', border: '2px solid #007bff',
                      background: '#e3f2fd', color: '#0056b3',
                      fontSize: '16px', fontWeight: 'bold', cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10
                    }}
                  >
                    <span style={{ fontSize: 32 }}>📡</span>
                    <span>ENVIAR A TERMINAL</span>
                    <span style={{ fontSize: 12, fontWeight: 'normal' }}>(Automático)</span>
                  </button>
                )}

                <button
                  onClick={() => {
                    setShowPaymentMethodModal(false);
                    handleFinishSale(pendingFinalizeMode);
                  }}
                  style={{
                    padding: '20px', borderRadius: '12px', border: '2px solid #6c757d',
                    background: '#f8f9fa', color: '#495057',
                    fontSize: '16px', fontWeight: 'bold', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10
                  }}
                >
                  <span style={{ fontSize: 32 }}>⌨️</span>
                  <span>MANUAL</span>
                  <span style={{ fontSize: 12, fontWeight: 'normal' }}>(Solo registro)</span>
                </button>
              </div>

              <button
                onClick={() => setShowPaymentMethodModal(false)}
                style={{ ...btnSmallRed, padding: '10px 20px', fontSize: 14 }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Waiting for Terminal Modal */}
        {waitingForTerminal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', zIndex: 100000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            color: 'white', textAlign: 'center'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }} className="spin-animation">⏳</div>
            <h2 style={{ fontSize: '28px', marginBottom: '10px' }}>{paymentMethod} en Terminal Point</h2>
            <p style={{ fontSize: '20px', marginBottom: '10px', maxWidth: '80%' }}>
              {terminalStatus}
            </p>
            <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '30px', color: '#ffc107' }}>
              ${(paymentMethod === 'Múltiple' ? (splitPayments.find(p => p.method === 'Tarjeta' || p.method === 'QR')?.amount || total) : total).toFixed(2)}
            </div>

            <p style={{ fontSize: '16px', color: '#ccc', marginBottom: '40px' }}>
              Por favor, instruya al cliente para completar el pago en el dispositiv✅
            </p>

            <button
              onClick={() => cancelTerminalProcess(true)}
              style={{
                padding: '15px 40px', background: '#dc3545', color: 'white',
                border: 'none', borderRadius: '50px', fontSize: '20px',
                cursor: 'pointer', boxShadow: '0 4px 15px rgba(220,53,69,0.5)'
              }}
            >
              Cancelar Operación
            </button>
            <style>{`
              @keyframes spin { 100% { -webkit-transform: rotate(360deg); transform:rotate(360deg); } }
              .spin-animation { animation: spin 2s linear infinite; }
            `}</style>
          </div>
        )}

        {/* Waiting for QR Modal */}
        {waitingForQR && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', zIndex: 100000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            color: 'white', textAlign: 'center'
          }}>
            <h2 style={{ fontSize: '28px', marginBottom: '10px' }}>Escanear QR</h2>
            <div style={{ background: 'white', padding: '15px', borderRadius: '10px', margin: '20px' }}>
              <div style={{ color: '#000', fontWeight: 'bold', marginBottom: 10 }}>CAJA: QR #1</div>
              {qrData ? (
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrData)}`}
                  alt="QR Pago"
                  style={{ width: 250, height: 250 }}
                />
              ) : (
                <img src="https://http2.mlstatic.com/frontend-assets/mp-web-navigation/ui-navigation/5.19.1/mercadopago/logo__small@2x.png" alt="MP Logo" style={{ height: 40 }} />
              )}
            </div>

            <p style={{ fontSize: '20px', marginBottom: '10px', maxWidth: '80%' }}>
              Esperando que el cliente pague...
            </p>
            <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '30px', color: '#009ee3' }}>
              ${(paymentMethod === 'Múltiple' ? (splitPayments.find(p => p.method === 'Tarjeta' || p.method === 'QR')?.amount || total) : total).toFixed(2)}
            </div>

            <button
              onClick={cancelQRProcess}
              style={{
                padding: '15px 40px', background: '#dc3545', color: 'white',
                border: 'none', borderRadius: '50px', fontSize: '20px',
                cursor: 'pointer', boxShadow: '0 4px 15px rgba(220,53,69,0.5)'
              }}
            >
              Cancelar QR
            </button>
          </div>
        )}

      </div>

      {/* Nuevo Cliente Modal */}
      {showCustomerModal && (
        <CustomerFormModal
          isOpen={showCustomerModal}
          onClose={() => setShowCustomerModal(false)}
          onSuccess={(newCustomer) => {
            setSelectedCustomer(newCustomer);
            setCustomerSearch(newCustomer.razonSocial || newCustomer.name);
            setCustomerSuggestions([]);
            setShowCustomerModal(false);
            if (!isMobile) try { focusMainSearch(false); } catch (e) { }
            toast.success('Cliente seleccionado');
          }}
          isEcommerceLite={isEcommerceLite}
        />
      )}

      {/* Lightbox vista previa de imagen de producto */}
      {imagePreviewUrl && (
        <div
          onClick={() => setImagePreviewUrl(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
            animation: 'fadeIn 0.15s ease'
          }}
        >
          <button
            onClick={() => setImagePreviewUrl(null)}
            style={{
              position: 'absolute', top: 20, right: 25,
              background: 'rgba(255,255,255,0.15)', color: '#fff',
              border: 'none', padding: '10px 20px', borderRadius: 30,
              cursor: 'pointer', fontSize: 16, fontWeight: 600
            }}
          >✖ Cerrar</button>
          <img
            src={imagePreviewUrl}
            alt="Vista previa"
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '92%', maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 12,
              boxShadow: '0 8px 60px rgba(0,0,0,0.6)',
              cursor: 'default'
            }}
          />
        </div>
      )}

      {/* Modal Presupuesto */}
      {showQuotationModal && (
        <div style={modalOverlay} onClick={() => setShowQuotationModal(false)}>
          <div style={{ ...modalContent, width: '90%', maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginBottom: 15 }}>ðŸ“ Guardar e Imprimir Presupuesto</h2>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>Formato de impresión:</label>
              <select
                style={{ ...inputSearch, width: '100%', padding: '10px' }}
                value={quotationFormat}
                onChange={(e) => setQuotationFormat(e.target.value)}
              >
                <option value="ticket">ðŸ§¾ Formato Ticket (Impresora Comandera)</option>
                <option value="a4">ðŸ“„ Formato A4 (PDF para enviar/imprimir)</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                style={{ ...btnNav, background: '#6c757d' }}
                onClick={() => setShowQuotationModal(false)}
              >
                Cancelar
              </button>
              <button
                style={{ ...btnGreen, display: 'flex', alignItems: 'center' }}
                onClick={handleSaveAndPrintQuotation}
              >
                💾 Guardar e Imprimir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cargar Presupuesto */}
      {showLoadQuotationModal && (
        <div style={modalOverlay} onClick={() => setShowLoadQuotationModal(false)}>
          <div style={{ ...modalContent, width: '90%', maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15 }}>
              <h2 style={{ fontSize: 18, fontWeight: 'bold' }}>📋 Cargar Presupuesto</h2>
              <button style={btnSmallGray} onClick={() => setShowLoadQuotationModal(false)}>✖</button>
            </div>
            <input
              type="text"
              placeholder="Buscar presupuesto (ej: P-2026-000001)..."
              style={{ ...inputSearch, width: '100%', marginBottom: 15 }}
              value={quotationSearch}
              onChange={e => setQuotationSearch(e.target.value)}
            />

            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {loadingQuotations ? (
                <div style={{ textAlign: 'center', padding: 20 }}>Cargando...</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Número</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Cliente</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Total</th>
                      <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingQuotations.filter(q =>
                      q.referenceNumber.toLowerCase().includes(quotationSearch.toLowerCase()) ||
                      (q.customerName || '').toLowerCase().includes(quotationSearch.toLowerCase())
                    ).map(q => (
                      <tr key={q._id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px' }}>{q.referenceNumber}</td>
                        <td style={{ padding: '8px' }}>{q.customerName || 'Consumidor Final'}</td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>${Number(q.total).toFixed(2)}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <button style={btnSmallPrimary} onClick={() => loadQuotationToCart(q)}>Cargar</button>
                        </td>
                      </tr>
                    ))}
                    {pendingQuotations.length === 0 && !loadingQuotations && (
                      <tr><td colSpan="4" style={{ textAlign: 'center', padding: 20, color: '#666' }}>No hay presupuestos pendientes.</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};


// Modal de consulta de precio
// Renderizado fuera del return principal por claridad (se activa con estado priceLookupOpen)
const PriceLookupModal = ({ open, product, products = [], onClose, onAdd, isMobile = false, onManualScan = null }) => {
  // local state for search term, suggestions and selected product
  const [term, setTerm] = useState(product?.barcode || product?.name || '');
  const [suggestionsLocal, setSuggestionsLocal] = useState([]);
  const [selected, setSelected] = useState(product || null);
  const [, setLoadingLookup] = useState(false);

  // Ref + behavior to capture manual keyboard scanner input on desktop
  const scannerInputRef = useRef(null);
  const [scannerFocused, setScannerFocused] = useState(false);
  // Mobile camera / torch support
  const mobileScannerWrapperRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const torchTrackRef = useRef(null);
  useEffect(() => {
    // When modal opens on desktop, focus the hidden scanner input so USB/CCD scanners
    // (which act as keyboard devices) will type into it and trigger Enter.
    if (!open) return;
    try {
      if (!isMobile && scannerInputRef && scannerInputRef.current) {
        // small delay to ensure modal input is rendered
        setTimeout(() => {
          try { scannerInputRef.current.focus(); } catch (e) { }
        }, 60);
      }
    } catch (e) { }
  }, [open, isMobile]);

  // When modal opens on mobile and a camera scanner is present, detect video track and torch capability
  useEffect(() => {
    if (!open || !isMobile) return undefined;
    let mounted = true;
    let pollId = null;
    const detect = () => {
      try {
        const wrapper = mobileScannerWrapperRef.current;
        if (!wrapper) return false;
        const video = wrapper.querySelector('video');
        if (!video) return false;
        const stream = video.srcObject || (video.mozSrcObject || null);
        if (!stream || !stream.getVideoTracks) return false;
        const track = stream.getVideoTracks()[0];
        if (!track) return false;
        // store track for toggling
        torchTrackRef.current = track;
        if (typeof track.getCapabilities === 'function') {
          const caps = track.getCapabilities();
          if (caps && caps.torch) {
            if (mounted) setTorchAvailable(true);
            return true;
          }
        }
        // Some browsers expose torch via capabilities even if getCapabilities not present; default false
        return false;
      } catch (e) {
        return false;
      }
    };
    // poll for video element/track for a short period
    let attempts = 0;
    pollId = setInterval(() => {
      attempts += 1;
      const ok = detect();
      if (ok || attempts > 25) {
        clearInterval(pollId);
        pollId = null;
      }
    }, 200);

    return () => {
      mounted = false;
      if (pollId) clearInterval(pollId);
    };
  }, [open, isMobile]);

  // Expose a small refocus helper so parent scan handler can request focus after failed lookup
  // This helper also clears term/selection/suggestions so the modal is ready to accept the next scan.
  useEffect(() => {
    try {
      // attach to window to allow Sales.handleScanned to call it when priceLookupOpen is true
      // eslint-disable-next-line no-undef
      window.salesPriceLookupRefocus = () => {
        try {
          try { setTerm(''); } catch (e) { }
          try { setSelected(null); } catch (e) { }
          try { setSuggestionsLocal([]); } catch (e) { }
          if (scannerInputRef && scannerInputRef.current) {
            try { scannerInputRef.current.value = ''; } catch (e) { }
            // small delay to ensure value cleared before focusing
            setTimeout(() => { try { scannerInputRef.current.focus(); } catch (e) { } }, 20);
          }
        } catch (e) { }
      };
    } catch (e) { }
    return () => {
      try { if (window && window.salesPriceLookupRefocus) delete window.salesPriceLookupRefocus; } catch (e) { }
    };
  }, []);

  // Handler when manual scanner (keyboard) types and presses Enter inside the hidden input
  const handleScannerInputKey = (e) => {
    try {
      if (e.key === 'Enter') {
        const val = String(e.target.value || '').trim();
        if (!val) return;
        // Use the provided scan handler from parent (Sales) if available
        try { if (typeof onManualScan === 'function') onManualScan(val); } catch (err) { console.error('onManualScan call failed', err); }
        // clear the input for next scan and re-focus so subsequent scans are captured
        try { e.target.value = ''; } catch (err) { }
        try { setTimeout(() => { try { e.target.focus(); } catch (e) { } }, 30); } catch (e) { }
      }
    } catch (err) {
      // ignore
    }
  };

  // Toggle torch on the mobile camera track (if available)
  const toggleTorch = async (on) => {
    try {
      const track = torchTrackRef.current;
      if (!track) {
        // try to detect one-time
        const wrapper = mobileScannerWrapperRef.current;
        const video = wrapper && wrapper.querySelector ? wrapper.querySelector('video') : null;
        const stream = video && (video.srcObject || video.mozSrcObject) ? (video.srcObject || video.mozSrcObject) : null;
        if (stream && stream.getVideoTracks) torchTrackRef.current = stream.getVideoTracks()[0];
      }
      const t = torchTrackRef.current;
      if (!t) return;
      if (typeof t.applyConstraints === 'function') {
        await t.applyConstraints({ advanced: [{ torch: !!on }] });
        setTorchOn(!!on);
        return;
      }
      // Fallback: try ImageCapture approach (rare)
      if (window.ImageCapture) {
        try {
          const ic = new window.ImageCapture(t);
          const caps = await ic.getPhotoCapabilities();
          if (caps && caps.fillLightMode && caps.fillLightMode.includes('flash')) {
            // Not standardized to toggle torch via ImageCapture directly; try applyConstraints still
            await t.applyConstraints({ advanced: [{ torch: !!on }] });
            setTorchOn(!!on);
            return;
          }
        } catch (e) {
          // ignore
        }
      }
    } catch (e) {
      console.warn('toggleTorch failed', e);
    }
  };

  useEffect(() => {
    setTerm(product?.barcode || product?.name || '');
    setSelected(product || null);
  }, [product]);

  // Ensure torch is turned off when modal closes
  useEffect(() => {
    if (!open) {
      try {
        if (torchOn) toggleTorch(false).catch(() => { });
      } catch (e) { }
      setTorchOn(false);
      setTorchAvailable(false);
      torchTrackRef.current = null;
    }
  }, [open, torchOn]);

  // local copy of discount computation (avoid depending on outer-scope helper)
  const computeDiscountedPriceLocal = (p, qty = 1) => {
    const baseLocal = Number(p.salePrice ?? p.price ?? p.unitPrice ?? 0) || 0;
    const type = p.discountType || 'none';
    const value = Number(p.discountValue || 0) || 0;
    const tiers = Array.isArray(p.discountTiers) ? p.discountTiers : [];

    if (!type || type === 'none') return Math.round(baseLocal * 100) / 100;
    if (type === 'percent') return Math.round((baseLocal * (1 - value / 100)) * 100) / 100;
    if (type === 'fixed') return Math.round(Math.max(0, baseLocal - value) * 100) / 100;
    if (type === 'tiered') {
      let match = null;
      for (const t of tiers) {
        const min = Number(t.minQty) || 0;
        const max = (t.maxQty === '' || t.maxQty === undefined) ? undefined : Number(t.maxQty);
        if (qty >= min && (max === undefined || qty <= max)) {
          if (!match || min > (Number(match.minQty) || 0)) match = t;
        }
      }
      if (!match) return Math.round(baseLocal * 100) / 100;
      if (match.type === 'percent') return Math.round((baseLocal * (1 - Number(match.value) / 100)) * 100) / 100;
      if (match.type === 'fixed') return Math.round(Math.max(0, baseLocal - Number(match.value)) * 100) / 100;
    }
    return Math.round(baseLocal * 100) / 100;
  };

  const activeProduct = selected || product || null;
  const base = activeProduct ? Number(activeProduct.salePrice ?? activeProduct.price ?? activeProduct.unitPrice ?? 0) || 0 : 0;
  const unitDisc = activeProduct ? computeDiscountedPriceLocal(activeProduct, 1) : 0;

  const hasDiscount = activeProduct ? unitDisc < base : false;
  const discType = activeProduct ? activeProduct.discountType || 'none' : 'none';
  const tiers = activeProduct ? (Array.isArray(activeProduct.discountTiers) ? activeProduct.discountTiers : []) : [];
  const sampleQtys = [1, 2, 5, 10];

  const handleTermChange = (e) => {
    const v = e.target.value || '';
    setTerm(v);
    if (!v) return setSuggestionsLocal([]);
    const lower = v.toLowerCase();
    const matches = (products || []).filter((p) => (p.name || '').toLowerCase().includes(lower) || (p.barcode || '').toLowerCase().includes(lower));
    setSuggestionsLocal(matches.slice(0, 20));
  };

  const handleSelectSuggestion = (p) => {
    setSelected(p);
    setTerm(p.name || p.barcode || '');
    setSuggestionsLocal([]);
    // After selecting, refocus hidden scanner input so another scan can be received immediately
    try {
      if (!isMobile && scannerInputRef && scannerInputRef.current) {
        setTimeout(() => { try { scannerInputRef.current.focus(); } catch (e) { } }, 40);
      }
    } catch (e) { }
  };

  const handleBackendLookup = async () => {
    if (!term) return;
    setLoadingLookup(true);
    try {
      // If looks numeric, try barcode search first
      if (/^\d+$/.test(term)) {
        const res = await httpRequest.get(`${API_URL_PRODUCTS}/search-by-barcode`, { params: { code: term } });
        let r = res.data;
        if (Array.isArray(r)) r = r.length ? r[0] : null;
        if (r) {
          setSelected(r);
          setSuggestionsLocal([]);
          setLoadingLookup(false);
          return;
        }
      }
      // fallback: try server-side name search if endpoint exists
      try {
        const res2 = await httpRequest.get(`${API_URL_PRODUCTS}/search`, { params: { query: term } });
        const hits = res2.data || [];
        if (Array.isArray(hits) && hits.length) {
          setSuggestionsLocal(hits.slice(0, 20));
        } else {
          setSuggestionsLocal([]);
          toast.info('No se encontraron productos en el servidor');
        }
      } catch (e) {
        // If server search not available, just clear suggestions
        setSuggestionsLocal([]);
      }
    } catch (err) {
      console.error('Backend lookup error', err);
      toast.error('Error buscando producto en el servidor');
    } finally {
      setLoadingLookup(false);
    }
  };
  // If a product is loaded into the modal (either via backend lookup or scan),
  // ensure the hidden scanner input is focused so the next scan is captured
  useEffect(() => {
    try {
      if (activeProduct && !isMobile && scannerInputRef && scannerInputRef.current) {
        setTimeout(() => { try { scannerInputRef.current.focus(); } catch (e) { } }, 60);
      }
    } catch (e) { }
  }, [activeProduct, isMobile]);

  // Close modal on Escape key for better UX
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (ev) => {
      try {
        if (ev.key === 'Escape' || ev.key === 'Esc') {
          try { onClose(); } catch (e) { }
        }
      } catch (e) { }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => { try { window.removeEventListener('keydown', onKeyDown); } catch (e) { } };
  }, [open, onClose]);
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>ðŸ”Ž Consulta de precio</div>
            {scannerFocused && (
              <span style={scannerReadyPill}>Listo para escanear</span>
            )}
          </div>
          <button aria-label="Cerrar" onClick={onClose} style={modalCloseBtn}>✖</button>
        </div>
        <div style={{ marginBottom: 10 }}>
          <input
            type="text"
            placeholder="Buscar por nombre o códig✅.."
            value={term}
            onChange={handleTermChange}
            onKeyDown={(e) => { if (e.key === 'Enter') { handleBackendLookup(); } }}
            style={{ ...inputSearch, width: '100%' }}
          />
          {/* Hidden input to capture manual keyboard/USB scanner on desktop */}
          {!isMobile && (
            <input
              ref={scannerInputRef}
              type="text"
              aria-label="barcode-capture"
              onKeyDown={handleScannerInputKey}
              onFocus={() => setScannerFocused(true)}
              onBlur={() => setScannerFocused(false)}
              style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', opacity: 0 }}
            />
          )}
          {suggestionsLocal.length > 0 && (
            <div style={suggestionsBox}>
              {suggestionsLocal.map((p) => (
                <div key={p._id || p.id || p.barcode} style={suggestionItem} onClick={() => handleSelectSuggestion(p)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ color: '#666' }}>{p.barcode}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {activeProduct && (
            <div style={{ marginTop: 8 }}>
              <div><strong>{activeProduct.name}</strong></div>
              <div style={{ fontSize: 13, color: '#666' }}>{activeProduct.barcode ? `Código: ${activeProduct.barcode}` : ''}</div>
            </div>
          )}
          {/* Mobile-only camera scanner inside Price Lookup modal */}
          {isMobile && (
            <div style={{ marginTop: 10 }}>
              {!showCamera ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...btnSmallPrimary }} onClick={() => setShowCamera(true)}>ðŸ“· Abrir cámara</button>
                  <button style={{ ...btnSmallGray }} onClick={handleBackendLookup}>ðŸ”Ž Buscar en servidor</button>
                </div>
              ) : (
                <div ref={mobileScannerWrapperRef} style={{ marginTop: 10 }}>
                  <BarcodeScannerHtml5Qrcode onDetected={(c) => { try { if (typeof onManualScan === 'function') onManualScan(c); } catch (e) { } }} />
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
                    {torchAvailable && (
                      <button style={{ ...btnSmallPrimary }} onClick={() => { try { toggleTorch(!torchOn); } catch (e) { } }}>
                        {torchOn ? 'ðŸ”¦ Apagar' : 'ðŸ”¦ Encender'}
                      </button>
                    )}
                    <button style={{ ...btnSmallRed }} onClick={() => setShowCamera(false)}>Cerrar cámara</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 8 }}>
          <div style={{ color: '#888', textDecoration: hasDiscount ? 'line-through' : 'none' }}>{'$' + base.toFixed(2)}</div>
          {hasDiscount && <div style={{ fontWeight: 800 }}>{'$' + unitDisc.toFixed(2)}</div>}
        </div>
        <div style={{ marginBottom: 8 }}>
          <strong>Tipo de descuento:</strong> {discType}{activeProduct && activeProduct.discountValue ? ` (${activeProduct.discountValue})` : ''}
        </div>
        {tiers.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <strong>Escalas:</strong>
            <ul>
              {tiers.map((t, i) => (
                <li key={i}>desde {t.minQty} hasta {t.maxQty || 'âˆž'} â†’ {t.type === 'percent' ? `${t.value}%` : `$${t.value}`}</li>
              ))}
            </ul>
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <strong>Precios de ejemplo:</strong>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 6 }}>Cantidad</th>
                <th style={{ textAlign: 'right', padding: 6 }}>Precio unit.</th>
              </tr>
            </thead>
            <tbody>
              {sampleQtys.map((q) => (
                <tr key={q} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: 6 }}>{q}</td>
                  <td style={{ padding: 6, textAlign: 'right' }}>{activeProduct ? ('$' + computeDiscountedPriceLocal(activeProduct, q).toFixed(2)) : 'â€”'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Auto-focus hidden scanner input on open (desktop). Buttons removed per UX request. */}
            <button style={{ ...btnSmallPrimary }} onClick={() => { setTerm(''); setSuggestionsLocal([]); setSelected(null); }} title="Limpiar bíºsqueda">Limpiar</button>

          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...btnSmallGray }} onClick={onClose}>Cerrar</button>
            <button style={{ ...btnGreen }} onClick={() => { if (activeProduct) { onAdd(activeProduct, 1); onClose(); } else { toast.info('Seleccioná un producto antes de agregar'); } }}>Agregar x1</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ðŸ”¹ Estilos
const btnNav = { background: '#007bff', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' };
const btnGreen = { background: '#28a745', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const btnClearCart = { background: '#ffc107', color: '#000', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
/* btnPrint removed (preview modal deprecated) */
const btnSmall = { background: '#6c757d', color: 'white', border: 'none', padding: '4px 6px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' };
const btnSmallGray = { background: '#adb5bd', color: 'white', border: 'none', padding: '4px 6px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' };
const btnSmallPrimary = { background: 'linear-gradient(180deg,#0d6efd,#0a58ca)', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', boxShadow: '0 6px 18px rgba(13,110,253,0.18)', transition: 'transform 120ms ease, box-shadow 120ms ease' };
const btnSmallRed = { background: '#dc3545', color: 'white', border: 'none', padding: '4px 6px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' };
const btnQty = { background: '#6c757d', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' };
const inputSearch = { padding: '8px', borderRadius: '6px', border: '1px solid #ccc', width: 'min(520px, 100%)' };
// Mostrar más sugerencias en primer plano sin necesidad de usar el scrollbar
// Usamos una altura relativa para adaptarnos a distintas resoluciones/ventanas
const suggestionsBox = { border: '1px solid #ccc', borderRadius: '6px', maxHeight: '45vh', overflowY: 'auto', marginTop: '4px', background: '#fff' };
const suggestionItem = { padding: '6px', cursor: 'pointer', borderBottom: '1px solid #eee' };
const discountBadge = { background: '#dc3545', color: 'white', padding: '4px 6px', borderRadius: 6, fontSize: 12, fontWeight: 800 };
// styles for cart item containers
const cartItemContainer = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.06)' };
const cartItemLeft = { display: 'flex', flexDirection: 'column', gap: 8, maxWidth: '65%' };
const cartItemRight = { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, minWidth: 120 };


// Styles helpers (kept as is)
const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.45)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalContent = {
  backgroundColor: 'white',
  padding: '22px',
  borderRadius: '12px',
  boxShadow: '0 10px 40px rgba(2,6,23,0.35)',
  maxWidth: '600px',
  width: '92%',
  maxHeight: '88vh',
  overflowY: 'auto',
  border: '1px solid rgba(13,110,253,0.08)',
};

const modalHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 8,
};

const modalCloseBtn = {
  background: 'transparent',
  border: 'none',
  fontSize: 18,
  cursor: 'pointer',
  color: '#666'
};

const scannerReadyPill = {
  background: '#e6ffef',
  color: '#0b7a3f',
  padding: '4px 8px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  border: '1px solid #c6f4d0'
};

/* ticketStyle removed (unused) */

const invoiceStyle = {
  fontFamily: 'Arial, sans-serif',
  padding: '20px',
  maxWidth: '800px',
  fontSize: '12px',
  lineHeight: '1.4',
  color: '#000',
};

const btnPayment = {
  flex: 1,
  padding: '15px',
  border: '2px solid #dee2e6',
  borderRadius: '8px',
  background: 'white',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold',
  transition: 'all 0.2s',
};

const btnPaymentActive = {
  ...btnPayment,
  background: '#007bff',
  color: 'white',
  border: '2px solid #007bff',
};

// Estilos móviles adicionales
const mobileFooter = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  padding: '10px',
  background: '#ffffff',
  borderTop: '1px solid #e6e6e6',
  zIndex: 1200,
  boxShadow: '0 -4px 12px rgba(0,0,0,0.08)'
};

const mobileFooterTotal = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingLeft: 8,
  minWidth: 100
};

const mobileFooterBtn = {
  background: '#28a745',
  color: 'white',
  border: 'none',
  padding: '12px 16px',
  borderRadius: 10,
  fontWeight: '800',
  fontSize: 16,
  cursor: 'pointer',
  flex: 1,
  marginRight: 8
};

// Nota: estilos btnLogout / btnTestSound eliminados porque los botones fueron removidos de la UI.

export default Sales;
